/**
 * PresupuestosView -- #/presupuestos: el asistente que arma presupuestos de obra.
 * Mision: AGENTE-INSIGNIA-V1-001 / PRESU-UI-T1 fase A (ASSIGN #8342).
 *
 * AUDIENCIA: NEGOCIO (dueno/equipo del negocio; demo biz_demo_maderinco) dentro
 * del panel completo. NUNCA renderBusinessDashboard, que es la tablet de los
 * colaboradores -- regla #5638.
 *
 * FRAMING (Josse): no vendemos "Nexo Presupuestos", vendemos "automatizamos tu
 * negocio". El AGENTE es el protagonista y la herramienta es su primera entrega:
 * por eso al entrar RECIBE el asistente y es el quien remite a cada herramienta.
 *
 * TRES vistas:
 *   V1 recepcion -- saludo + 3 caminos (subir PDF / crear / ver catalogo)
 *   V2 catalogo  -- productos del negocio (buscar; alta/edicion en fase B)
 *   V3 armador   -- secciones + items, AIU EN VIVO y copiloto lateral
 *
 * API: los 6 endpoints de PRESU-API-T1 (Argy-A) tal cual; ninguno inventado.
 * Mientras no existan, cada vista degrada FAIL-OPEN con mensaje honesto y se
 * enciende sola cuando la API publique (patron PANEL-AUDIOS).
 *
 * @module presentation/views/PresupuestosView
 */

import { PresupuestosApiClient } from '../../infrastructure/api/PresupuestosApiClient.js';
import { parsearNumeroColombiano } from '../shared/numeroColombiano.js';
import { getSelectedBusinessName, getSelectedBusiness } from '../components/BusinessSelector.js';
import { AppConfig } from '../../config.js';
import { AgentApiClient } from '../../infrastructure/api/AgentApiClient.js';
import { enfoqueActivo } from '../components/NavBar.js';
import { montarConsola } from '../components/NxConsolaAgente.js';
import { itemDesdeProducto, cambiarCantidad, cambiarPrecio, origenEnPalabras, guardarPresupuesto, codigoYVersion } from '../../application/presupuestos/armadorItems.js';
import { guardarArmador, fraseDelFallo, revisionDe } from '../../application/presupuestos/porPaso.js';
import { cuerpoRecorrido, recorridoLeido, fraseDelRecorrido } from '../../application/presupuestos/recorrido.js';
import { PresupuestoPorPasoApiClient } from '../../infrastructure/api/PresupuestoPorPasoApiClient.js';
import { agruparVariantes, nombreDeFuente } from '../../application/presupuestos/variantesDeLaLista.js';
import { cubiertaRespondida, puestoEsencial } from '../../application/presupuestos/siguienteSeccion.js';
import { estadosDeEsenciales, ETIQUETA_ESTADO } from '../../application/presupuestos/estadoEsenciales.js';
import { arbolDe, candidatosDeTipos, estadoDelArbol } from '../../application/presupuestos/arbolEsencial.js';
import { ESENCIALES } from '../../application/presupuestos/ordenDeSecciones.js';
import { pideGuia } from '../../application/presupuestos/asistenteGuiado.js';
import { ARBOLES, ARBOLES_SHA256 } from '../../application/presupuestos/arbolesMaderinco.js';
import { contextoDeConsulta, FRASE_SIN_DATOS, negocioCambio, fraseNegocioCambio } from '../../application/asistente/contextoDeRespuesta.js';
import { contextoRespuestaHTML } from '../shared/NxContextoRespuesta.js';
import { normalizarPlantilla, itemDesdePlantilla, erroresDelFormulario, palabras, unidadDeLista } from '../../application/presupuestos/plantilla.js';
import { puedeCargarCatalogo, revisarArchivo, resumenDeCarga, fraseDeCarga, fraseDelError } from '../../application/presupuestos/cargaCatalogo.js';
import { revisarArchivoCapitulos, resumenDeCargaCapitulos, fraseDeCargaCapitulos, fraseDelErrorCapitulos } from '../../application/presupuestos/cargaCapitulos.js';
import { PILOTO_CUBIERTA, resumenDelPiloto, frasesDelPiloto, fraseDelFalloPiloto } from '../../application/presupuestos/cargaPiloto.js';
import { PASOS, leerUbicaciones, cuerpoUbicaciones, erroresDeLaApi, vistaPrevia, resumenDelCambio } from '../../application/presupuestos/ubicacionesProducto.js';
import { leerSeccionProductos, moverEnPaso, conOrdenEnSeccion } from '../../application/presupuestos/vistaPorSeccion.js';
import { leerCasarPlantilla, leerConfirmar, frasesDelCasado, tandasParaConfirmar, sumarConfirmados } from '../../application/presupuestos/catalogoUnico.js';
import { leerVistaPrevia, estadoInicial, problemaDeFila, cuerpoConfirmar, erroresPorFila, leerConfirmado, textoAviso, PASOS_IMPORTADOR, MAX_FILAS } from '../../application/presupuestos/importadorItems.js';
import { filaDeProducto, estadoDeFila, filtrarFilas, textoResultados, hayUbicaciones, opcionesDeFiltro, etiquetaPaso, referenciasSinProducto } from '../../application/presupuestos/misItems.js';
import { buscarEnPlantilla, buscarEnCatalogo, pideAclararTecho } from '../../application/presupuestos/guiaPlantilla.js';
import { TIPOS, Q2, Q3, IDS_DE_TIPO } from '../../application/presupuestos/arbolCubierta.js';
import { tarjetasDeTipos, ponerPartidaDelTipo, cambiarAreaDelTipo, partidaDelTipo, quitarPartidaDelTipo, valorDeLinea, MATERIAL } from '../../application/presupuestos/tipoEnTarjetas.js';
import { recorridoPorCapitulos, lineasDesdeApi, esencialesPorSeccion, seccionDeCadaItem, pasoPorSecciones, NOMBRES_DE_CAPITULO, nombreCortoDeCapitulo } from '../../application/presupuestos/capitulosDelCliente.js';
import { pantallasDelAsistente, lineaDelTipo, tarjetasDeLinea, claseDeLinea, moverPantalla, textoSiguiente, opcionesDeLinea, filaEnPresupuesto, resumenPorSeccion, etiquetasCortas } from '../../application/presupuestos/asistentePasos.js';
import { asistenteHTML, pantallaTipoHTML, pantallaCantidadesHTML, pantallaRevisionHTML, pantallaResumenHTML } from '../components/presupuestos/AsistentePasos.js';
import { leerItemEstandar, estandarPorLinea } from '../../application/presupuestos/itemEstandar.js';
import { montarCatalogoEstandar, montarEditorEstandar } from '../components/presupuestos/CatalogoEstandar.js';
import { replicarPresupuesto, preciosDeHoy, fraseDeLaReplica, casamientoDesdeApi, llevarReplicaALosPasos } from '../../application/presupuestos/replicarPresupuesto.js';
import { LINEAS_DEL_CLIENTE } from '../../application/presupuestos/lineasDelCliente.js';
import { recorridoCapitulosHTML } from '../components/presupuestos/RecorridoCapitulos.js';
import { seccionesDeCubierta, candidatosDe, complementosDe, cantidadExplicita, estadoDelConjunto, cambiarTipo, indicePorFuente } from '../../application/presupuestos/seleccionCubierta.js';

/**
 * MADERINCO-PANTALLA-ITEMS-T1 (T129): la plantilla de secciones del negocio demo. null = sin consultar; false = este
 * negocio no la tiene (PUERTA_CERRADA fuera del demo, o la ruta aun no publicada): la pantalla queda como hoy.
 * Se consulta una vez por negocio.
 */
let plantillaDemo = null;
let plantillaDeNegocio = null;
// La lista del cliente para el modo por capitulos y de donde salio ('api' o 'archivo'); se lee con la plantilla.
let lineasCliente = LINEAS_DEL_CLIENTE;
let fuenteLineasCliente = 'archivo';
// Grupo 4: linea del cliente -> { plantilla, estandar } del item estandar; vacio si la API no lo sirve.
let estandarDeLineas = new Map();
// MAD-LAPIZ-ITEM-T1: la lectura entera (con el numero de cada linea), la que el editor de la fila necesita para guardar.
let lecturaEstandar = null;
const lineaDeLectura = (nombre) => (lecturaEstandar?.capitulos || []).flatMap((c) => c.lineas).find((x) => x.linea === nombre) || null;
const ponerLecturaEstandar = (lectura) => { lecturaEstandar = lectura; estandarDeLineas = lectura ? estandarPorLinea(lectura) : new Map(); };
let plantillaEnVuelo = null;
/**
 * PR 4: estandarId -> { n, nombre, item }: la seccion del cliente de su linea y el item de la plantilla de su primer ref
 * servido. Se arma con lo que cargarPlantilla dejo (plantilla, lista del cliente e item estandar).
 */
function ubicadorDeEstandar() {
  const porId = new Map();
  if (!plantillaDemo || !estandarDeLineas.size) return () => null;
  const porRef = new Map(plantillaDemo.secciones.flatMap((s) => s.items.map((i) => [i.fuenteId, { ...i, seccionClave: s.clave }])));
  for (const cap of recorridoPorCapitulos(plantillaDemo, lineasCliente).capitulos) {
    for (const l of cap.lineas) {
      for (const e of estandarDeLineas.get(l.linea)?.estandar || []) {
        if (porId.has(e.id)) continue;
        const item = (e.items || []).map((i) => porRef.get(i.ref)).find(Boolean);
        if (item) porId.set(e.id, { n: cap.numero, nombre: NOMBRES_DE_CAPITULO[cap.numero] || cap.nombre, item });
      }
    }
  }
  return (id) => porId.get(String(id)) || null;
}

function cargarPlantilla() {
  const negocio = getSelectedBusiness() || '';
  if (plantillaDeNegocio !== negocio) { plantillaDemo = null; plantillaEnVuelo = null; plantillaDeNegocio = negocio; }
  if (plantillaDemo !== null) return Promise.resolve(plantillaDemo);
  if (!plantillaEnVuelo) {
    plantillaEnVuelo = PresupuestosApiClient.plantilla().then(async (res) => {
      const p = res.ok ? normalizarPlantilla(res.data) : null;
      // MADERINCO-LISTA-ITEMS-SECCIONES-T1 (A #36427, Ares #36348 cond. 4): la lista del cliente sale de la API; si la
      // API no la tiene (sin cargar o falla), el archivo del panel. Solo para el demo (la ruta es solo demo).
      if (p && p.secciones.length > 0) {
        const cap = await PresupuestosApiClient.capitulos().catch(() => null);
        const desdeApi = cap?.ok ? lineasDesdeApi(cap.data) : null;
        lineasCliente = desdeApi || LINEAS_DEL_CLIENTE;
        fuenteLineasCliente = desdeApi ? 'api' : 'archivo';
        // MAD-IMPLEMENTAR-DISENO-T1 grupo 4: el item estandar de cada linea (nexo-api 785). Sin el, las lineas como hoy.
        const ie = await PresupuestosApiClient.itemEstandar().catch(() => null);
        ponerLecturaEstandar(ie?.ok ? leerItemEstandar(ie) : null);
      }
      plantillaDemo = p && p.secciones.length > 0 ? p : false;
      return plantillaDemo;
    }).catch(() => (plantillaDemo = false));
  }
  return plantillaEnVuelo;
}
const esDemoMaderinco = () => !!plantillaDemo;
// MADERINCO-DASHBOARD-PARPADEO-T1 (Ares #30330): los atajos del asistente se pintaban DOS veces -- primero en orden y
// palabras de cualquier negocio ("Subir un PDF... Mis productos") y, al llegar la plantilla (~600 ms), en los del demo
// ("Mis presupuestos, Mis items..."). Eso era el parpadeo de nombres. El negocio demo se sabe al pintar (es el del
// selector); la plantilla solo existe para el, asi que el orden y las palabras salen bien desde la primera pintada.
const NEGOCIO_DEMO_MADERINCO = 'biz_demo_maderinco';
const esDemoAlPintar = () => esDemoMaderinco() || getSelectedBusiness() === NEGOCIO_DEMO_MADERINCO;
/** La seccion de la plantilla que corresponde a una seccion del armador (por su clave, o por el nombre). */
function seccionDePlantilla(s) {
  if (!plantillaDemo || !s) return null;
  const n = String(s.nombre || '').trim().toLowerCase();
  return plantillaDemo.secciones.find((p) => (s.plantillaClave && p.clave === s.plantillaClave) || p.nombre.trim().toLowerCase() === n) || null;
}
/** #36553: el numero de la seccion del cliente de una seccion del armador (por su marca o por su nombre), o null. */
function numeroDeSeccionCliente(s) {
  if (Number.isInteger(s?.seccionCliente)) return s.seccionCliente;
  const n = String(s?.nombre || '').trim().toLowerCase();
  const par = Object.entries(NOMBRES_DE_CAPITULO).find(([, v]) => v.toLowerCase() === n);
  return par ? Number(par[0]) : null;
}

/** La consola viva de la recepcion, para repintarle los chips con las cuentas. */
let consolaRecepcion = null;

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
const fmtCOP = (n) => '$' + Math.round(Number(n) || 0).toLocaleString('es-CO');
const fmtNum = (n) => (Number(n) || 0).toLocaleString('es-CO', { maximumFractionDigits: 3 });
const num = (v) => { const n = parsearNumeroColombiano(v); return (n === null || isNaN(n)) ? 0 : n; };

/**
 * TEATRO DE LA DEMO (idea de Josse, ASSIGN #8480).
 *
 * En la demo los presupuestos no estan ahi desde el principio: van APARECIENDO
 * con cada acto, para que el cliente vea la herramienta llenarse. La cortina es
 * SOLO del front y SOLO en modo enfoque: los 3 presupuestos siguen reales en la
 * BD, y para un negocio de verdad no existe ninguna cortina.
 *
 * El paso sube solo con lo que pasa en pantalla (subir un PDF, pedirle al
 * asistente un borrador o una plantilla) y se reinicia con un boton, asi que la
 * demo se puede repetir infinitas veces sin tocar datos.
 */
const DEMO_PASO_KEY = 'nx_demo_step';
const DEMO_ORDEN = ['JJ 1271', 'JJ 1272', 'CABANA-STD-80'];

function demoActiva() {
  try { return enfoqueActivo(); } catch (e) { return false; }
}

function demoPaso() {
  if (!demoActiva()) return Infinity; // sin cortina fuera del modo enfoque
  const n = Number(localStorage.getItem(DEMO_PASO_KEY));
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function demoSubirPaso(n) {
  if (!demoActiva()) return;
  const actual = Number(localStorage.getItem(DEMO_PASO_KEY)) || 0;
  if (n > actual) localStorage.setItem(DEMO_PASO_KEY, String(n));
}

/** Deja pasar solo los presupuestos que ya fueron revelados. */
function demoFiltrar(items) {
  const paso = demoPaso();
  if (!Number.isFinite(paso)) return items;
  const visibles = DEMO_ORDEN.slice(0, paso);
  const delPaso = (items || []).filter(p => visibles.includes(String(p.codigo ?? p.Codigo ?? '')));

  // UNA fila por codigo: el teatro revela UN presupuesto por paso, no todas las
  // versiones que compartan ese codigo. Josse subio el PDF y le aparecieron DOS
  // CHOIBA JJ 1271 -- la v4 emitida (la de verdad) y una v5 borrador que habia
  // quedado de probar "Usar como borrador". Parecia que el upload creaba un
  // borrador solo; medido en BD, el upload solo encola (los 3 uploads tienen
  // presupuesto_id NULL) y ese borrador tenia origen 'clonado-panel'.
  // Se queda la version VIGENTE (la mayor), que es la que el dueno llamaria
  // "su presupuesto"; las anteriores son historial, no filas sueltas.
  const porCodigo = new Map();
  for (const p of delPaso) {
    const cod = String(p.codigo ?? p.Codigo ?? '');
    const ver = Number(p.version ?? p.Version ?? 0) || 0;
    const previo = porCodigo.get(cod);
    if (!previo || ver > (Number(previo.version ?? previo.Version ?? 0) || 0)) porCodigo.set(cod, p);
  }
  // Se respeta el orden del guion, no el de llegada del servidor.
  return visibles.map(c => porCodigo.get(c)).filter(Boolean);
}

/** Estado del armador mientras el usuario arma (aun no guardado). */
const armador = { secciones: [], utilidadPct: 5, ivaUtilidadPct: 19, descuento: 0, areaM2: 0, proyecto: '', cliente: '' };

/**
 * Empezar uno nuevo es empezar EN BLANCO.
 *
 * El estado vive en el modulo y no se limpiaba nunca: despues de guardar, el
 * siguiente presupuesto arrancaba con las secciones del anterior. Y peor: el
 * formulario se re-renderiza vacio, pero proyecto y cliente seguian en el
 * estado -- o sea la pantalla decia una cosa y al guardar se mandaba otra, sin
 * que el dueno pudiera verlo. Eso es dato equivocado en silencio, no desorden.
 */
/**
 * Carga un presupuesto guardado EN el armador para editarlo como version nueva.
 *
 * El armador trabaja con su propio vocabulario (titulo/cantidad/precioUnit); el
 * guardado viene con el del server y puede llegar en camelCase o PascalCase. Se
 * traduce aqui, en un solo sitio, para que el armador no tenga que adivinar.
 */
function cargarEnArmador(p) {
  limpiarArmador();
  armador.proyecto = p.proyecto ?? p.Proyecto ?? '';
  armador.cliente = p.cliente_nombre ?? p.clienteNombre ?? p.ClienteNombre ?? '';
  armador.areaM2 = Number(p.area_m2 ?? p.areaM2 ?? p.AreaM2 ?? 0) || 0;
  const r = p.resumen ?? p.Resumen ?? {};
  armador.utilidadPct = Number(r.utilidadPct ?? r.UtilidadPct ?? 5) || 5;
  armador.ivaUtilidadPct = Number(r.ivaUtilidadPct ?? r.IvaUtilidadPct ?? 19) || 19;
  armador.descuento = Number(r.descuento ?? r.Descuento ?? 0) || 0;
  // El codigo y la version viajan para que al guardar sea VERSION NUEVA del
  // mismo presupuesto y no un presupuesto suelto sin parentesco.
  armador.codigo = p.codigo ?? p.Codigo ?? null;
  armador.version = Number(p.version ?? p.Version ?? 1) || 1;
  armador.secciones = seccionesOrdenadas(p.secciones ?? p.Secciones ?? []).map(sec => ({
    nombre: sec.nombre ?? sec.Nombre ?? 'Seccion',
    esAdicional: sec.es_adicional ?? sec.esAdicional ?? sec.EsAdicional ?? false,
    items: (sec.items ?? sec.Items ?? []).map(i => ({
      titulo: i.titulo ?? i.Titulo ?? '',
      productoId: i.producto_id ?? i.productoId ?? i.ProductoId ?? null,
      unidad: i.unidad ?? i.Unidad ?? '',
      cantidad: Number(i.cantidad ?? i.Cantidad ?? 0) || 0,
      precioUnit: Number(i.precio_unit ?? i.precioUnit ?? i.PrecioUnit ?? 0) || 0
    }))
  }));
}

/**
 * Orden ESTABLE de las secciones: la obra primero y los adicionales al final,
 * y dentro de cada grupo por su numero.
 *
 * Argy-A midio (#8493) que el GET ordena por -orden- y que ese campo NO
 * desempata: el UNIQUE es (presupuesto_id, orden, es_adicional), asi que dos
 * secciones pueden compartirlo -- y de hecho lo comparten en los TRES
 * presupuestos (Cubierta y Adicionales, las dos con orden = 1). Postgres no
 * promete cual sale primero: hoy sale bien por el plan que tiene, y manana un
 * VACUUM o un indice nuevo pueden poner -Adicionales- ARRIBA de la obra en la
 * ficha del cliente, sin avisar.
 *
 * El arreglo de raiz es suyo (una linea en el ORDER BY). Esto es lo mio: la
 * pantalla no depende de un orden que nadie prometio, y ademas queda agrupado
 * como el PDF real. Si el server ordena igual, esto no cambia nada.
 */
function seccionesOrdenadas(secciones) {
  const esAdic = (s) => !!(s.es_adicional ?? s.esAdicional ?? s.EsAdicional ?? false);
  const num = (s) => Number(s.orden ?? s.Orden ?? 0) || 0;
  return [...(secciones || [])].sort((a, b) =>
    (esAdic(a) === esAdic(b)) ? num(a) - num(b) : (esAdic(a) ? 1 : -1));
}

function limpiarArmador() {
  armador.secciones = [];
  armador.proyecto = '';
  armador.cliente = '';
  armador.areaM2 = 0;
  armador.utilidadPct = 5;
  armador.ivaUtilidadPct = 19;
  armador.descuento = 0;
  armador.codigo = null;
  armador.version = null;
  // Presupuesto por pasos (D-126): el borrador del servidor, si el negocio usa pasos y la clave del Guardar en curso.
  armador._servidor = null;
  // REPLICAR-PRESUPUESTO-T1: de que presupuesto anterior salio este (la frase de arriba del armador).
  armador._replica = null;
  armador._porPaso = undefined;
  armador._claveGuardar = null;
}

/**
 * AIU -- REGLA DE CASA (arbitraje de Ares #8358, verificado contra el PDF
 * original de Maderinco): el cliente compara las FILAS que ve contra el TOTAL
 * que ve, con calculadora en mano. Si no suman, perdimos. Por eso cada linea
 * se redondea a peso ENTERO y el total es una suma de enteros:
 *
 *   subtotal = SUM(items.subtotal)            [columna GENERADA, ya redondeada]
 *   utilidad = round(subtotal x pct)
 *   iva      = round(utilidad_YA_REDONDEADA x ivaPct)
 *   total    = subtotal + utilidad + iva - descuento
 *   valor_m2 = round(total / area)            [redondeo, no truncado]
 *
 * ADICIONALES: las secciones con esAdicional van en SU PROPIO bloque con el
 * mismo esquema, y su total se suma al final. Verificado contra el Choiba real
 * (JJ 1271, el que vino por pdf-import):
 *
 *   obra        221.883.434 + 11.094.172 + 2.107.893 = 235.085.499
 *   adicionales  12.650.000 +    632.500 +   120.175 =  13.402.675
 *   suma                                               248.488.174
 *   descuento                                        -  1.000.000  ("visita en sitio")
 *   TOTAL                                              247.488.174   (3.825.165 / m2)
 *
 * Sumarlos en un solo bloque, como hacia la primera version, daba otro total.
 *
 * EL DESCUENTO ES LA EXCEPCION: es la unica cifra que NO pasa por el 1,0595
 * (se resta AL FINAL, despues del IVA), asi que mueve el total peso por peso.
 * Olvidarlo al medir cuesta caro: yo lo omiti en una consulta, me dio 248.488.174,
 * y con eso llegue a acusar de equivocado a este mismo comentario, que estaba
 * bien. El clon de prueba JJ 1272 no tiene descuento y por eso SI da 248.488.174:
 * dos totales distintos para el mismo subtotal, y los dos correctos.
 *
 * Una sola verdad: esto calcula SOLO lo aun no guardado; lo guardado se pinta
 * con el resumen que manda el server (que usa esta misma regla).
 */
export function calcularAiu({ secciones = [], utilidadPct = 0, ivaUtilidadPct = 0, descuento = 0, areaM2 = 0 } = {}) {
  const sumaDe = (lista) => lista.reduce((acc, s) =>
    acc + (s.items || []).reduce((a, i) =>
      a + Math.round((Number(i.cantidad) || 0) * (Number(i.precioUnit) || 0)), 0), 0);

  const bloque = (lista) => {
    const sub = sumaDe(lista);
    const util = Math.round(sub * (Number(utilidadPct) || 0) / 100);
    const iva = Math.round(util * (Number(ivaUtilidadPct) || 0) / 100);
    return { subtotal: sub, utilidad: util, ivaUtilidad: iva, total: sub + util + iva };
  };

  const base = bloque(secciones.filter(s => !s.esAdicional));
  const adicionales = bloque(secciones.filter(s => s.esAdicional));
  const total = base.total + adicionales.total - (Number(descuento) || 0);
  const areaNum = Number(areaM2) || 0;

  return {
    subtotal: base.subtotal,
    utilidad: base.utilidad,
    ivaUtilidad: base.ivaUtilidad,
    adicionales,
    total,
    valorM2: areaNum > 0 ? Math.round(total / areaNum) : null
  };
}

/** Mensaje honesto segun la causa (leccion del banner que mentia, #8279). */
function mensajeFalla(code, queFalta) {
  if (code === 'FORBIDDEN') return 'Tu usuario no tiene permiso para ver esto.';
  if (code === 'NOT_IMPLEMENTED') return queFalta;
  return 'No se pudo cargar ahora. Intenta de nuevo en un momento.';
}

/**
 * Falla contada por CAUSA, incluido el ambiente. La captura de Josse decia
 * "falta publicar la consulta" cuando el endpoint YA estaba publicado: el panel
 * estaba en dev, preguntandole a una API local que no estaba levantada. Un
 * mensaje que no distingue eso hace dudar de algo que si funciona.
 */
function bloqueFalla(res, queFalta, detalle) {
  const code = res.error?.code;
  const chico = 'font-size:0.875rem;color:#8b949e';
  // El negocio de arriba y el negocio del usuario no son lo mismo. Decirlo con
  // nombre y con salida practica, en vez de un "no tienes permiso" que deja al
  // dueno sin saber que hacer.
  if (code === 'NEGOCIO_AJENO' || code === 'SIN_IDENTIDAD') {
    const nombre = getSelectedBusinessName() || getSelectedBusiness() || 'ese negocio';
    return `<p class="empty-state" style="color:#d29922">Tu usuario no est&aacute; asignado a <b>${esc(nombre)}</b>.
      <br><span style="${chico}">Arriba lo tienes seleccionado, pero el acceso lo da el negocio, no el selector. P&iacute;dele acceso a quien lo administra, o entra con el usuario de ese negocio.</span></p>`;
  }
  if (code === 'NO_EXISTE') {
    return `<p class="empty-state" style="color:#d29922">Ese presupuesto ya no est&aacute; en tu negocio.
      <br><span style="${chico}">Puede que lo hayan borrado, o que pertenezca a otro negocio.</span></p>`;
  }
  if (code === 'SIN_NEGOCIO') {
    return `<p class="empty-state" style="color:#d29922">Tu usuario no tiene un negocio asignado.
      <br><span style="${chico}">Los presupuestos son por negocio: sin eso no hay que mostrar.</span></p>`;
  }
  if (code === 'SIN_SESION') {
    return `<p class="empty-state" style="color:#d29922">Tu sesi&oacute;n no est&aacute; activa o ya venci&oacute;.
      <br><span style="${chico}">Vuelve a entrar para seguir.</span></p>`;
  }
  if (code === 'FORBIDDEN') {
    return `<p class="empty-state" style="color:#d29922">Tu usuario no tiene permiso para ver esto.</p>`;
  }
  if (AppConfig.env === 'dev') {
    return `<p class="empty-state" style="color:#d29922">Est&aacute;s en <b>ambiente dev</b>: el panel le pregunta a tu API local (${esc(AppConfig.apiBase)}) y no obtuvo respuesta.
      <br><span style="${chico}">Esta consulta S&Iacute; est&aacute; publicada en producci&oacute;n. Cambia el ambiente a prod, o levanta tu API local.</span></p>`;
  }
  if (code === 'NOT_IMPLEMENTED') {
    return `<p class="empty-state" style="color:#d29922">${esc(queFalta)}<br><span style="${chico}">${esc(detalle || 'Falta publicar esta consulta en la API; en cuanto exista, esto se enciende solo.')}</span></p>`;
  }
  return `<p class="empty-state" style="color:#d29922">No pudimos hablar con la API en este momento.<br><span style="${chico}">Intenta de nuevo en un momento.</span></p>`;
}

function burbujaAgente(html) {
  return `<div style="display:flex;gap:10px;align-items:flex-start;margin-bottom:12px">
    <div style="width:34px;height:34px;border-radius:50%;background:linear-gradient(135deg,#238636,#58a6ff);display:flex;align-items:center;justify-content:center;font-size:1rem;flex-shrink:0">&#129333;</div>
    <div style="background:#161b22;border:1px solid #30363d;border-radius:12px;border-top-left-radius:2px;padding:12px 14px;font-size:0.875rem;color:#c9d1d9;line-height:1.5;max-width:640px">${html}</div>
  </div>`;
}

// ---------------------------------------------------------------- V1 recepcion

/**
 * Chat de la recepcion: el acto 2 de la demo vive aqui.
 *
 * NO inventa nada: usa AgentApiClient.sendConsulta, que ya existe y ya manda el
 * businessId del selector (fallback chain del workflow Consulta Nexo). Las
 * capabilities presupuestos.* estan live, asi que "usa el Choiba como borrador"
 * o "cabana estandar de 80 metros" responden de verdad.
 *
 * Si la respuesta menciona un codigo de presupuesto, se ofrece abrirlo: el
 * codigo se resuelve a id con GET /presupuestos?codigo=, que tambien existe.
 */
/**
 * La recepcion con datos de verdad (feedback Josse #8476, punto 4).
 *
 * Un saludo generico no dice nada; "tienes 3 presupuestos guardados, el ultimo
 * por $247.488.174" demuestra que el asistente SABE de este negocio. Sale de los
 * dos GET que ya existen: no hay endpoint nuevo.
 *
 * Es adorno con red: si algo falla, la recepcion queda como estaba y nadie ve
 * un error. Por eso no bloquea el render ni se espera antes de pintar.
 */
async function darVidaALaRecepcion($host, irA) {
  const [productos, guardados] = await Promise.all([
    PresupuestosApiClient.listarProductos({}),
    PresupuestosApiClient.listarPresupuestos({}),
    cargarPlantilla()
  ]);

  const cuentas = { esDemo: esDemoAlPintar() };

  if (productos.ok && Array.isArray(productos.data)) {
    // Teatro: en el paso 0 el negocio "todavia no tiene nada montado", asi que
    // el contador no puede cantar 49 productos mientras la lista dice que no
    // hay presupuestos. Se revelan juntos, al subir el PDF (captura de Josse).
    cuentas.productos = demoPaso() === 0 ? 0 : productos.data.length;
  }

  if (guardados.ok && Array.isArray(guardados.data)) {
    const lista = demoFiltrar(guardados.data);
    cuentas.guardados = lista.length;

    if (lista.length > 0) {
      // El ultimo es el primero: el listado viene por fecha DESC del server.
      const ultimo = lista[0];
      const total = Number(ultimo.total ?? ultimo.Total ?? 0);
      const codigo = ultimo.codigo ?? ultimo.Codigo ?? '';
      const $saludo = $host.querySelector('[data-presu-saludo]');
      if ($saludo && total > 0) {
        $saludo.insertAdjacentHTML('beforeend',
          `<br><span style="color:#8b949e">Tienes <b>${lista.length}</b> presupuesto${lista.length === 1 ? '' : 's'} guardado${lista.length === 1 ? '' : 's'}; el &uacute;ltimo${codigo ? ' (' + esc(codigo) + ')' : ''} por <b>${fmtCOP(total)}</b>.</span>`);
      }
    }
  }

  // Los chips se repintan con las cuentas: el atajo lleva el dato encima.
  if (consolaRecepcion && irA) consolaRecepcion.chips(chipsRecepcion(irA, cuentas));
}

/** Una conversacion por sesion de recepcion. */
let conversacionRecepcion = null;

/**
 * El cerebro del chat de la recepcion, ahora hablando con la consola en vez de
 * con el DOM. NO inventa nada: usa AgentApiClient.sendConsulta, que ya existe y
 * pregunta por el negocio del selector (la API lo toma de la ruta).
 */
async function responderConsulta(texto, api, $host, negocio) {
  if (!conversacionRecepcion) conversacionRecepcion = `presu-${Date.now()}`;
  // PANEL-ASISTENTE-FORMATO-T1: se recuerda para que negocio se pregunto; si cambia en el camino, no se pinta como actual.
  const consultadoPara = getSelectedBusiness();
  const nombrePara = getSelectedBusinessName() || negocio;
  const res = await AgentApiClient.sendConsulta(texto, conversacionRecepcion);
  if (negocioCambio(consultadoPara, getSelectedBusiness())) { api.responder(esc(fraseNegocioCambio(nombrePara))); return; }
  const d = res.ok ? (res.data || {}) : null;

  // Se dice lo que paso, por causa. Un "no pude" mudo en una demo es peor que
  // no tener el chat: el presentador no sabe si insistir o seguir.
  if (!d) {
    api.responder(`${esc(String(res.error || 'No pude consultar ahora mismo.'))} Puedes seguir con los atajos de abajo.`);
    return;
  }
  if (d.error) {
    api.responder(`${esc(String(d.error))}<br><span style="color:#8b949e">Prueba de otra forma, o usa un atajo de abajo.</span>`);
    return;
  }

  // Teatro: la capacidad que respondio decide que se revela.
  const cap = String(d.capability_used || '');
  if (cap.includes('presupuestos.borrador')) demoSubirPaso(2);
  if (cap.includes('presupuestos.plantilla')) demoSubirPaso(3);

  const narrativa = d.narrative ? String(d.narrative) : '';
  // PANEL-ASISTENTE-FORMATO-T1: conclusion arriba; debajo la linea de contexto (negocio, fuente, hora del dato separada
  // de la de consulta) y lo que se tomo por defecto. Sin frase y sin filas: "no encontre datos", no "Listo".
  const ctx = contextoDeConsulta(d, { negocio: nombrePara, consultadoEn: new Date() });
  const cuerpo = (narrativa
    ? esc(narrativa).replace(/\n/g, '<br>')
    : ctx.vacio ? esc(FRASE_SIN_DATOS) : 'Listo. Mira los atajos de abajo para seguir.') + contextoRespuestaHTML(ctx);
  const codigo = codigoDePresupuesto(d);
  const $burbuja = api.responder(cuerpo);

  if (codigo && $burbuja) {
    $burbuja.querySelector('.nx-burbuja-texto').insertAdjacentHTML('beforeend',
      `<div style="margin-top:10px"><button type="button" class="btn-primary btn-sm" data-presu-ver-codigo="${esc(codigo)}">Ver ${esc(codigo)}</button></div>`);
    const $b = $burbuja.querySelector('[data-presu-ver-codigo]');
    $b.addEventListener('click', () => abrirPorCodigo($host, negocio, codigo, $b));
  }
}

/** Busca un codigo de presupuesto en las filas o en la narrativa. */
function codigoDePresupuesto(d) {
  const filas = Array.isArray(d.rows) ? d.rows : [];
  for (const f of filas) {
    const c = f?.codigo ?? f?.Codigo ?? f?.codigo_presupuesto;
    if (c) return String(c);
  }
  // En la narrativa aparecen como "JJ 1271" o "CABANA-STD-80".
  const m = String(d.narrative || '').match(/\b([A-Z]{2,}[A-Z0-9-]*\s?\d{3,}|[A-Z]+-[A-Z]+-\d+)\b/);
  return m ? m[1].trim() : null;
}

/** El chat da el CODIGO; el detalle necesita el id. Se resuelve con el listado. */
async function abrirPorCodigo($host, negocio, codigo, $btn) {
  const textoOriginal = $btn.innerHTML;
  $btn.disabled = true;
  $btn.textContent = 'Abriendo...';
  const res = await PresupuestosApiClient.listarPresupuestos({ codigo });
  $btn.disabled = false;
  $btn.innerHTML = textoOriginal;
  const encontrado = res.ok ? (res.data || []).find(p => String(p.codigo ?? p.Codigo ?? '') === codigo) || (res.data || [])[0] : null;
  const id = encontrado ? (encontrado.id ?? encontrado.Id) : null;
  if (!id) {
    $btn.insertAdjacentHTML('afterend',
      `<span style="font-size:0.875rem;color:#d29922;margin-left:8px">No lo encontre en tus presupuestos.</span>`);
    return;
  }
  renderDetalle($host, negocio, id, { desde: 'asistente' });
}

function renderRecepcion($host, negocio) {
  $host.innerHTML = `
    <section class="artifact-view presu-scope">
      <div class="artifact-header"><div>
        ${demoActiva() ? '' : regresoHtml()}
        <h1>Presupuestos</h1>
      </div></div>

      <div data-presu-consola></div>

      <div id="nx-presu-zona" style="margin-top:16px;max-width:760px"></div>
    </section>`;

  // El chat es el PROTAGONISTA (idea de Josse, #8529): lo primero que se ve es
  // una pregunta, no un menu que hay que aprender. Las cuatro acciones no se
  // botan -- bajan a chips, que es la carta de sugerencias para quien todavia
  // no sabe que preguntar.
  const irA = {
    subir: () => renderDropzone(document.getElementById('nx-presu-zona')),
    crear: () => renderArmador($host, negocio),
    catalogo: () => renderCatalogo($host, negocio),
    guardados: () => renderGuardados($host, negocio)
  };

  consolaRecepcion = montarConsola({
    contenedor: $host.querySelector('[data-presu-consola]'),
    agente: { nombre: `Asistente de ${negocio || 'tu negocio'}`, avatar: '\u{1F9D1}‍\u{1F4BC}' },
    saludo: `<span data-presu-saludo>Hola. Yo te armo los presupuestos: puedo montar los que ya tienes hechos,
      crear uno nuevo contigo, o mostrarte tus productos y precios.
      <br><span style="color:#8b949e">Preg&uacute;ntame lo que necesites, o usa un atajo de abajo.</span></span>`,
    placeholder: 'Escribe tu pregunta... (ej. usa el Choiba como borrador)',
    sugerencias: chipsRecepcion(irA, { esDemo: esDemoAlPintar() }),
    onPregunta: (texto, api) => responderConsulta(texto, api, $host, negocio)
  });

  darVidaALaRecepcion($host, irA);
}

/** Los cuatro atajos de siempre, ahora como chips. */
function chipsRecepcion(irA, cuentas = {}) {
  // T129: en el demo de Maderinco el orden es Mis presupuestos / Mis items / Crear presupuesto / Subir un PDF, y se
  // dice "item". En los demas negocios, como hoy.
  const w = palabras(!!cuentas.esDemo);
  const subir = { id: 'subir', texto: '\u{1F4C4} Subir un PDF', accion: irA.subir };
  const crear = { id: 'crear', texto: '\u{1F9EE} Crear presupuesto', accion: irA.crear };
  const catalogo = {
    id: 'catalogo',
    texto: cuentas.productos ? `\u{1F4E6} ${w.mis} (${cuentas.productos})` : `\u{1F4E6} ${w.mis}`,
    accion: irA.catalogo
  };
  const guardados = {
    id: 'guardados',
    texto: cuentas.guardados ? `\u{1F4CB} Mis presupuestos (${cuentas.guardados})` : '\u{1F4CB} Mis presupuestos',
    accion: irA.guardados
  };
  // CATALOGO-UNICO-ITEMS-T1 (Josse #43584): sin atajo aparte de "Items estandar"; el editor vive en Mis items.
  return cuentas.esDemo ? [guardados, catalogo, crear, subir] : [subir, crear, catalogo, guardados];
}


/**
 * Espejo de MaxPdfBytes del PresupuestosController. Vive en dos lados a
 * proposito y esta anotado: aqui solo sirve para no hacer esperar una subida
 * que va a rebotar. Quien manda es la API.
 */
const MAX_PDF_BYTES = 15 * 1024 * 1024;

const enMB = (bytes) => (bytes / (1024 * 1024)).toLocaleString('es-CO', { maximumFractionDigits: 1 }) + ' MB';

/**
 * Cada rechazo con su nombre. Antes TODO 400 caia en "intenta de nuevo en un
 * momento", que para un archivo muy grande o que no es PDF es falso: reintentar
 * lo mismo da lo mismo.
 */
function razonDelRechazo(res, file) {
  const chico = 'color:#8b949e';
  switch (res.error?.code) {
    case 'MUY_GRANDE':
      return `Ese PDF pesa <b>${enMB(file.size)}</b> y no cabe.<br><span style="${chico}">M&aacute;ndamelo partido, o comprimido.</span>`;
    case 'NO_ES_PDF':
      return `Ese archivo no es un PDF de verdad, aunque se llame as&iacute;.<br><span style="${chico}">Suele pasar con fotos renombradas: exp&oacute;rtalo como PDF y me lo mandas.</span>`;
    case 'SIN_ARCHIVO':
      return 'No lleg&oacute; el archivo. Vuelve a soltarlo aqu&iacute;.';
    case 'SIN_SESION':
      // Medido contra produccion: el upload responde 401 sin token o con token
      // vencido, y esto se leia como "no tienes permiso". Son cosas distintas y
      // la salida practica tambien: aca hay que volver a entrar.
      return `Tu sesi&oacute;n no est&aacute; activa o ya venci&oacute;.
        <br><span style="color:#8b949e">Vuelve a entrar y repite el env&iacute;o; el archivo no se perdi&oacute;.</span>`;
    case 'FORBIDDEN':
      return `Tu usuario no tiene permiso para subir presupuestos.
        <br><span style="color:#8b949e">Entraste bien, pero tu rol no alcanza: p&iacute;deselo a quien administra el negocio.</span>`;
    case 'NOT_IMPLEMENTED':
      return `La recepci&oacute;n de PDF todav&iacute;a no est&aacute; conectada.<br><span style="${chico}">Mand&aacute;selo al equipo mientras tanto.</span>`;
    default:
      return `No pudimos recibirlo en este momento.<br><span style="${chico}">Intenta de nuevo en un momento.</span>`;
  }
}

/** Subir PDF: promesa CONTROLADA -- queda en cola, no se procesa en fase A. */
function renderDropzone($zona) {
  if (!$zona) return;
  $zona.innerHTML = `
    ${burbujaAgente('S&uacute;beme el PDF y lo dejo en la fila. <b>En 24 horas lo tienes montado</b> en tu cat&aacute;logo &mdash; te aviso cuando est&eacute;.')}
    <div id="nx-presu-drop" style="border:2px dashed #30363d;border-radius:10px;padding:22px;text-align:center;color:#8b949e;font-size:0.875rem;cursor:pointer;background:#0d1117">
      Arrastra el PDF aqu&iacute; o haz clic para elegirlo
      <input type="file" accept="application/pdf" style="display:none">
    </div>
    <div id="nx-presu-drop-msg" style="font-size:0.875rem;margin-top:8px;min-height:1.2em"></div>`;

  const $drop = document.getElementById('nx-presu-drop');
  const $input = $drop.querySelector('input');
  const $msg = document.getElementById('nx-presu-drop-msg');

  // Un envio a la vez. En la BD quedaron TRES uploads del mismo archivo, dos de
  // ellos en el mismo minuto: subir dos veces no rompe nada (el endpoint solo
  // encola) pero ensucia la cola y hace dudar de si se mando o no.
  let enviando = false;

  const enviar = async (file) => {
    if (!file || enviando) return;
    if (file.type && !file.type.includes('pdf')) {
      $msg.style.color = '#d29922';
      $msg.textContent = 'Por ahora solo recibo archivos PDF.';
      return;
    }
    // Pesar ANTES de subir: hacer esperar una subida entera para despues decir
    // que no cabe es la peor forma de contarlo. La AUTORIDAD sigue siendo la API
    // (valida tamano y firma %PDF); esto es cortesia, y si algun dia los limites
    // no coinciden, el mensaje de la API igual aterriza honesto abajo.
    if (file.size > MAX_PDF_BYTES) {
      $msg.style.color = '#d29922';
      $msg.innerHTML = `Ese PDF pesa <b>${enMB(file.size)}</b> y el m&aacute;ximo son <b>${enMB(MAX_PDF_BYTES)}</b>.
        <br><span style="color:#8b949e">Mand&aacute;melo partido, o comprimido.</span>`;
      return;
    }
    $msg.style.color = '#8b949e';
    $msg.textContent = 'Subiendo ' + file.name + '...';
    enviando = true;
    let res;
    try {
      res = await PresupuestosApiClient.subirPdf(file);
    } finally {
      enviando = false;
      // Sin esto, elegir el MISMO archivo otra vez no dispara change y parece
      // que el boton no responde.
      $input.value = '';
    }
    if (res.ok) {
      $msg.style.color = '#3fb950';
      $msg.innerHTML = 'Recibido. Queda en la fila &mdash; <b>en 24 horas lo tienes montado</b>.';
      // Teatro: subir el PDF revela el primer presupuesto. En la demo el
      // presentador lo sube en vivo y la lista deja de estar vacia sola.
      if (demoPaso() < 1) {
        demoSubirPaso(1);
        $msg.insertAdjacentHTML('beforeend',
          '<br><span style="color:#8b949e">Tu presupuesto qued&oacute; montado.</span> <button type="button" class="btn-primary btn-sm" data-presu-ver-montado style="margin-top:8px">Verlo</button>');
        $msg.querySelector('[data-presu-ver-montado]').addEventListener('click', () => {
          const $host = document.getElementById('nx-main');
          renderGuardados($host, getSelectedBusinessName?.() || '');
        });
      }
      return;
    }
    $msg.style.color = '#d29922';
    $msg.innerHTML = razonDelRechazo(res, file);
  };

  $drop.addEventListener('click', (e) => {
    // El input vive DENTRO del dropzone: sin esta guarda su propio click
    // burbujea hasta aqui y vuelve a abrir el selector de archivos.
    if (e.target === $input) return;
    $input.click();
  });
  $input.addEventListener('change', () => enviar($input.files?.[0]));
  ['dragover', 'dragenter'].forEach(ev => $drop.addEventListener(ev, e => { e.preventDefault(); $drop.style.borderColor = '#238636'; }));
  ['dragleave', 'drop'].forEach(ev => $drop.addEventListener(ev, e => { e.preventDefault(); $drop.style.borderColor = '#30363d'; }));
  $drop.addEventListener('drop', e => enviar(e.dataTransfer?.files?.[0]));
}

// ----------------------------------------------------------------- V2 catalogo

/**
 * Alta y edicion de un producto del catalogo (POST/PUT /presupuestos/productos,
 * vivos en origin/main desde el principio).
 *
 * AUDIENCIA: NEGOCIO (dueno/equipo).
 *
 * OJO CON LA DESCRIPCION: el cliente manda `descripcion: producto.descripcion ??
 * null`. Si este formulario no incluyera el campo, editar un producto BORRARIA
 * la descripcion que ya tenia, en silencio. Por eso va precargado; no es adorno.
 *
 * Lecciones aplicadas: sin <label> envolviendo inputs (la trampa del flex-basis
 * del REDIRECT #2160), todo numero por el parser colombiano, cero prompt/alert,
 * y el panel se lleva la clase de ambito consigo porque vive en document.body.
 */
function abrirFormProducto(prod, alGuardar) {
  const esNuevo = !prod?.id;
  const val = (x) => (x === null || x === undefined || x === 0 ? '' : String(x).replace('.', ','));

  const $ov = document.createElement('div');
  $ov.style.cssText = 'position:fixed;inset:0;background:#0d1117cc;z-index:9998';
  const $panel = document.createElement('div');
  $panel.className = 'presu-scope';
  $panel.style.cssText = 'position:fixed;top:0;right:0;height:100vh;width:440px;max-width:100vw;background:#0d1117;border-left:1px solid #30363d;z-index:9999;display:flex;flex-direction:column';
  $panel.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;padding:14px 16px;border-bottom:1px solid #21262d;flex-shrink:0">
      <h3 style="margin:0;font-size:0.95rem;color:#f0f6fc">${esNuevo ? 'Nuevo producto' : 'Editar producto'}</h3>
      <button type="button" data-fp-cerrar title="Cerrar" style="background:transparent;border:none;color:#8b949e;cursor:pointer;font-size:1.4rem;line-height:1;padding:0 4px">&times;</button>
    </div>
    <div style="flex:1;overflow-y:auto;padding:16px">
      <div class="presu-campo" style="margin-bottom:16px"><span>Nombre</span>
        <input data-fp-nombre class="form-input" style="width:100%" placeholder="ej. Mes&oacute;n en granito" value="${esc(prod?.nombre ?? '')}"></div>

      <div class="presu-fila" style="margin-bottom:16px">
        <div class="presu-campo"><span>Unidad</span>
          <input data-fp-unidad class="form-input" style="width:110px" placeholder="m2, und, ml" value="${esc(prod?.unidad ?? '')}"></div>
        <div class="presu-campo"><span>Precio de venta</span>
          <input data-fp-venta class="form-input presu-num" style="width:14ch" inputmode="decimal" placeholder="1.250.000" value="${esc(val(prod?.precioVenta))}"></div>
      </div>

      <div class="presu-fila" style="margin-bottom:16px">
        <div class="presu-campo"><span>Costo</span>
          <input data-fp-costo class="form-input presu-num" style="width:14ch" inputmode="decimal" placeholder="opcional" value="${esc(val(prod?.costoUnitario))}"></div>
      </div>

      <div style="font-size:14px;color:#8b949e;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:8px">Por mayor (opcional)</div>
      <div class="presu-fila" style="margin-bottom:16px">
        <div class="presu-campo"><span>Precio</span>
          <input data-fp-mayor class="form-input presu-num" style="width:14ch" inputmode="decimal" value="${esc(val(prod?.precioMayor))}"></div>
        <div class="presu-campo"><span>Desde (unidades)</span>
          <input data-fp-desde class="form-input presu-num" style="width:8ch" inputmode="decimal" value="${esc(val(prod?.mayorDesdeUnidades))}"></div>
      </div>

      <div class="presu-campo" style="margin-bottom:16px"><span>Descripci&oacute;n</span>
        <textarea data-fp-desc class="form-input" rows="3" style="width:100%" placeholder="Opcional">${esc(prod?.descripcion ?? '')}</textarea></div>

      ${esDemoMaderinco() ? `<!-- MADERINCO-PRODUCTO-SECCION-CONTRATO-T1 (Ares #30713; diseno de Atenea WORK-PRODUCTO-SECCION-PASO): donde aparece. -->
      <section data-fp-ubic style="margin-bottom:16px;font-size:14px">
        <div style="color:#f0f6fc;font-weight:600;margin-bottom:4px">D&oacute;nde aparece en el presupuesto</div>
        <div style="color:#8b949e;margin-bottom:8px">Elige las secciones donde se ofrece y el paso en que aparece. No se agrega autom&aacute;ticamente.</div>
        ${esNuevo ? '<div data-fp-ubic-nuevo style="color:#8b949e">Guarda el producto primero; despu&eacute;s eliges d&oacute;nde aparece.</div>'
          : `<div data-fp-ubic-filas role="list"><div style="color:#8b949e">Leyendo d&oacute;nde aparece&hellip;</div></div>
        <button type="button" class="btn-ghost btn-sm" data-fp-ubic-anadir style="font-size:14px;margin-top:6px" disabled>+ A&ntilde;adir secci&oacute;n</button>
        <div data-fp-ubic-previa style="margin-top:10px;color:#c9d1d9"></div>
        <div data-fp-ubic-cambio style="margin-top:4px;color:#d29922"></div>`}
      </section>` : ''}

      <div style="display:flex;align-items:center;gap:8px;margin-bottom:16px">
        <input type="checkbox" data-fp-activo ${prod?.activo === false ? '' : 'checked'} style="width:16px;height:16px">
        <span style="font-size:14px;color:#c9d1d9">Se puede usar en presupuestos</span>
      </div>

      <button class="btn-primary btn-sm" data-fp-guardar style="width:100%">${esNuevo ? 'Crear producto' : 'Guardar cambios'}</button>
      <div data-fp-msg style="font-size:0.875rem;margin-top:10px;min-height:1.2em"></div>
    </div>`;
  document.body.appendChild($ov);
  document.body.appendChild($panel);

  // MADERINCO-PRODUCTO-SECCION-CONTRATO-T1: estado del bloque "Donde aparece" (solo demo y producto ya creado).
  const $ubic = $panel.querySelector('[data-fp-ubic-filas]');
  const ubic = { filas: [], original: [], revision: null, errores: {}, listo: false };
  const seccionesDemo = () => (plantillaDemo?.secciones || []).filter((s) => s.id);
  const nombreDeSeccion = (id) => seccionesDemo().find((s) => s.id === id)?.nombre || '';
  const pintarUbic = () => {
    if (!$ubic) return;
    const opcSec = (sel) => `<option value="">Elige la secci&oacute;n</option>` + seccionesDemo().map((s) =>
      `<option value="${esc(s.id)}"${s.id === sel ? ' selected' : ''}>${esc(s.nombre)}</option>`).join('');
    const opcPaso = (sel) => `<option value="">Elige el paso</option>` + PASOS.map((p) =>
      `<option value="${p.id}"${p.id === sel ? ' selected' : ''}>${esc(p.etiqueta)}</option>`).join('');
    $ubic.innerHTML = ubic.filas.length ? ubic.filas.map((f, i) => `
      <div role="listitem" data-fp-ubic-fila="${i}" style="display:flex;flex-wrap:wrap;gap:6px;align-items:center;padding:8px 0;border-top:1px solid #21262d">
        <select class="form-input" data-fp-ubic-sec="${i}" aria-label="Secci&oacute;n de la fila ${i + 1}" style="flex:1 1 160px;font-size:14px">${opcSec(f.seccionId)}</select>
        <select class="form-input" data-fp-ubic-paso="${i}" aria-label="Paso de la fila ${i + 1}" style="flex:1 1 140px;font-size:14px">${opcPaso(f.paso)}</select>
        <button type="button" class="btn-ghost btn-sm" data-fp-ubic-quitar="${i}" style="font-size:14px">Quitar asociaci&oacute;n</button>
        ${f.paso === 'revision_obligatoria' ? `<div style="flex-basis:100%;color:#8b949e">${esc(PASOS[1].ayuda)}</div>` : ''}
        ${f.pregunta ? `<div style="flex-basis:100%;color:#8b949e">Pregunta: ${esc(f.pregunta)}</div>` : ''}
        ${ubic.errores[i] ? `<div role="alert" data-fp-ubic-error="${i}" style="flex-basis:100%;color:#f85149">${esc(ubic.errores[i])}</div>` : ''}
      </div>`).join('') : '';
    const nombre = $panel.querySelector('[data-fp-nombre]').value.trim();
    $panel.querySelector('[data-fp-ubic-previa]').innerHTML = vistaPrevia(ubic.filas, nombre, nombreDeSeccion).map((l) => `<div>${esc(l)}</div>`).join('');
    $panel.querySelector('[data-fp-ubic-cambio]').textContent = resumenDelCambio(ubic.original, ubic.filas);
    $ubic.querySelectorAll('[data-fp-ubic-sec]').forEach((s) => s.addEventListener('change', () => { ubic.filas[+s.dataset.fpUbicSec].seccionId = s.value; delete ubic.errores[+s.dataset.fpUbicSec]; pintarUbic(); }));
    $ubic.querySelectorAll('[data-fp-ubic-paso]').forEach((s) => s.addEventListener('change', () => { ubic.filas[+s.dataset.fpUbicPaso].paso = s.value; delete ubic.errores[+s.dataset.fpUbicPaso]; pintarUbic(); }));
    $ubic.querySelectorAll('[data-fp-ubic-quitar]').forEach((b) => b.addEventListener('click', () => {
      ubic.filas.splice(+b.dataset.fpUbicQuitar, 1); ubic.errores = {}; pintarUbic();
      $panel.querySelector('[data-fp-ubic-anadir]')?.focus();
    }));
  };
  if ($ubic) {
    const cargarUbic = async () => {
      if (plantillaDemo === null) await cargarPlantilla();
      const r = await PresupuestosApiClient.leerUbicaciones(prod.id);
      if (!r.ok) { $ubic.innerHTML = `<div style="color:#d29922">${esc(mensajeFalla(r.error?.code, 'Todavía no se puede leer dónde aparece.'))}</div>`; return; }
      const l = leerUbicaciones(r.data);
      ubic.revision = l.revision; ubic.filas = l.filas; ubic.original = l.filas.map((f) => ({ ...f })); ubic.listo = true;
      $panel.querySelector('[data-fp-ubic-anadir]').disabled = false;
      pintarUbic();
    };
    cargarUbic();
    $panel.querySelector('[data-fp-ubic-anadir]').addEventListener('click', () => {
      ubic.filas.push({ seccionId: '', paso: '', orden: null, pregunta: null }); pintarUbic();
      $ubic.querySelector(`[data-fp-ubic-sec="${ubic.filas.length - 1}"]`)?.focus();
    });
  }
  // Una fila a medio llenar TAMBIEN es un cambio (medido: sin esto, Guardar cerraba y la fila incompleta se perdia sin
  // decir nada); asi pasa por cuerpoUbicaciones y el error queda en su fila.
  const hayCambiosUbic = () => ubic.listo && (ubic.filas.length !== ubic.original.length
    || ubic.filas.some((f) => !f.seccionId || !f.paso) || !!resumenDelCambio(ubic.original, ubic.filas));
  $panel.querySelector('[data-fp-nombre]').addEventListener('input', () => { if (ubic.listo) pintarUbic(); });

  // Cerrar con cambios sin guardar: se pregunta EN EL PANEL (diseno: nada de confirm nativo).
  const cerrarYa = () => { $ov.remove(); $panel.remove(); };
  const cerrar = () => {
    if (!hayCambiosUbic()) return cerrarYa();
    if ($panel.querySelector('[data-fp-descartar]')) return;
    const $d = document.createElement('div');
    $d.setAttribute('data-fp-descartar', '');
    $d.setAttribute('role', 'alertdialog');
    $d.style.cssText = 'padding:10px 16px;border-top:1px solid #d29922;background:#161b22;font-size:14px;color:#c9d1d9;display:flex;gap:8px;align-items:center;flex-wrap:wrap';
    $d.innerHTML = 'Tienes cambios de ubicaci&oacute;n sin guardar. <button type="button" class="btn-ghost btn-sm" data-fp-seguir style="font-size:14px">Seguir editando</button><button type="button" class="btn-sm" data-fp-descartar-si style="font-size:14px;background:#da3633;color:#fff">Descartar</button>';
    $panel.appendChild($d);
    $d.querySelector('[data-fp-seguir]').addEventListener('click', () => { $d.remove(); $panel.querySelector('[data-fp-guardar]').focus(); });
    $d.querySelector('[data-fp-descartar-si]').addEventListener('click', cerrarYa);
    $d.querySelector('[data-fp-seguir]').focus();
  };
  $ov.addEventListener('click', cerrar);
  $panel.querySelector('[data-fp-cerrar]').addEventListener('click', cerrar);

  const $nombre = $panel.querySelector('[data-fp-nombre]');
  const $msg = $panel.querySelector('[data-fp-msg]');
  const $btn = $panel.querySelector('[data-fp-guardar]');
  const textoBtn = $btn.innerHTML;
  const leer = (sel) => $panel.querySelector(sel).value;

  $nombre.focus();

  $btn.addEventListener('click', async () => {
    // La API exige nombre de al menos 2 letras: decirlo aqui y senalar el campo
    // es mejor que gastar el viaje para que vuelva un 400 generico.
    const nombre = leer('[data-fp-nombre]').trim();
    if (nombre.length < 2) {
      $msg.style.color = '#d29922';
      $msg.textContent = 'El nombre necesita al menos dos letras.';
      $nombre.classList.add('input-error');
      $nombre.focus();
      setTimeout(() => $nombre.classList.remove('input-error'), 1200);
      return;
    }

    // MADERINCO-PRODUCTO-SECCION-CONTRATO-T1: las filas se revisan ANTES de guardar nada (una incompleta no se guarda
    // como valida; el error va en su fila).
    let cuerpoUbic = null;
    if (hayCambiosUbic()) {
      const c = cuerpoUbicaciones(ubic.filas, ubic.revision);
      if (!c.ok) {
        ubic.errores = c.errores; pintarUbic();
        $msg.style.color = '#d29922';
        $msg.textContent = 'Revisa las filas marcadas en Dónde aparece en el presupuesto.';
        $ubic.querySelector(`[data-fp-ubic-fila="${Object.keys(c.errores)[0]}"] select`)?.focus();
        return;
      }
      cuerpoUbic = c.cuerpo;
    }

    $btn.disabled = true;
    $btn.innerHTML = '<span style="display:inline-block;width:16px;height:16px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;vertical-align:-3px;animation:presu-gira 0.7s linear infinite"></span> Guardando...';
    $msg.textContent = '';

    const res = await PresupuestosApiClient.guardarProducto({
      id: prod?.id ?? null,
      nombre,
      descripcion: leer('[data-fp-desc]').trim() || null,
      unidad: leer('[data-fp-unidad]').trim() || null,
      costoUnitario: num(leer('[data-fp-costo]')) || null,
      precioVenta: num(leer('[data-fp-venta]')) || null,
      precioMayor: num(leer('[data-fp-mayor]')) || null,
      mayorDesdeUnidades: num(leer('[data-fp-desde]')) || null,
      activo: $panel.querySelector('[data-fp-activo]').checked
    });

    if (res.ok && cuerpoUbic) {
      // El producto ya quedo; ahora sus ubicaciones. Si fallan se dice que quedo y que no (sin exito enganoso) y la
      // edicion se conserva para reintentar.
      const ru = await PresupuestosApiClient.guardarUbicaciones(prod.id, cuerpoUbic);
      if (!ru.ok) {
        $btn.disabled = false;
        $btn.innerHTML = textoBtn;
        const ea = erroresDeLaApi(ru.detalles, ubic.filas.length);
        ubic.errores = ea.filas; pintarUbic();
        $msg.style.color = '#d29922';
        $msg.textContent = ru.error?.code === 'VERSION_CAMBIO' || ru.status === 409
          ? 'El producto se guardó, pero alguien cambió sus ubicaciones mientras editabas: no se guardaron las tuyas. Cierra y vuelve a abrir para ver las de ahora.'
          : 'El producto se guardó, pero sus ubicaciones no: ' + (ea.bloque || ru.error?.detalle || mensajeFalla(ru.error?.code, 'intenta de nuevo')) + '. Tus cambios siguen aquí.';
        return;
      }
      const l = leerUbicaciones(ru.data);
      ubic.revision = l.revision; ubic.original = l.filas; ubic.filas = l.filas.map((f) => ({ ...f }));
    }

    $btn.disabled = false;
    $btn.innerHTML = textoBtn;

    if (res.ok) {
      cerrarYa();
      alGuardar();
      return;
    }
    $msg.style.color = '#d29922';
    $msg.innerHTML = res.error?.code === 'VALIDACION'
      ? esc(res.error?.detalle || 'Revisa los datos del producto.')
      : mensajeFalla(res.error?.code, 'Guardar productos todavia no esta conectado.');
  });
}

/**
 * MADERINCO-CARGA-CATALOGO-UI-T1: "Cargar catalogo aprobado". AUDIENCIA: Josse (admin) o el gerente, en escritorio, una vez.
 * Elige el .json, lo manda SIN tocar sus bytes (cargarPlantilla) y muestra lo que la API cargo. Repetir = 0 nuevos.
 */
function montarCargaCatalogo($el) {
  $el.innerHTML = `
    <div data-presu-carga style="background:#161b22;border:1px solid #30363d;border-radius:10px;padding:12px 14px;margin-bottom:16px;max-width:720px">
      <div style="font-size:0.875rem;color:#c9d1d9;font-weight:600">Cargar cat&aacute;logo aprobado</div>
      <div style="font-size:0.875rem;color:#8b949e;margin:4px 0 10px">Solo administrador o gerente, en el negocio demo. El archivo se manda tal cual: si no es el aprobado, no se carga nada.</div>
      <div class="presu-fila" style="gap:8px;align-items:center">
        <label for="presu-carga-archivo" style="font-size:0.875rem;color:#8b949e">Archivo .json</label>
        <input type="file" id="presu-carga-archivo" accept=".json,application/json" data-presu-carga-archivo>
        <button type="button" class="btn-primary btn-sm" data-presu-carga-enviar disabled>Cargar</button>
      </div>
      <div data-presu-carga-resultado role="status" style="font-size:0.875rem;margin-top:8px"></div>
    </div>`;
  const $archivo = $el.querySelector('[data-presu-carga-archivo]');
  const $btn = $el.querySelector('[data-presu-carga-enviar]');
  const $res = $el.querySelector('[data-presu-carga-resultado]');
  const decir = (texto, color) => { $res.style.color = color; $res.textContent = texto; };
  $archivo.addEventListener('change', () => {
    const file = $archivo.files && $archivo.files[0];
    const motivo = file ? revisarArchivo(file) : null;
    $btn.disabled = !file || !!motivo;
    if (motivo) decir(motivo, '#f85149'); else $res.textContent = '';
  });
  $btn.addEventListener('click', async () => {
    const file = $archivo.files && $archivo.files[0];
    const motivo = revisarArchivo(file);
    if (motivo) { decir(motivo, '#f85149'); return; }
    $btn.disabled = true;
    const antes = $btn.textContent;
    $btn.textContent = 'Cargando...';
    decir('', '#8b949e');
    const res = await PresupuestosApiClient.cargarPlantilla(file);
    $btn.textContent = antes;
    $btn.disabled = false;
    if (!res.ok) { decir(fraseDelError(res.error), '#f85149'); return; }
    const r = resumenDeCarga(res.data);
    $res.innerHTML = `<span style="color:#3fb950">${esc(fraseDeCarga(r))}</span>
      <span style="color:#6e7681" data-presu-carga-sha> &middot; huella ${esc(r.sha16)}</span>`;
    // La plantilla cambio: el armador la vuelve a pedir la proxima vez.
    plantillaDemo = null; plantillaEnVuelo = null;
  });
}

/**
 * MADERINCO-LISTA-ITEMS-SECCIONES-T1 (Ares #36434): "Cargar capitulos aprobados". Mismo camino que el catalogo: Josse
 * (admin) o el gerente elige Docs/presupuestos/capitulos_del_cliente.json, se manda tal cual y se dice lo que la API
 * cargo. Despues el modo por capitulos vuelve a pedir la lista (sale de la API en vez del archivo del panel).
 */
function montarCargaCapitulos($el) {
  $el.innerHTML = `
    <div data-presu-carga-cap style="background:#161b22;border:1px solid #30363d;border-radius:10px;padding:12px 14px;margin-bottom:16px;max-width:720px">
      <div style="font-size:0.875rem;color:#c9d1d9;font-weight:600">Cargar secciones aprobadas</div>
      <div style="font-size:0.875rem;color:#8b949e;margin:4px 0 10px">La lista del cliente (10 secciones, 68 l&iacute;neas). Solo administrador o gerente, en el negocio demo. Va despu&eacute;s del cat&aacute;logo. El archivo se manda tal cual: si no es el aprobado, no se carga nada.</div>
      <div class="presu-fila" style="gap:8px;align-items:center">
        <label for="presu-carga-cap-archivo" style="font-size:0.875rem;color:#8b949e">Archivo .json</label>
        <input type="file" id="presu-carga-cap-archivo" accept=".json,application/json" data-presu-carga-cap-archivo>
        <button type="button" class="btn-primary btn-sm" data-presu-carga-cap-enviar disabled>Cargar</button>
      </div>
      <div data-presu-carga-cap-resultado role="status" style="font-size:0.875rem;margin-top:8px"></div>
    </div>`;
  const $archivo = $el.querySelector('[data-presu-carga-cap-archivo]');
  const $btn = $el.querySelector('[data-presu-carga-cap-enviar]');
  const $res = $el.querySelector('[data-presu-carga-cap-resultado]');
  const decir = (texto, color) => { $res.style.color = color; $res.textContent = texto; };
  $archivo.addEventListener('change', () => {
    const file = $archivo.files && $archivo.files[0];
    const motivo = file ? revisarArchivoCapitulos(file) : null;
    $btn.disabled = !file || !!motivo;
    if (motivo) decir(motivo, '#f85149'); else $res.textContent = '';
  });
  $btn.addEventListener('click', async () => {
    const file = $archivo.files && $archivo.files[0];
    const motivo = revisarArchivoCapitulos(file);
    if (motivo) { decir(motivo, '#f85149'); return; }
    $btn.disabled = true;
    const antes = $btn.textContent;
    $btn.textContent = 'Cargando...';
    decir('', '#8b949e');
    const res = await PresupuestosApiClient.cargarCapitulos(file);
    $btn.textContent = antes;
    $btn.disabled = false;
    if (!res.ok) { decir(fraseDelErrorCapitulos(res.error), '#f85149'); return; }
    const r = resumenDeCargaCapitulos(res.data);
    $res.innerHTML = `<span style="color:#3fb950">${esc(fraseDeCargaCapitulos(r))}</span>
      <span style="color:#6e7681" data-presu-carga-cap-sha> &middot; huella ${esc(r.sha16)}</span>`;
    // La lista cambio: la proxima vez se vuelven a pedir plantilla y capitulos.
    plantillaDemo = null; plantillaEnVuelo = null;
  });
}

/**
 * MADERINCO-PRODUCTO-SECCION-CONTRATO-T1 (Ares #30563): "Cargar piloto Cubierta". AUDIENCIA: Josse (admin) en el demo,
 * una vez. Crea productos en el catalogo: por eso confirma EN LA PAGINA (sin dialogo nativo) y dice lo que la API hizo.
 * Repetir no duplica (la API cuenta los ya casados aparte).
 */
function montarCargaPiloto($el) {
  $el.innerHTML = `
    <div data-presu-piloto style="background:#161b22;border:1px solid #30363d;border-radius:10px;padding:12px 14px;margin-bottom:16px;max-width:720px">
      <div style="font-size:14px;color:#c9d1d9;font-weight:600">Cargar piloto Cubierta</div>
      <div style="font-size:14px;color:#8b949e;margin:4px 0 10px">Crea en tu cat&aacute;logo los productos de las partidas de Cubierta y les pone su secci&oacute;n. Repetirlo no duplica nada.</div>
      <div data-presu-piloto-acciones class="presu-fila" style="gap:8px;align-items:center">
        <button type="button" class="btn-secondary btn-sm" data-presu-piloto-pedir>Cargar piloto</button>
      </div>
      <div data-presu-piloto-resultado role="status" style="font-size:14px;margin-top:8px"></div>
    </div>`;
  const $acc = $el.querySelector('[data-presu-piloto-acciones]');
  const $res = $el.querySelector('[data-presu-piloto-resultado]');
  const pedir = () => {
    $acc.innerHTML = `<span style="font-size:14px;color:#d29922">Esto crea productos en el cat&aacute;logo del demo. &iquest;Seguimos?</span>
      <button type="button" class="btn-primary btn-sm" data-presu-piloto-si>S&iacute;, cargar</button>
      <button type="button" class="btn-ghost btn-sm" data-presu-piloto-no>Cancelar</button>`;
    $acc.querySelector('[data-presu-piloto-si]').addEventListener('click', cargar);
    $acc.querySelector('[data-presu-piloto-no]').addEventListener('click', inicio);
    $acc.querySelector('[data-presu-piloto-si]').focus();
  };
  const inicio = () => {
    $acc.innerHTML = '<button type="button" class="btn-secondary btn-sm" data-presu-piloto-pedir>Cargar piloto</button>';
    $acc.querySelector('[data-presu-piloto-pedir]').addEventListener('click', pedir);
  };
  const cargar = async () => {
    $acc.innerHTML = '<span style="font-size:14px;color:#8b949e" data-presu-piloto-cargando>Cargando el piloto...</span>';
    $res.textContent = '';
    const res = await PresupuestosApiClient.casarPiloto(PILOTO_CUBIERTA);
    inicio();
    if (!res.ok) { $res.style.color = '#f85149'; $res.textContent = fraseDelFalloPiloto(res.error); return; }
    const r = resumenDelPiloto(res.data);
    $res.style.color = r.errores.length ? '#d29922' : '#3fb950';
    $res.innerHTML = frasesDelPiloto(r).map((l) => `<div>${esc(l)}</div>`).join('')
      + (r.errores.length ? `<details style="margin-top:4px;color:#8b949e"><summary>Ver cu&aacute;les</summary>${r.errores.map((e) => `<div>${esc(e)}</div>`).join('')}</details>` : '');
    // El catalogo cambio: la lista y la plantilla se vuelven a pedir la proxima vez.
    plantillaDemo = null; plantillaEnVuelo = null;
  };
  inicio();
}

/**
 * PRESU-IMPORTADOR-PANEL-T1 (D-143; contrato nexo-api 584). AUDIENCIA: el dueno del demo (admin), en Mis items.
 * "Subir archivo": el Excel (.xlsx) de un presupuesto -> la API devuelve una VISTA PREVIA sin escribir nada -> el dueno
 * revisa fila por fila (puede quitarla o cambiar nombre, unidad, precio, seccion y paso) -> "Guardar los N marcados".
 * La seccion es una LISTA de las que ya existen (o ninguna): nunca se escribe una nueva. Precio vacio = Por definir.
 * Si la API rechaza (una fila invalida = no se guarda nada) se marca la fila con su motivo y no se pierde lo editado.
 */
function montarImportador($el, alTerminar) {
  let vista = null;
  let estados = [];
  let errores = new Map();
  const etiquetaPasoImp = { tipo: '1. Tipo', revision_obligatoria: '2. Revisi&oacute;n obligatoria', complemento: '3. Complemento' };
  $el.innerHTML = `
    <div data-presu-importar style="background:#161b22;border:1px solid #30363d;border-radius:10px;padding:12px 14px;margin-bottom:16px;font-size:14px">
      <div style="color:#c9d1d9;font-weight:600">Subir un Excel de presupuesto</div>
      <div style="color:#8b949e;margin:4px 0 10px">Te mostramos lo que entendimos antes de guardar nada. Solo se guardan las filas que dejes marcadas.</div>
      <div class="presu-fila" style="gap:8px;align-items:center;flex-wrap:wrap">
        <label for="presu-importar-archivo" style="color:#c9d1d9">Archivo (.xlsx)</label>
        <input id="presu-importar-archivo" type="file" accept=".xlsx" data-presu-importar-archivo style="font-size:14px;max-width:100%">
        <button type="button" class="btn-secondary btn-sm" data-presu-importar-leer style="font-size:14px" disabled>Ver vista previa</button>
      </div>
      <div data-presu-importar-estado role="status" style="margin-top:8px"></div>
      <div data-presu-importar-tabla></div>
    </div>`;
  const $arch = $el.querySelector('[data-presu-importar-archivo]');
  const $leer = $el.querySelector('[data-presu-importar-leer]');
  const $estado = $el.querySelector('[data-presu-importar-estado]');
  const $tabla = $el.querySelector('[data-presu-importar-tabla]');
  const decir = (html, color = '#8b949e') => { $estado.style.color = color; $estado.innerHTML = html; };
  $arch.addEventListener('change', () => { $leer.disabled = !$arch.files?.length; });

  const marcadas = () => estados.filter((e) => e.incluir).length;
  const pintarTabla = () => {
    if (!vista) { $tabla.innerHTML = ''; return; }
    const opSec = (sel) => `<option value="">Sin secci&oacute;n</option>${vista.secciones.map((s) => `<option value="${esc(s.id)}"${s.id === sel ? ' selected' : ''}>${esc(s.nombre)}</option>`).join('')}`;
    const opPaso = (sel) => PASOS_IMPORTADOR.map((p) => `<option value="${p}"${p === sel ? ' selected' : ''}>${etiquetaPasoImp[p]}</option>`).join('');
    $tabla.innerHTML = `
      <div data-presu-importar-resumen style="margin-top:10px;color:#c9d1d9">${vista.totalFilas} filas le&iacute;das de <b>${esc(vista.archivo)}</b>${vista.hoja ? ` (hoja ${esc(vista.hoja)})` : ''}: ${vista.conSeccion} con secci&oacute;n, ${vista.sinSeccion} sin secci&oacute;n, ${vista.duplicados} ya en Mis items.</div>
      ${vista.problemas.length ? `<div data-presu-importar-problemas role="note" style="margin-top:6px;color:#d29922">${vista.problemas.map(esc).join(' ')}</div>` : ''}
      <div data-presu-importar-filas style="margin-top:8px;border-top:1px solid #21262d">
        ${vista.filas.map((f, i) => { const e = estados[i]; const err = errores.get(i); return `
          <div data-presu-importar-fila="${i}" style="padding:8px 0;border-bottom:1px solid #21262d;${e.incluir ? '' : 'opacity:.6;'}${err ? 'border-left:3px solid #f85149;padding-left:8px;' : ''}">
            <label style="display:flex;gap:8px;align-items:center;color:#c9d1d9"><input type="checkbox" data-imp-campo="incluir" data-imp-i="${i}" ${e.incluir ? 'checked' : ''}> Fila ${f.fila}${f.seccionArchivo ? ` &middot; <span style="color:#8b949e">${esc(f.seccionArchivo)}</span>` : ''}</label>
            <div style="display:flex;flex-wrap:wrap;gap:6px 10px;margin-top:6px">
              <label style="flex:1 1 240px;display:flex;flex-direction:column;gap:2px;color:#8b949e">Nombre<input class="form-input" data-imp-campo="descripcion" data-imp-i="${i}" value="${esc(e.descripcion)}" style="font-size:14px"></label>
              <label style="flex:0 1 90px;display:flex;flex-direction:column;gap:2px;color:#8b949e">Unidad<input class="form-input" data-imp-campo="unidad" data-imp-i="${i}" value="${esc(e.unidad)}" style="font-size:14px;min-width:0"></label>
              <label style="flex:0 1 130px;display:flex;flex-direction:column;gap:2px;color:#8b949e">Precio<input class="form-input" inputmode="decimal" data-imp-campo="precio" data-imp-i="${i}" value="${e.precio === null ? '' : esc(String(e.precio))}" placeholder="Por definir" style="font-size:14px;min-width:0"></label>
              <label style="flex:1 1 180px;display:flex;flex-direction:column;gap:2px;color:#8b949e">Secci&oacute;n<select class="form-input" data-imp-campo="seccionId" data-imp-i="${i}" style="font-size:14px;min-width:0">${opSec(e.seccionId)}</select></label>
              <label style="flex:0 1 170px;display:flex;flex-direction:column;gap:2px;color:#8b949e">Paso<select class="form-input" data-imp-campo="paso" data-imp-i="${i}" style="font-size:14px;min-width:0">${opPaso(e.paso)}</select></label>
            </div>
            ${f.productoExistenteId ? `<label style="display:flex;gap:8px;align-items:center;margin-top:6px;color:#c9d1d9"><input type="checkbox" data-imp-campo="usarExistente" data-imp-i="${i}" ${e.usarExistente ? 'checked' : ''}> Usar el que ya est&aacute; en Mis items: ${esc(f.productoExistente || 'producto existente')}</label>` : ''}
            ${f.avisos.length ? `<div style="margin-top:4px;color:#d29922">${f.avisos.map((a) => esc(textoAviso(a))).join(' &middot; ')}</div>` : ''}
            ${err ? `<div data-presu-importar-error role="alert" style="margin-top:4px;color:#f85149">${esc(err)}</div>` : ''}
          </div>`; }).join('')}
      </div>
      <div class="presu-fila" style="gap:8px;align-items:center;margin-top:10px;flex-wrap:wrap">
        <button type="button" class="btn-primary btn-sm" data-presu-importar-guardar ${marcadas() ? '' : 'disabled'} style="font-size:14px">Guardar los ${marcadas()} marcados</button>
        <button type="button" class="btn-ghost btn-sm" data-presu-importar-descartar style="font-size:14px">Descartar</button>
      </div>`;
    $tabla.querySelectorAll('[data-imp-campo]').forEach((c) => c.addEventListener(c.type === 'checkbox' || c.tagName === 'SELECT' ? 'change' : 'input', () => {
      const i = Number(c.dataset.impI);
      const campo = c.dataset.impCampo;
      if (c.type === 'checkbox') estados[i][campo] = c.checked;
      else if (campo === 'precio') { const n = parsearNumeroColombiano(c.value); estados[i].precio = c.value.trim() === '' ? null : (n === null || isNaN(n) ? NaN : n); }
      else estados[i][campo] = c.value;
      errores.delete(i);
      if (c.type === 'checkbox') pintarTabla();
      else { const $g = $tabla.querySelector('[data-presu-importar-guardar]'); $g.textContent = `Guardar los ${marcadas()} marcados`; }
    }));
    $tabla.querySelector('[data-presu-importar-guardar]').addEventListener('click', guardar);
    $tabla.querySelector('[data-presu-importar-descartar]').addEventListener('click', () => { vista = null; estados = []; errores = new Map(); $arch.value = ''; $leer.disabled = true; decir(''); pintarTabla(); });
  };
  $leer.addEventListener('click', async () => {
    const file = $arch.files?.[0];
    if (!file) return;
    $leer.disabled = true;
    decir('Leyendo el archivo...');
    const res = await PresupuestosApiClient.importarVistaPrevia(file);
    $leer.disabled = false;
    if (!res.ok) { vista = null; pintarTabla(); decir(esc(res.error?.detalle || fraseDelFalloPiloto(res.error)), '#f85149'); return; }
    vista = leerVistaPrevia(res.data);
    estados = vista.filas.map(estadoInicial);
    errores = new Map();
    decir(vista.filas.length ? 'Revisa las filas: nada se ha guardado todav&iacute;a.' : 'El archivo no trae filas que podamos usar.', vista.filas.length ? '#8b949e' : '#d29922');
    pintarTabla();
  });
  const guardar = async () => {
    const validas = new Set(vista.secciones.map((s) => s.id));
    const locales = new Map();
    estados.forEach((e, i) => { const p = problemaDeFila(e, validas); if (p) locales.set(i, p); });
    if (locales.size) { errores = locales; pintarTabla(); decir(`${locales.size} fila${locales.size === 1 ? '' : 's'} con problemas: corr&iacute;gelas o desm&aacute;rcalas.`, '#f85149'); return; }
    const { filas, origen } = cuerpoConfirmar(estados);
    if (filas.length > MAX_FILAS) { decir(`Puedes guardar hasta ${MAX_FILAS} filas a la vez.`, '#f85149'); return; }
    const $g = $tabla.querySelector('[data-presu-importar-guardar]');
    $g.disabled = true; $g.textContent = 'Guardando...';
    const res = await PresupuestosApiClient.importarConfirmar(filas);
    if (!res.ok) {
      errores = erroresPorFila(res, origen);
      pintarTabla();
      decir(errores.size ? 'No se guard&oacute; nada: hay filas con problemas (marcadas en rojo).' : esc(fraseDelFalloPiloto(res.error)), '#f85149');
      return;
    }
    const r = leerConfirmado(res.data);
    vista = null; estados = []; errores = new Map(); $arch.value = ''; $leer.disabled = true;
    pintarTabla();
    const pl = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;
    decir(`Listo: ${pl(r.filas, 'fila guardada', 'filas guardadas')} (${pl(r.creados, 'producto nuevo', 'productos nuevos')}, ${pl(r.reusados, 'que ya exist&iacute;a', 'que ya exist&iacute;an')}) y ${pl(r.ubicaciones, 'ubicaci&oacute;n', 'ubicaciones')} en tus secciones.`, '#3fb950');
    plantillaDemo = null; plantillaEnVuelo = null; await cargarPlantilla();
    alTerminar();
  };
}

/**
 * PRESU-UNION-AUTOMATICA-T1 (D-143, Ares #31552; antes MADERINCO-CATALOGO-UNICO-T1, nexo-api 577). AUDIENCIA: el dueno
 * del demo (admin), en Mis items. Ya NO hay boton "Traer los items": al abrir Mis items la union plantilla-Mis items
 * corre sola, UNA vez por negocio en esta sesion del navegador (577 es idempotente: repetir no crea nada, pero no se
 * gasta el viaje en cada visita). Sin parecidos ni errores no se pinta nada; con productos nuevos, una linea de estado.
 * Lo que se parece a un producto que creo una persona sigue "por confirmar": lista marcada y "Confirmar los N
 * marcados"; desmarcar uno lo deja como esta. Nada se casa por nombre aqui.
 */
const unionHechaEn = new Set();
function montarCasarPlantilla($el, alTerminar) {
  let porConfirmar = [];
  let marcados = new Set();
  let nombres = new Map();
  const negocio = getSelectedBusiness() || '';
  const pintarBloque = (estado) => {
    $el.innerHTML = `
    <div data-presu-casar style="background:#161b22;border:1px solid #30363d;border-radius:10px;padding:12px 14px;margin-bottom:16px">
      <div style="font-size:14px;color:#c9d1d9;font-weight:600">Tu plantilla y Mis items</div>
      <div data-presu-casar-resultado role="status" style="font-size:14px;margin-top:6px">${estado}</div>
      <div data-presu-casar-confirmar></div>
    </div>`;
    return $el.querySelector('[data-presu-casar-confirmar]');
  };
  let $conf = null;
  const pintarConfirmar = (aviso = '') => {
    if (!$conf) return;
    if (!porConfirmar.length) { $conf.innerHTML = aviso ? `<div role="status" style="font-size:14px;color:#3fb950;margin-top:8px">${esc(aviso)}</div>` : ''; return; }
    $conf.innerHTML = `
      <div style="margin-top:12px;font-size:14px">
        <div style="color:#f0f6fc;font-weight:600">Por confirmar (${porConfirmar.length})</div>
        <div style="color:#8b949e;margin:2px 0 6px">Cada item de la plantilla con el producto tuyo que tiene ese nombre. Desmarca los que no sean el mismo.</div>
        <div data-presu-casar-lista style="max-height:360px;overflow-y:auto;border-top:1px solid #21262d">
          ${porConfirmar.map((p) => `
            <label data-presu-casar-fila="${esc(p.itemId)}" style="display:flex;gap:8px;align-items:flex-start;padding:6px 0;border-bottom:1px solid #21262d;cursor:pointer">
              <input type="checkbox" data-presu-casar-marca="${esc(p.itemId)}" ${marcados.has(p.itemId) ? 'checked' : ''} style="margin-top:3px">
              <span style="flex:1;min-width:0;color:#e6edf3">${esc(p.descripcion)}<span style="display:block;color:#8b949e">&rarr; ${esc(nombres.get(p.productoSugeridoId) || 'producto de Mis items')}</span></span>
            </label>`).join('')}
        </div>
        <div class="presu-fila" style="gap:8px;align-items:center;margin-top:8px">
          <button type="button" class="btn-primary btn-sm" data-presu-casar-confirmar-ir ${marcados.size ? '' : 'disabled'} style="font-size:14px">Confirmar los ${marcados.size} marcados</button>
        </div>
        <div data-presu-casar-aviso role="status" style="color:#d29922;margin-top:6px">${esc(aviso)}</div>
      </div>`;
    $conf.querySelectorAll('[data-presu-casar-marca]').forEach((c) => c.addEventListener('change', () => {
      if (c.checked) marcados.add(c.dataset.presuCasarMarca); else marcados.delete(c.dataset.presuCasarMarca);
      const $ir = $conf.querySelector('[data-presu-casar-confirmar-ir]');
      $ir.textContent = `Confirmar los ${marcados.size} marcados`;
      $ir.disabled = marcados.size === 0;
    }));
    $conf.querySelector('[data-presu-casar-confirmar-ir]').addEventListener('click', confirmar);
  };
  // La plantilla se relee ANTES de repintar: el panel sabe que esta en el demo porque la tiene (esDemoMaderinco); si
  // solo se vaciara, Mis items volveria a las tarjetas de los otros negocios.
  const releerPlantilla = async () => { plantillaDemo = null; plantillaEnVuelo = null; await cargarPlantilla(); };
  const unir = async () => {
    $el.innerHTML = '';
    const res = await PresupuestosApiClient.casarPlantilla();
    if (!$el.isConnected) return;
    if (!res.ok) { $conf = pintarBloque(`<span style="color:#f85149">${esc(fraseDelFalloPiloto(res.error))}</span>`); return; }
    unionHechaEn.add(negocio);
    const r = leerCasarPlantilla(res.data);
    porConfirmar = r.porConfirmar;
    marcados = new Set(porConfirmar.map((p) => p.itemId));
    const hayAlgo = porConfirmar.length || r.errores.length || r.productosCreados > 0;
    if (!hayAlgo) return; // nada nuevo que contar: no se pinta bloque
    if (porConfirmar.length) {
      const lp = await PresupuestosApiClient.listarProductos({});
      nombres = new Map((lp.ok ? lp.data : []).map((p) => [String(p.id ?? p.Id ?? ''), p.nombre ?? p.Nombre ?? '']));
    }
    const lineas = [];
    if (r.productosCreados > 0) lineas.push(`${r.productosCreados} items de tu plantilla ya est&aacute;n en Mis items.`);
    if (porConfirmar.length) lineas.push(`${porConfirmar.length} se parecen a un producto que t&uacute; creaste: confirma abajo si son el mismo.`);
    if (r.errores.length) lineas.push(`${r.errores.length} no se pudieron unir.`);
    $conf = pintarBloque(`<span style="color:${r.errores.length ? '#d29922' : '#3fb950'}">${lineas.join(' ')}</span>`
      + (r.errores.length ? `<details style="margin-top:4px;color:#8b949e"><summary>Ver cu&aacute;les</summary>${r.errores.map((e) => `<div>${esc(e)}</div>`).join('')}</details>` : ''));
    pintarConfirmar();
    if (r.productosCreados > 0) { await releerPlantilla(); alTerminar(); }
  };
  const confirmar = async () => {
    const tandas = tandasParaConfirmar(porConfirmar, marcados);
    const $ir = $conf.querySelector('[data-presu-casar-confirmar-ir]');
    $ir.disabled = true; $ir.textContent = 'Confirmando...';
    const hechos = [];
    for (const t of tandas) {
      const r = await PresupuestosApiClient.confirmarCasados(t);
      if (!r.ok) { pintarConfirmar(fraseDelFalloPiloto(r.error)); return; }
      hechos.push(leerConfirmar(r.data));
    }
    const s = sumarConfirmados(hechos);
    const confirmadosIds = new Set(tandas.flat().map((p) => p.itemId));
    porConfirmar = porConfirmar.filter((p) => !confirmadosIds.has(p.itemId));
    marcados = new Set([...marcados].filter((id) => !confirmadosIds.has(id)));
    pintarConfirmar(`${s.confirmados} unidos a su producto${s.yaEstaban ? `, ${s.yaEstaban} ya lo estaban` : ''}${s.errores.length ? `; ${s.errores.length} no se pudieron` : ''}.`);
    await releerPlantilla();
    alTerminar();
  };
  if (!unionHechaEn.has(negocio)) unir();
}

/**
 * MADERINCO-MIS-ITEMS-REDISENO-T1 parte 1 (diseno de Atenea #31063, alternativa A). Solo demo: una fila por producto
 * con nombre completo (sin cortar), unidad, precio ("Por definir" si no viene, distinto de $0), estado y Editar. La
 * fila se dobla sola a 375 (flex-wrap): nada se esconde para que quepa. Seccion y paso esperan el contrato de A.
 */
function listaMisItemsHTML(filas) {
  return `<div data-presu-misitems-lista role="list" style="border-top:1px solid #21262d">
    ${filas.map((f) => `
      <div role="listitem" data-presu-misitems-fila="${esc(f.id)}" style="display:flex;flex-wrap:wrap;gap:6px 14px;align-items:center;padding:10px 4px;border-bottom:1px solid #21262d;font-size:14px">
        <div style="flex:1 1 260px;min-width:0">
          <div style="color:#e6edf3;font-weight:600;overflow-wrap:anywhere">${esc(f.nombre)}</div>
          ${f.descripcion && f.descripcion !== f.nombre ? `<div style="color:#8b949e;overflow-wrap:anywhere">${esc(f.descripcion)}</div>` : ''}
          ${f.ubicaciones === null ? '' : `<div data-presu-misitems-ubicaciones style="color:#8b949e">${f.ubicaciones.length ? f.ubicaciones.map((u) => `${esc(u.seccion || 'Sección')} &middot; ${esc(etiquetaPaso(u.paso))}`).join(' / ') : 'Sin secci&oacute;n todav&iacute;a'}</div>`}
        </div>
        <div style="flex:0 0 auto;color:#c9d1d9"><span style="color:#8b949e">Unidad </span>${f.unidad ? esc(f.unidad) : 'sin unidad'}</div>
        <div style="flex:0 0 auto;color:${f.precio === null ? '#d29922' : '#3fb950'};font-weight:600" data-presu-misitems-precio>${f.precio === null ? 'Por definir' : fmtCOP(f.precio)}${(f.precio !== null && f.precioMayor > 0 && f.mayorDesde > 0) ? `<span style="color:#8b949e;font-weight:400"> &middot; por mayor ${fmtCOP(f.precioMayor)} desde ${fmtNum(f.mayorDesde)}</span>` : ''}</div>
        <div style="flex:0 0 auto;color:${f.activo ? '#c9d1d9' : '#8b949e'}" data-presu-misitems-estado>${esc(estadoDeFila(f))}</div>
        <button type="button" class="btn-ghost btn-sm" data-presu-editar="${esc(f.id)}" aria-label="Editar ${esc(f.nombre)}" style="font-size:14px">Editar</button>
      </div>`).join('')}
  </div>`;
}

async function renderCatalogo($host, negocio) {
  $host.innerHTML = `
    <section class="artifact-view presu-scope presu-ancho">
      <div class="artifact-header"><div>
        ${regresoHtml({ volverA: "#/presupuestos", texto: "Volver al asistente", atributos: "data-presu-volver" })}
        <h1>${palabras(esDemoMaderinco()).tus}</h1>
      </div></div>
      ${/* CATALOGO-UNICO-ITEMS-T1 (maqueta aprobada, Ares #43601): en el demo el catalogo ES el item estandar; la lista de
           productos de antes queda plegada debajo con sus herramientas. */ esDemoMaderinco()
        ? `<div data-presu-catalogo-estandar></div>
           <details data-presu-productos-anteriores class="ce-linea" style="margin-top:24px"><summary><b>Productos sueltos del cat&aacute;logo anterior</b><span>buscar, agregar, ver por secci&oacute;n</span></summary><div style="padding:12px">`
        : ''}
      <div class="presu-fila" style="gap:8px;margin-bottom:16px">
        <input type="search" data-presu-buscar placeholder="Buscar en tu cat&aacute;logo..." class="form-input" style="width:320px">
        <button type="button" class="btn-primary btn-sm" data-presu-nuevo-producto>+ Agregar producto</button>
        ${esDemoMaderinco() ? '<button type="button" class="btn-ghost btn-sm" data-presu-ver-por-seccion aria-expanded="false" style="font-size:14px">Ver por secci&oacute;n</button>' : ''}
        ${esDemoMaderinco() && puedeCargarCatalogo(AppConfig.role) ? '<button type="button" class="btn-ghost btn-sm" data-presu-subir-archivo aria-expanded="false" style="font-size:14px">Subir archivo</button>' : ''}
        ${esDemoMaderinco() ? '<label style="font-size:14px;color:#c9d1d9;display:inline-flex;gap:6px;align-items:center">Estado <select data-presu-misitems-estado-filtro class="form-input" style="font-size:14px;width:auto;min-width:0"><option value="todos">Todos</option><option value="activos">Disponibles</option><option value="inactivos">Inactivos</option></select></label>' : ''}
      </div>
      ${esDemoMaderinco() ? '<section data-presu-por-seccion hidden style="margin-bottom:16px;font-size:14px"></section>' : ''}
      ${esDemoMaderinco() && puedeCargarCatalogo(AppConfig.role) ? '<div data-presu-casar-host></div>' : ''}
      ${esDemoMaderinco() && puedeCargarCatalogo(AppConfig.role) ? '<div data-presu-importar-host hidden></div>' : ''}
      <div data-presu-catalogo><div class="presu-skel"><div></div><div></div><div></div><div></div></div></div>
      ${esDemoMaderinco() ? '</div></details>' : ''}
      ${/* MADERINCO-SKILL-RESTO-T1: la carga del catalogo aprobado es de administracion: plegada DEBAJO de la lista (antes
           ocupaba la primera pantalla a 375 y ponia una segunda accion primaria junto a Agregar producto). */ ''}
      ${puedeCargarCatalogo(AppConfig.role) ? `<details data-presu-admin-catalogo style="margin-top:20px;border-top:1px solid #21262d;padding-top:12px">
        <summary style="cursor:pointer;font-size:0.875rem;color:#8b949e">Herramientas de administraci&oacute;n</summary>
        <div data-presu-carga-catalogo style="margin-top:10px"></div>
        ${esDemoMaderinco() ? '<div data-presu-carga-capitulos></div>' : ''}
        ${esDemoMaderinco() ? '<div data-presu-carga-piloto></div>' : ''}</details>` : ''}
    </section>`;

  const $estandar = $host.querySelector('[data-presu-catalogo-estandar]');
  if ($estandar) montarCatalogoEstandar($estandar);
  const $carga = $host.querySelector('[data-presu-carga-catalogo]');
  if ($carga) montarCargaCatalogo($carga);
  const $capitulos = $host.querySelector('[data-presu-carga-capitulos]');
  if ($capitulos) montarCargaCapitulos($capitulos);
  const $piloto = $host.querySelector('[data-presu-carga-piloto]');
  if ($piloto) montarCargaPiloto($piloto);

  $host.querySelector('[data-presu-volver]').addEventListener('click', (e) => {
    e.preventDefault();
    renderRecepcion($host, negocio);
  });

  const $lista = $host.querySelector('[data-presu-catalogo]');

  // Vista por seccion (Ares #30819; diseno de Atenea MADERINCO-PRODUCTO-SECCION-PASO-T1): elegir una seccion de la
  // plantilla y ver sus productos por paso; subir/bajar dentro del paso (escribe el orden con la revision de cada
  // producto) y Editar abre el producto con su bloque Donde aparece.
  const $porSec = $host.querySelector('[data-presu-por-seccion]');
  if ($porSec) {
    let seccionSel = '';
    let vista = null;
    const pintarPorSeccion = async (aviso = '') => {
      if (plantillaDemo === null) await cargarPlantilla();
      const secciones = (plantillaDemo?.secciones || []).filter((s) => s.id);
      $porSec.innerHTML = `
        <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:8px">
          <label for="presu-por-seccion-sel" style="color:#c9d1d9;font-weight:600">Secci&oacute;n</label>
          <select id="presu-por-seccion-sel" class="form-input" data-presu-por-seccion-sel style="max-width:320px;font-size:14px">
            <option value="">Elige una secci&oacute;n&hellip;</option>
            ${secciones.map((s) => `<option value="${esc(s.id)}"${s.id === seccionSel ? ' selected' : ''}>${esc(s.nombre)}</option>`).join('')}
          </select>
        </div>
        <div data-presu-por-seccion-aviso role="status" style="color:#d29922;min-height:1em">${esc(aviso)}</div>
        <div data-presu-por-seccion-lista>${!seccionSel ? '<div style="color:#8b949e">Elige una secci&oacute;n para ver qu&eacute; productos aparecen ah&iacute; y en qu&eacute; paso.</div>'
          : !vista ? '<div style="color:#8b949e">Leyendo&hellip;</div>'
          : vista.total === 0 ? '<div style="color:#8b949e">Ning&uacute;n producto aparece todav&iacute;a en esta secci&oacute;n. Se asignan desde Editar producto &rarr; D&oacute;nde aparece.</div>'
          : vista.grupos.filter((g) => g.productos.length).map((g, gi) => `
            <div data-presu-por-seccion-paso="${esc(g.paso)}" style="margin-top:10px">
              <div style="color:#f0f6fc;font-weight:600">${esc(g.etiqueta)} (${g.productos.length})</div>
              ${g.productos.map((p, i) => `
                <div data-presu-por-seccion-prod="${esc(p.productoId)}" style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;padding:6px 0;border-top:1px solid #21262d">
                  <span style="flex:1 1 200px;color:#e6edf3">${esc(p.nombre)}${p.activo ? '' : ' <span style="color:#8b949e">(inactivo)</span>'}</span>
                  <span style="color:#8b949e">${esc(p.unidad || '')} ${p.precioVenta ? fmtCOP(p.precioVenta) : '&mdash;'}</span>
                  <button type="button" class="btn-ghost btn-sm" data-presu-por-seccion-mover="${esc(g.paso)}|${i}|-1" ${i === 0 ? 'disabled' : ''} aria-label="Subir ${esc(p.nombre)}" style="font-size:14px">&uarr; Subir</button>
                  <button type="button" class="btn-ghost btn-sm" data-presu-por-seccion-mover="${esc(g.paso)}|${i}|1" ${i === g.productos.length - 1 ? 'disabled' : ''} aria-label="Bajar ${esc(p.nombre)}" style="font-size:14px">&darr; Bajar</button>
                  <button type="button" class="btn-ghost btn-sm" data-presu-por-seccion-editar="${esc(p.productoId)}" style="font-size:14px">Editar</button>
                </div>`).join('')}
            </div>`).join('')}</div>`;
      $porSec.querySelector('[data-presu-por-seccion-sel]').addEventListener('change', async (e) => {
        seccionSel = e.target.value; vista = null; await pintarPorSeccion();
        if (!seccionSel) return;
        const r = await PresupuestosApiClient.productosDeSeccion(seccionSel);
        vista = r.ok ? leerSeccionProductos(r.data) : null;
        await pintarPorSeccion(r.ok ? '' : mensajeFalla(r.error?.code, 'Todavía no se puede leer esta sección.'));
        $porSec.querySelector('[data-presu-por-seccion-sel]')?.focus();
      });
      $porSec.querySelectorAll('[data-presu-por-seccion-mover]').forEach((b) => b.addEventListener('click', async () => {
        const [paso, i, dir] = b.dataset.presuPorSeccionMover.split('|');
        const grupo = vista.grupos.find((g) => g.paso === paso);
        const cambios = moverEnPaso(grupo, Number(i), Number(dir));
        if (!cambios.length) return;
        $porSec.querySelectorAll('button').forEach((x) => { x.disabled = true; });
        // Un PUT por producto que cambia (con SU revision). Si uno falla se dice cual y se relee lo que quedo.
        let fallo = '';
        for (const c of cambios) {
          const lu = await PresupuestosApiClient.leerUbicaciones(c.productoId);
          const l = lu.ok ? leerUbicaciones(lu.data) : null;
          const ru = l ? await PresupuestosApiClient.guardarUbicaciones(c.productoId, { revision: l.revision, ubicaciones: conOrdenEnSeccion(l.filas, seccionSel, c.orden) }) : lu;
          if (!ru.ok) { fallo = `No se pudo cambiar el orden de un producto (${ru.error?.code || 'error'}); esto es lo que quedó.`; break; }
        }
        const r = await PresupuestosApiClient.productosDeSeccion(seccionSel);
        if (r.ok) vista = leerSeccionProductos(r.data);
        await pintarPorSeccion(fallo);
        $porSec.querySelector(`[data-presu-por-seccion-prod="${CSS.escape(grupo.productos[Number(i)].productoId)}"] button`)?.focus();
      }));
      $porSec.querySelectorAll('[data-presu-por-seccion-editar]').forEach((b) => b.addEventListener('click', async () => {
        const id = b.dataset.presuPorSeccionEditar;
        const res = await PresupuestosApiClient.listarProductos({});
        const p = res.ok ? (res.data || []).find((x) => String(x.id ?? x.Id ?? '') === id) : null;
        abrirFormProducto(p ? normalizarProducto(p) : { id }, async () => {
          const r = await PresupuestosApiClient.productosDeSeccion(seccionSel);
          if (r.ok) vista = leerSeccionProductos(r.data);
          await pintarPorSeccion();
        });
      }));
    };
    $host.querySelector('[data-presu-ver-por-seccion]')?.addEventListener('click', async (e) => {
      $porSec.hidden = !$porSec.hidden;
      e.currentTarget.setAttribute('aria-expanded', String(!$porSec.hidden));
      if (!$porSec.hidden) { await pintarPorSeccion(); $porSec.querySelector('[data-presu-por-seccion-sel]')?.focus(); }
    });
  }
  let ultimaBusqueda = '';

  $host.querySelector('[data-presu-nuevo-producto]').addEventListener('click', () => {
    abrirFormProducto(null, () => pintar(ultimaBusqueda));
  });

  // MADERINCO-MIS-ITEMS-REDISENO-T1 parte 2: filtros de seccion y paso (solo cuando la API manda ubicaciones, nexo-api 579).
  let filtroSeccion = '';
  let filtroPaso = '';
  const pintar = async (buscar = '') => {
    ultimaBusqueda = buscar;
    const res = await PresupuestosApiClient.listarProductos({ buscar, conUbicaciones: esDemoMaderinco() });
    if (!res.ok) {
      $lista.innerHTML = bloqueFalla(res, 'Tu catalogo todavia no esta conectado.',
        'Tus productos ya estan cargados; falta publicar la consulta para verlos aqui.');
      return;
    }
    const items = res.data;
    if (items.length === 0) {
      if (buscar) { $lista.innerHTML = `<p class="empty-state">Nada con ese nombre.</p>`; return; }
      // Este mensaje decia "el catalogo lo decide el usuario, no el selector" y
      // mandaba a entrar con otro usuario. Desde el ASSIGN #8561 eso es FALSO:
      // todas las llamadas mandan ?negocio= del selector y el backend valida
      // membresia. Un vacio ahora significa vacio de verdad en ESE negocio, y
      // decirlo mal manda al dueno a cerrar sesion para nada.
      const sel = getSelectedBusiness();
      $lista.innerHTML = `<div class="presu-vacio"><div class="icono">&#128230;</div>
          <p>No hay ${palabras(esDemoMaderinco()).plural} cargados en <b>${esc(getSelectedBusinessName() || sel || 'este negocio')}</b> todav&iacute;a.</p>
          <p style="font-size:0.875rem;color:#8b949e">Si esperabas ver otros, cambia el negocio con el selector de arriba.</p></div>`;
      return;
    }
    if (esDemoMaderinco()) {
      const estado = $host.querySelector('[data-presu-misitems-estado-filtro]')?.value || 'todos';
      const todas = items.map(filaDeProducto);
      const conUbic = hayUbicaciones(todas);
      const op = conUbic ? opcionesDeFiltro(todas) : { secciones: [], pasos: [], sinUbicar: false };
      if (filtroSeccion && filtroSeccion !== 'sin_ubicar' && !op.secciones.some((x) => x.id === filtroSeccion)) filtroSeccion = '';
      if (filtroPaso && !op.pasos.some((x) => x.id === filtroPaso)) filtroPaso = '';
      const filas = filtrarFilas(todas, { estado, seccion: filtroSeccion, paso: filtroPaso });
      const hayFiltro = estado !== 'todos' || !!filtroSeccion || !!filtroPaso;
      const barra = conUbic ? `<div data-presu-misitems-filtros class="presu-fila" style="gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:8px;font-size:14px">
          <label style="color:#c9d1d9;display:inline-flex;gap:6px;align-items:center">Secci&oacute;n <select data-presu-misitems-seccion class="form-input" style="font-size:14px;width:auto;min-width:0;max-width:220px">
            <option value="">Todas</option>${op.secciones.map((x) => `<option value="${esc(x.id)}"${x.id === filtroSeccion ? ' selected' : ''}>${esc(x.nombre)}</option>`).join('')}${op.sinUbicar ? `<option value="sin_ubicar"${filtroSeccion === 'sin_ubicar' ? ' selected' : ''}>Sin secci&oacute;n todav&iacute;a</option>` : ''}</select></label>
          <label style="color:#c9d1d9;display:inline-flex;gap:6px;align-items:center">Paso <select data-presu-misitems-paso class="form-input" style="font-size:14px;width:auto;min-width:0"${filtroSeccion === 'sin_ubicar' ? ' disabled' : ''}>
            <option value="">Todos</option>${op.pasos.map((x) => `<option value="${esc(x.id)}"${x.id === filtroPaso ? ' selected' : ''}>${esc(x.etiqueta)}</option>`).join('')}</select></label>
          ${hayFiltro ? '<button type="button" class="btn-ghost btn-sm" data-presu-misitems-limpiar style="font-size:14px">Limpiar filtros</button>' : ''}
        </div>` : '';
      // claridad v0.5: las referencias de plantilla sin producto se cuentan APARTE (no son productos disponibles).
      const pend = referenciasSinProducto(plantillaDemo);
      const pendientes = pend.conocido && pend.sinProducto > 0
        ? `<div data-presu-misitems-pendientes role="note" style="font-size:14px;color:#d29922;margin-bottom:8px">${pend.sinProducto === 1 ? '1 referencia' : `${pend.sinProducto} referencias`} de tu plantilla todav&iacute;a sin producto: no est&aacute;n en esta lista.</div>`
        : '';
      $lista.innerHTML = barra + pendientes + `<div data-presu-misitems-cuenta role="status" style="font-size:14px;color:#8b949e;margin-bottom:6px">${esc(textoResultados(filas.length, hayFiltro || !!buscar))}</div>`
        + (filas.length ? listaMisItemsHTML(filas) : `<p class="empty-state">Ning&uacute;n producto con este filtro.</p>`);
      $lista.querySelector('[data-presu-misitems-seccion]')?.addEventListener('change', (e) => { filtroSeccion = e.target.value; if (filtroSeccion === 'sin_ubicar') filtroPaso = ''; pintar(ultimaBusqueda); });
      $lista.querySelector('[data-presu-misitems-paso]')?.addEventListener('change', (e) => { filtroPaso = e.target.value; pintar(ultimaBusqueda); });
      $lista.querySelector('[data-presu-misitems-limpiar]')?.addEventListener('click', () => {
        filtroSeccion = ''; filtroPaso = '';
        const $est = $host.querySelector('[data-presu-misitems-estado-filtro]'); if ($est) $est.value = 'todos';
        pintar(ultimaBusqueda).then(() => $host.querySelector('[data-presu-misitems-seccion]')?.focus());
      });
      $lista.querySelectorAll('[data-presu-editar]').forEach(b => b.addEventListener('click', () => {
        const p = items.find(x => String(x.id ?? x.Id ?? '') === b.dataset.presuEditar);
        // claridad v0.5: volver de Editar conserva busqueda y filtros (pintar los relee) y el foco en la fila editada.
        const id = b.dataset.presuEditar;
        if (p) abrirFormProducto(normalizarProducto(p), () => pintar(ultimaBusqueda).then(() => $lista.querySelector(`[data-presu-editar="${CSS.escape(id)}"]`)?.focus()));
      }));
      return;
    }
    $lista.innerHTML = `
      <div style="font-size:0.875rem;color:#8b949e;margin-bottom:6px">${items.length} producto${items.length === 1 ? '' : 's'}</div>
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(320px,1fr));gap:8px">
        ${items.map(p => {
          const nombre = p.nombre ?? p.Nombre ?? '(sin nombre)';
          const unidad = p.unidad ?? p.Unidad ?? '';
          const descripcion = p.descripcion ?? p.Descripcion ?? '';
          const venta = p.precio_venta ?? p.precioVenta ?? 0;
          const mayor = p.precio_mayor ?? p.precioMayor ?? 0;
          const desde = p.mayor_desde_unidades ?? p.mayorDesdeUnidades ?? 0;
          return `<button type="button" data-presu-editar="${esc(p.id ?? p.Id ?? '')}" style="width:100%;text-align:left;background:#161b22;border:1px solid #30363d;border-radius:8px;padding:10px 12px;display:flex;gap:10px;justify-content:space-between;align-items:center;cursor:pointer;color:inherit;font:inherit">
            <div style="min-width:0">
              <div style="font-size:0.95rem;color:#c9d1d9;font-weight:600">${esc(nombre)}</div>
              ${descripcion ? `<div style="font-size:0.875rem;color:#8b949e;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%">${esc(descripcion)}</div>` : ''}
              <div style="font-size:0.875rem;color:#8b949e">${unidad ? esc(unidad) : 'sin unidad'}${(mayor > 0 && desde > 0) ? ' &middot; por mayor ' + fmtCOP(mayor) + ' desde ' + fmtNum(desde) : ''}</div>
            </div>
            <div style="text-align:right;white-space:nowrap;font-size:0.875rem;color:#3fb950;font-weight:600">${fmtCOP(venta)}</div>
          </button>`;
        }).join('')}
      </div>
      <div style="font-size:0.875rem;color:#8b949e;margin-top:8px">Toca un producto para editarlo.</div>`;

    // Se re-wirea en cada repintado: el listado se re-renderiza entero.
    $lista.querySelectorAll('[data-presu-editar]').forEach(b => b.addEventListener('click', () => {
      const id = b.dataset.presuEditar;
      const p = items.find(x => String(x.id ?? x.Id ?? '') === id);
      if (p) abrirFormProducto(normalizarProducto(p), () => pintar(ultimaBusqueda));
    }));
  };

  // PRESU-IMPORTADOR-PANEL-T1: Subir archivo abre (y cierra) el importador; se monta la primera vez que se abre.
  const $imp = $host.querySelector('[data-presu-importar-host]');
  $host.querySelector('[data-presu-subir-archivo]')?.addEventListener('click', (e) => {
    const abrir = $imp.hidden;
    $imp.hidden = !abrir;
    e.currentTarget.setAttribute('aria-expanded', String(abrir));
    if (abrir && !$imp.firstChild) montarImportador($imp, () => pintar(ultimaBusqueda));
    if (abrir) $imp.querySelector('[data-presu-importar-archivo]')?.focus();
  });
  const $casar = $host.querySelector('[data-presu-casar-host]');
  if ($casar) montarCasarPlantilla($casar, () => pintar(ultimaBusqueda));

  $host.querySelector('[data-presu-misitems-estado-filtro]')?.addEventListener('change', () => pintar(ultimaBusqueda));

  await pintar();

  let t = null;
  $host.querySelector('[data-presu-buscar]').addEventListener('input', (e) => {
    clearTimeout(t);
    const q = e.target.value.trim();
    t = setTimeout(() => pintar(q), 300);
  });
}

// --------------------------------------------------- V4 guardados (lista)

/**
 * Presupuestos ya guardados. Hasta hoy el armador GUARDABA y el presupuesto
 * desaparecia: no habia por donde volver a verlo.
 *
 * AUDIENCIA: NEGOCIO (dueno/equipo, pantalla de escritorio).
 *
 * REGLA DE ESTA VISTA: el resumen que manda es el de la API, tal cual. Aqui NO
 * se recalcula nada. Si el panel recalculara, el mismo presupuesto tendria dos
 * totales -- uno en el armador y otro en la lista -- y el dueno no sabria a
 * cual creerle. (Medido sobre los 3 presupuestos reales: la API suma todo en un
 * bloque y no redondea intermedios, mientras el armador separa adicionales y
 * redondea por bloque; en Choiba eso da 1 peso de diferencia. Reportado en
 * bitacora: la formula no la decide una vista.)
 */
async function renderGuardados($host, negocio) {
  $host.innerHTML = `
    <section class="artifact-view presu-scope presu-ancho">
      <div class="artifact-header"><div>
        ${regresoHtml({ volverA: "#/presupuestos", texto: "Volver al asistente", atributos: "data-presu-volver" })}
        <h1>Tus presupuestos</h1>
      </div></div>
      <input type="search" data-presu-buscar-codigo placeholder="Buscar por c&oacute;digo (ej. JJ 1271)" class="form-input" style="width:320px;margin-bottom:16px">
      <div data-presu-lista><div class="presu-skel"><div></div><div></div><div></div><div></div></div></div>
    </section>`;

  $host.querySelector('[data-presu-volver]').addEventListener('click', (e) => {
    e.preventDefault();
    renderRecepcion($host, negocio);
  });

  const $lista = $host.querySelector('[data-presu-lista]');

  const pintar = async (codigo = '') => {
    const res = await PresupuestosApiClient.listarPresupuestos({ codigo });
    if (!res.ok) { $lista.innerHTML = bloqueFalla(res, 'Tus presupuestos todavia no estan conectados.'); return; }
    const items = demoFiltrar(res.data);
    if (items.length === 0) {
      $lista.innerHTML = codigo
        ? `<div class="presu-vacio"><div class="icono">&#128269;</div><p>Ning&uacute;n presupuesto con ese c&oacute;digo.</p></div>`
        : (demoPaso() === 0
          ? `<div class="presu-vacio"><div class="icono">&#128196;</div>
              <p>A&uacute;n no tienes presupuestos montados.</p>
              <button type="button" class="btn-primary btn-sm" data-presu-ir-subir>Sube tu primer PDF</button></div>`
          : `<div class="presu-vacio"><div class="icono">&#128203;</div>
              <p>Todav&iacute;a no has guardado ning&uacute;n presupuesto.</p>
              <button type="button" class="btn-primary btn-sm" data-presu-ir-armador>Crear el primero</button></div>`);
      const $ir = $lista.querySelector('[data-presu-ir-armador]');
      if ($ir) $ir.addEventListener('click', () => renderArmador($host, negocio));
      const $subir = $lista.querySelector('[data-presu-ir-subir]');
      if ($subir) $subir.addEventListener('click', () => {
        renderRecepcion($host, negocio);
        renderDropzone(document.getElementById('nx-presu-zona'));
      });
      return;
    }
    // Filas de 42px, el dinero a la derecha y hover de fila (regla 12 del COORD #8413).
    $lista.innerHTML = `
      <div style="font-size:14px;color:#8b949e;margin-bottom:8px">${items.length} presupuesto${items.length === 1 ? '' : 's'}</div>
      <div style="background:#161b22;border:1px solid #30363d;border-radius:10px;overflow:hidden">
        ${items.map(p => {
          const id = p.id ?? p.Id;
          const total = Number(p.total ?? p.Total ?? 0);
          return `<button type="button" class="presu-fila-lista" data-presu-abrir="${esc(id)}" style="width:100%;min-height:42px;text-align:left;background:transparent;border:none;border-bottom:1px solid #21262d;color:#c9d1d9;cursor:pointer;padding:8px 12px;display:flex;gap:12px;align-items:center;justify-content:space-between">
            <span style="min-width:0">
              <span style="font-size:0.95rem;font-weight:600">${esc(p.proyecto ?? p.Proyecto ?? '(sin proyecto)')}</span>
              <span style="display:block;font-size:0.875rem;color:#8b949e">${esc(p.codigo ?? p.Codigo ?? 's/c')} &middot; ${esc(p.cliente_nombre ?? p.clienteNombre ?? p.ClienteNombre ?? 'sin cliente')} &middot; ${esc(fechaCorta(p.fecha ?? p.Fecha))}</span>
            </span>
            <span style="text-align:right;white-space:nowrap">
              <span style="font-size:0.9rem;color:#3fb950;font-weight:600">${fmtCOP(total)}</span>
              <span style="display:block;font-size:0.875rem;color:#8b949e">${esc(estadoLegible(p.estado ?? p.Estado))}</span>
            </span>
          </button>`;
        }).join('')}
      </div>`;

    $lista.querySelectorAll('[data-presu-abrir]').forEach(b => b.addEventListener('click', () => {
      // Se lleva el total QUE MOSTRO LA LISTA para poder contrastarlo con el
      // que devuelve la ficha. Ver el comentario de renderDetalle.
      const fila = items.find(p => String(p.id ?? p.Id ?? '') === b.dataset.presuAbrir);
      const totalLista = fila ? Number(fila.total ?? fila.Total ?? NaN) : NaN;
      renderDetalle($host, negocio, b.dataset.presuAbrir, { totalLista });
    }));
  };

  await pintar();

  let t = null;
  $host.querySelector('[data-presu-buscar-codigo]').addEventListener('input', (e) => {
    clearTimeout(t);
    const q = e.target.value.trim();
    t = setTimeout(() => pintar(q), 300);
  });
}

/** dd/mm/aaaa a la colombiana; la API manda la fecha como texto ISO. */
function fechaCorta(f) {
  if (!f) return 'sin fecha';
  const m = String(f).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(f);
}

/** El estado viaja en clave de maquina; el dueno lee palabras. */
function estadoLegible(e) {
  const mapa = {
    borrador: 'Borrador',
    pdf_solicitado: 'PDF solicitado',
    en_cola: 'En cola',
    aprobado: 'Aprobado',
    emitido: 'Emitido',
    enviado: 'Enviado',
    anulado: 'Anulado'
  };
  const clave = String(e || '').toLowerCase();
  if (mapa[clave]) return mapa[clave];
  if (!clave) return 'Sin estado';
  // Un estado que nadie mapeo se ve igual de pulido: -emitido- no puede quedar
  // en minuscula al lado de -Borrador-.
  return clave.charAt(0).toUpperCase() + clave.slice(1).replace(/_/g, ' ');
}

// -------------------------------------------------- V5 detalle de un guardado

async function renderDetalle($host, negocio, id, opts = {}) {
  $host.innerHTML = `
    <section class="artifact-view presu-scope presu-ancho">
      <div class="artifact-header"><div>
        ${regresoHtml({ volverA: '#/presupuestos', texto: (opts.desde === 'asistente' || demoActiva()) ? 'Volver al asistente' : 'Volver a tus presupuestos', atributos: 'data-presu-volver-lista' })}
        <h1 data-presu-titulo>Presupuesto</h1>
      </div></div>
      <div class="presu-grid">
        <div><div data-presu-cuerpo><div class="presu-skel"><div></div><div></div><div></div><div></div></div></div></div>
        <div class="presu-aside"><div data-presu-lado></div></div>
      </div>
    </section>`;

  // El regreso vuelve A DONDE ESTABAS, no siempre a la lista. En modo enfoque
  // este enlace es la UNICA salida visible (el navbar esta oculto), asi que
  // mandarte a una pantalla en la que no estabas es perder al usuario.
  $host.querySelector('[data-presu-volver-lista]').addEventListener('click', (e) => {
    e.preventDefault();
    // En modo demo el asistente ES la casa: el dashboard general de Nexo no
    // existe para quien mira la proyeccion, y la lista es una parada, no el
    // punto de partida. Fuera del demo se respeta de donde vino.
    if (opts.desde === 'asistente' || demoActiva()) return renderRecepcion($host, negocio);
    renderGuardados($host, negocio);
  });

  const $cuerpo = $host.querySelector('[data-presu-cuerpo]');
  const $lado = $host.querySelector('[data-presu-lado]');

  const res = await PresupuestosApiClient.obtenerPresupuesto(id);
  if (!res.ok || !res.data) {
    $cuerpo.innerHTML = bloqueFalla(res, 'No pudimos abrir este presupuesto.');
    return;
  }
  const p = res.data;
  const r = p.resumen ?? p.Resumen ?? {};
  const secciones = seccionesOrdenadas(p.secciones ?? p.Secciones ?? []);

  $host.querySelector('[data-presu-titulo]').textContent = p.proyecto ?? p.Proyecto ?? 'Presupuesto';

  $cuerpo.innerHTML = `
    <div class="presu-det-cabecera">
      <div class="presu-det-codigo">${esc(p.codigo ?? p.Codigo ?? 's/c')} &middot; versi&oacute;n ${esc(p.version ?? p.Version ?? 1)}</div>
      <div class="presu-det-meta">${esc(p.cliente_nombre ?? p.clienteNombre ?? p.ClienteNombre ?? 'Sin cliente')} &middot; ${esc(fechaCorta(p.fecha ?? p.Fecha))} &middot; ${esc(estadoLegible(p.estado ?? p.Estado))}</div>
    </div>
    ${secciones.length === 0
      ? `<div class="presu-vacio"><div class="icono">&#128230;</div><p>Este presupuesto no tiene secciones cargadas.</p></div>`
      : secciones.map(s => {
        const items = s.items ?? s.Items ?? [];
        const esAdic = s.es_adicional ?? s.esAdicional ?? s.EsAdicional ?? false;
        const sumaSeccion = items.reduce((a, i) => a + Number(i.subtotal ?? i.Subtotal ?? 0), 0);
        return `<div class="presu-det-seccion${esAdic ? ' es-adicional' : ''}">
          <div class="presu-det-sec-cab">
            <div class="presu-det-sec-nombre">${esc(s.nombre ?? s.Nombre ?? 'Seccion')}${esAdic ? '<span class="presu-det-badge">adicional</span>' : ''}</div>
            <div class="presu-det-sec-total">${fmtCOP(sumaSeccion)}</div>
          </div>
          <div class="presu-det-items">
          ${items.length === 0
            ? '<div class="presu-det-vacio">Sin productos.</div>'
            : items.map(i => `<div class="presu-det-item">
                <span class="presu-det-item-titulo">${esc(i.titulo ?? i.Titulo ?? '')}
                  <span class="presu-det-item-detalle">${fmtNum(i.cantidad ?? i.Cantidad ?? 0)}${(i.unidad ?? i.Unidad) ? ' ' + esc(i.unidad ?? i.Unidad) : ''} &times; ${fmtCOP(i.precio_unit ?? i.precioUnit ?? i.PrecioUnit ?? 0)}</span></span>
                <span class="presu-det-item-valor">${fmtCOP(i.subtotal ?? i.Subtotal ?? 0)}</span></div>`).join('')}
          </div>
        </div>`;
      }).join('')}`;

  // El resumen sale TAL CUAL de la API: es la unica verdad de lo guardado.
  const val = (camel, pascal) => Number(r[camel] ?? r[pascal] ?? 0);
  const hay = (camel, pascal) => (camel in r) || (pascal in r);

  // El resumen viene en DOS formas segun lo que este desplegado:
  //   plano   -> Subtotal es el subtotal COMPLETO y hay un solo AIU.
  //   bloques -> Subtotal es SOLO el principal, y llegan los campos
  //              *Adicional + TotalPrincipal (arbitraje #8358, PR #163 de Argy-A).
  // Si se pintara el shape de bloques como si fuera plano, las filas impresas
  // NO sumarian el total impreso -- justo lo que la regla de casa evita. Asi que
  // se detecta y se pinta cada una como es; cuando la API cambie, esto se
  // enciende solo, sin re-deploy.
  const porBloques = hay('totalAdicional', 'TotalAdicional') || hay('totalPrincipal', 'TotalPrincipal');
  const conAdicionales = porBloques && (val('subtotalAdicional', 'SubtotalAdicional') > 0 || val('totalAdicional', 'TotalAdicional') > 0);
  const utilPct = fmtNum(val('utilidadPct', 'UtilidadPct'));
  const ivaPct = fmtNum(val('ivaUtilidadPct', 'IvaUtilidadPct'));

  const subtitulo = (t) => `<div style="font-size:0.875rem;color:#8b949e;text-transform:uppercase;letter-spacing:0.04em;margin:10px 0 2px">${t}</div>`;

  const bloquePrincipal =
    filaResumen('Subtotal', fmtCOP(val('subtotal', 'Subtotal'))) +
    filaResumen('Utilidad ' + utilPct + '%', fmtCOP(val('utilidad', 'Utilidad'))) +
    filaResumen('IVA ' + ivaPct + '% (sobre la utilidad)', fmtCOP(val('ivaUtilidad', 'IvaUtilidad'))) +
    (conAdicionales ? filaResumen('Suma', fmtCOP(val('totalPrincipal', 'TotalPrincipal')), { borde: true }) : '');

  const bloqueAdicionales = !conAdicionales ? '' :
    subtitulo('Adicionales') +
    filaResumen('Subtotal', fmtCOP(val('subtotalAdicional', 'SubtotalAdicional'))) +
    filaResumen('Utilidad ' + utilPct + '%', fmtCOP(val('utilidadAdicional', 'UtilidadAdicional'))) +
    filaResumen('IVA ' + ivaPct + '% (sobre la utilidad)', fmtCOP(val('ivaUtilidadAdicional', 'IvaUtilidadAdicional'))) +
    filaResumen('Suma', fmtCOP(val('totalAdicional', 'TotalAdicional')), { borde: true });

  // El Total NO es una fila mas del listado: es la cifra por la que el cliente
  // dice si o no. Va en su propio bloque, a 1.45rem, separado por una linea.
  const totalVal = val('total', 'Total');
  $lado.innerHTML = `
    <div class="presu-det-resumen">
      <div class="presu-det-resumen-tit">Resumen</div>
      ${conAdicionales ? subtitulo('Obra') : ''}
      ${bloquePrincipal}
      ${bloqueAdicionales}
      ${val('descuento', 'Descuento') > 0 ? filaResumen('Descuento', '-' + fmtCOP(val('descuento', 'Descuento')), { color: '#8b949e' }) : ''}
      <div class="presu-det-total">
        <span class="presu-det-total-label">Total</span>
        <span class="presu-det-total-valor" style="color:${totalVal < 0 ? '#f85149' : '#3fb950'}">${fmtCOP(totalVal)}</span>
      </div>
      ${val('total', 'Total') < 0 ? `<div style="font-size:0.875rem;color:#f85149;margin-top:6px">
          Este presupuesto da <b>en contra</b>: el descuento pesa m&aacute;s que todo lo cotizado.
          <br><span style="color:#8b949e">Suele pasar cuando una copia se queda sin productos y conserva el descuento. No lo env&iacute;es as&iacute;.</span></div>` : ''}
      ${val('valorM2', 'ValorM2') > 0 ? filaResumen('Por m&sup2;', fmtCOP(val('valorM2', 'ValorM2')), { color: '#8b949e' }) : ''}
      <div style="font-size:0.875rem;color:#8b949e;margin-top:8px">Estos n&uacute;meros vienen guardados; no se recalculan aqu&iacute;.</div>
      <div data-presu-aviso-lista></div>
      <button class="btn-primary btn-sm" data-presu-pedir-pdf style="width:100%;margin-top:12px">Ver el PDF</button>
      <button class="btn-ghost btn-sm" data-presu-editar-version style="width:100%;margin-top:8px">Editar (nueva versi&oacute;n)</button>
      <button class="btn-ghost btn-sm" data-presu-usar-borrador style="width:100%;margin-top:8px">Usar como borrador</button>
      ${esDemoMaderinco() ? '<button class="btn-ghost btn-sm" data-presu-replicar style="width:100%;min-height:44px;margin-top:8px">Replicar en los pasos (precios de hoy)</button>' : ''}
      <div data-presu-pdf-msg style="font-size:0.875rem;margin-top:6px;min-height:1em"></div>
    </div>`;

  // Punto 2 del feedback: editar carga el guardado EN el armador y al guardar
  // sale como version nueva del MISMO codigo, no como un presupuesto suelto.
  const $editar = $lado.querySelector('[data-presu-editar-version]');
  $editar.addEventListener('click', async () => {
    cargarEnArmador(p);
    // Argy-A midio que existe UNIQUE (business_id, codigo, version) (#8479):
    // la version NO es libre. Si abro una v4 vieja y ya hay una v5, mandar
    // version+1 chocaria contra la restriccion. Se pregunta cual es la ultima
    // ANTES de abrir el armador; si la consulta falla, se sigue con la del
    // presupuesto abierto, que es lo que se sabia hasta ahora.
    if (armador.codigo) {
      const textoEditar = $editar.innerHTML;
      $editar.disabled = true;
      $editar.textContent = 'Abriendo...';
      const res = await PresupuestosApiClient.listarPresupuestos({ codigo: armador.codigo });
      $editar.disabled = false;
      $editar.innerHTML = textoEditar;
      if (res.ok && Array.isArray(res.data)) {
        const versiones = res.data
          .filter(x => String(x.codigo ?? x.Codigo ?? '') === armador.codigo)
          .map(x => Number(x.version ?? x.Version ?? 0) || 0);
        if (versiones.length) armador.version = Math.max(...versiones, armador.version);
      }
    }
    // MADERINCO-ARBOLES-SECCIONES-T1: la version nueva arranca con lo respondido en la que se abrio.
    renderArmador($host, negocio, { yaCargado: true, desde: 'detalle', volverA: id, recorridoDe: id });
  });

  // REPLICAR-PRESUPUESTO-T1 (decision de Ares, evento 43514): uno NUEVO desde este, sin su codigo ni su cliente, cada seccion en la de
  // la plantilla, las cantidades por confirmar y el precio de hoy junto al de entonces. Lo arma replicarPresupuesto.
  const $replicar = $lado.querySelector('[data-presu-replicar]');
  if ($replicar) $replicar.addEventListener('click', async () => {
    const texto = $replicar.innerHTML;
    $replicar.disabled = true;
    $replicar.textContent = 'Leyendo la plantilla y los precios de hoy...';
    const [pl, prods, cas] = await Promise.all([PresupuestosApiClient.plantilla(), PresupuestosApiClient.listarProductos(),
      PresupuestosApiClient.casamientoItemEstandar(id)]);
    $replicar.disabled = false;
    $replicar.innerHTML = texto;
    const $m = $lado.querySelector('[data-presu-pdf-msg]');
    if (!pl.ok || !prods.ok) {
      $m.style.color = '';
      $m.classList.add('presu-rep-pendiente');
      $m.textContent = !pl.ok
        ? 'No pude leer tu plantilla, así que no sé en qué sección va cada partida. Intenta de nuevo en un momento.'
        : 'No pude leer los precios de hoy del catálogo. Intenta de nuevo en un momento.';
      return;
    }
    // Sin el casamiento (ruta aun no servida o sin permiso) la replica sigue igual, sin tipo: no frena lo demas.
    const rep = replicarPresupuesto(p, { seccionesPlantilla: normalizarPlantilla(pl.data).secciones, precios: preciosDeHoy(prods.data),
      casamiento: cas.ok ? casamientoDesdeApi(cas.data) : null });
    // PR 4 (Ares #43601): con el asistente, lo de tipo ELEGIDO va a su seccion del cliente y a su fila; lo sugerido y lo
    // sin casar queda en el Resumen por clasificar. El total no se mueve (cada partida conserva su precio).
    await cargarPlantilla();
    if (plantillaDemo && estandarDeLineas.size) {
      const p4 = llevarReplicaALosPasos(rep, ubicadorDeEstandar());
      rep.secciones = p4.secciones;
      rep.cuentas = { ...rep.cuentas, enPasos: p4.enPasos };
    }
    limpiarArmador();
    armador.utilidadPct = rep.origen.utilidadPct || 5;
    armador.ivaUtilidadPct = rep.origen.ivaUtilidadPct || 19;
    armador.descuento = rep.origen.descuento;
    armador.areaM2 = rep.origen.areaM2;
    armador.secciones = rep.secciones;
    armador._replica = rep;
    renderArmador($host, negocio, { yaCargado: true, desde: 'detalle', volverA: id });
  });

  // Punto 1: clonar es del server (POST /{id}/clonar, lo construye Argy-A).
  // Mientras no exista, el boton lo dice y ofrece la salida que SI funciona.
  const $clon = $lado.querySelector('[data-presu-usar-borrador]');
  const textoClon = $clon.innerHTML;
  $clon.addEventListener('click', async () => {
    $clon.disabled = true;
    $clon.innerHTML = '<span style="display:inline-block;width:16px;height:16px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;vertical-align:-3px;animation:presu-gira 0.7s linear infinite"></span> Clonando...';
    const res = await PresupuestosApiClient.clonarPresupuesto(id);
    $clon.disabled = false;
    $clon.innerHTML = textoClon;
    const $m = $lado.querySelector('[data-presu-pdf-msg]');
    if (res.ok) {
      const nuevo = res.data?.data ?? res.data ?? null;
      const nuevoId = nuevo?.id ?? nuevo?.Id ?? null;
      // La respuesta cuenta cuantas secciones e items se copiaron. Argy-A lo
      // expuso para -no cantar victoria en vacio- (contrato leido en su rama
      // argy-api/presu-clonar antes de que mergeara), asi que se usa: un clon
      // sin items es un cascaron y el dueno tiene que saberlo ANTES de
      // mandarselo al cliente.
      const items = Number(nuevo?.items ?? nuevo?.Items ?? -1);
      if (items === 0) {
        $m.style.color = '#d29922';
        // Argy-A midio (#8487) que un clon cascara CONSERVA el descuento, asi
        // que su total queda en NEGATIVO: -1.000.000 exacto en el Choiba. Si el
        // aviso no lo nombra, el rojo del total parece otro error distinto.
        $m.innerHTML = `Se cre&oacute; la copia, pero <b>sin ning&uacute;n producto</b>.
          <br><span style="color:#8b949e">Qued&oacute; solo el encabezado, y como conserva el descuento su total se va a ver <b>en negativo</b>. Rev&iacute;sala antes de mandarla.</span>`;
        if (nuevoId) $m.insertAdjacentHTML('beforeend',
          '<br><button type="button" class="btn-ghost btn-sm" data-presu-ver-clon style="margin-top:8px">Verla igual</button>');
        const $ver = $m.querySelector('[data-presu-ver-clon]');
        if ($ver) $ver.addEventListener('click', () => renderDetalle($host, negocio, nuevoId));
        return;
      }
      if (nuevoId) return renderDetalle($host, negocio, nuevoId);
      return renderGuardados($host, negocio);
    }
    $m.style.color = '#d29922';
    if (res.error?.code === 'NOT_IMPLEMENTED') {
      $m.innerHTML = `Clonar en el servidor todav&iacute;a no est&aacute; publicado.
        <br><span style="color:#8b949e">Mientras tanto usa <b>Editar (nueva versi&oacute;n)</b>, que hace lo mismo desde aqu&iacute;.</span>`;
    } else if (res.error?.code === 'VERSION_OCUPADA') {
      // Este SI es de los que se arreglan reintentando: alguien creo esa
      // version en el mismo instante. Decirlo asi, y no con un -intenta mas
      // tarde- generico que no explica nada.
      $m.innerHTML = `Alguien cre&oacute; esa versi&oacute;n al mismo tiempo.
        <br><span style="color:#8b949e">Vuelve a tocar el bot&oacute;n: la siguiente versi&oacute;n queda libre.</span>`;
    } else {
      $m.innerHTML = esc(mensajeFalla(res.error?.code, 'No pudimos clonarlo ahora.'));
    }
  });

  // CONTRASTE LISTA vs FICHA.
  //
  // Argy-A reporto (#8484) que la formula del dinero esta IMPLEMENTADA dos
  // veces en la API -- el ResumenSql de la ficha y el LATERAL del listado -- y
  // que hoy son identicas, pero si alguien toca una sola "la lista y la ficha
  // muestran totales distintos del mismo presupuesto, y nadie lo nota hasta que
  // un cliente compara". Decidio no unificarlas sin decision de arquitectura, y
  // hace bien.
  //
  // Pero el panel es el unico que VE LOS DOS numeros: el de la lista al pintar
  // la fila y el de la ficha al abrirla. Compararlos aqui cuesta tres lineas y
  // convierte "nadie lo nota" en "se nota la primera vez que pasa".
  const totalLista = Number(opts.totalLista);
  const totalFicha = Number(val('total', 'Total'));
  if (Number.isFinite(totalLista) && Number.isFinite(totalFicha) && Math.round(totalLista) !== Math.round(totalFicha)) {
    const $aviso = $lado.querySelector('[data-presu-aviso-lista]');
    if ($aviso) {
      $aviso.style.cssText = 'margin-top:8px;font-size:0.875rem;color:#d29922;border-top:1px solid #30363d;padding-top:8px';
      $aviso.innerHTML = `En el listado este presupuesto aparece por <b>${fmtCOP(totalLista)}</b>
        y aqu&iacute; por <b>${fmtCOP(totalFicha)}</b>.
        <br><span style="color:#8b949e">Son dos c&aacute;lculos del servidor que deber&iacute;an coincidir: av&iacute;sale al equipo antes de usar cualquiera de los dos.</span>`;
    }
  }

  const $btn = $lado.querySelector('[data-presu-pedir-pdf]');
  const $pmsg = $lado.querySelector('[data-presu-pdf-msg]');
  const textoBtn = $btn.innerHTML;

  // El boton ENTREGA el PDF, no lo "solicita". Un boton que solo deja constancia
  // de un deseo no es un boton: es una promesa. Consume GET /{id}/pdf (lo esta
  // construyendo Argy-A, ASSIGN #8526) y mientras no exista lo dice honesto.
  $btn.addEventListener('click', async () => {
    $btn.disabled = true;
    $btn.innerHTML = '<span style="display:inline-block;width:16px;height:16px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;vertical-align:-3px;animation:presu-gira 0.7s linear infinite"></span> Abriendo...';
    $pmsg.textContent = '';
    const pdf = await PresupuestosApiClient.descargarPdf(id);
    $btn.disabled = false;
    $btn.innerHTML = textoBtn;

    if (pdf.ok && pdf.blob) {
      // Se abre en pestana nueva y se deja el enlace de descarga al lado: en una
      // demo proyectada abrirlo vale mas que bajarlo, pero el dueno se lo quiere
      // llevar.
      const url = URL.createObjectURL(pdf.blob);
      const nombre = `${(p.codigo ?? p.Codigo ?? 'presupuesto')}.pdf`;
      window.open(url, '_blank');
      $pmsg.style.color = '#3fb950';
      $pmsg.innerHTML = `Abierto en otra pesta&ntilde;a. <a href="${url}" download="${esc(nombre)}" style="color:#58a6ff">Descargarlo</a>`;
      // No se revoca de inmediato: la pestana recien abierta y el enlace de
      // descarga siguen necesitando la URL viva.
      setTimeout(() => URL.revokeObjectURL(url), 120000);
      return;
    }

    $pmsg.style.color = '#d29922';
    if (pdf.error?.code === 'NOT_IMPLEMENTED') {
      // Fail-open: se deja constancia con el endpoint que SI existe, y se dice
      // la verdad de que el archivo todavia no se entrega solo.
      const marca = await PresupuestosApiClient.solicitarPdf(id);
      $pmsg.innerHTML = marca.ok
        ? `El PDF se est&aacute; generando; te lo enviamos apenas est&eacute; listo.
           <br><span style="color:#8b949e">Qued&oacute; anotado en el presupuesto.</span>`
        : 'El PDF se est&aacute; generando; te lo enviamos apenas est&eacute; listo.';
      return;
    }
    if (pdf.error?.code === 'SIN_SESION') {
      $pmsg.innerHTML = `Tu sesi&oacute;n no est&aacute; activa o ya venci&oacute;.
        <br><span style="color:#8b949e">Vuelve a entrar y toca de nuevo.</span>`;
      return;
    }
    $pmsg.textContent = mensajeFalla(pdf.error?.code, 'No pudimos abrir el PDF ahora.');
  });
}

// ------------------------------------------------------------------ V3 armador

/**
 * El catalogo puede llegar en camelCase o PascalCase segun el build de la API.
 * Se normaliza UNA vez aqui para que el formulario no tenga que adivinar en
 * cada campo (y para no repetir el ?? en siete sitios).
 */
function normalizarProducto(p) {
  return {
    id: p.id ?? p.Id ?? null,
    nombre: p.nombre ?? p.Nombre ?? '',
    descripcion: p.descripcion ?? p.Descripcion ?? '',
    unidad: p.unidad ?? p.Unidad ?? '',
    costoUnitario: p.costo_unitario ?? p.costoUnitario ?? p.CostoUnitario ?? 0,
    precioVenta: p.precio_venta ?? p.precioVenta ?? p.PrecioVenta ?? 0,
    precioMayor: p.precio_mayor ?? p.precioMayor ?? p.PrecioMayor ?? 0,
    mayorDesdeUnidades: p.mayor_desde_unidades ?? p.mayorDesdeUnidades ?? p.MayorDesdeUnidades ?? 0,
    activo: (p.activo ?? p.Activo) !== false
  };
}

function filaResumen(label, valor, opts = {}) {
  const size = opts.grande ? '0.95rem' : '0.875rem'; // MADERINCO-SKILL-RESTO-T1: filas del resumen a 14px
  const peso = opts.grande ? '700' : '600';
  const borde = opts.borde ? 'border-top:1px solid #30363d;margin-top:4px;padding-top:6px;' : '';
  return `<div style="display:flex;justify-content:space-between;gap:10px;font-size:${size};color:${opts.color || '#c9d1d9'};padding:3px 0;${borde}">
    <span>${label}</span><span style="font-weight:${peso};font-variant-numeric:tabular-nums;white-space:nowrap">${valor}</span></div>`;
}

/**
 * Picker de productos del catalogo REAL (GET /presupuestos/productos, live).
 * Se abre por seccion desde el armador.
 *
 * Lecciones aplicadas: nada de <label> envolviendo el buscador (roba el foco,
 * ADMIN-UX-T1); el input NUNCA se reconstruye, solo se repinta la lista; la
 * cantidad pasa por el parser colombiano (12,5 no puede volverse 125); y cero
 * prompt/alert nativos (prohibidos por el SKILL).
 */
function abrirPicker(seccionIdx, alAgregar) {
  const seccion = armador.secciones[seccionIdx];
  if (!seccion) return;

  const $ov = document.createElement('div');
  $ov.style.cssText = 'position:fixed;inset:0;background:#0d1117cc;z-index:9998';
  const $panel = document.createElement('div');
  $panel.className = 'presu-scope';
  $panel.style.cssText = 'position:fixed;top:0;right:0;height:100vh;width:420px;max-width:100vw;background:#0d1117;border-left:1px solid #30363d;z-index:9999;display:flex;flex-direction:column';
  $panel.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;padding:14px 16px;border-bottom:1px solid #21262d;flex-shrink:0">
      <div style="min-width:0">
        <h3 style="margin:0;font-size:0.95rem;color:#f0f6fc">Agregar a ${esc(seccion.nombre)}</h3>
        <div style="font-size:0.875rem;color:#8b949e">Busca en tu cat&aacute;logo</div>
      </div>
      <button type="button" data-picker-cerrar title="Cerrar" style="background:transparent;border:none;color:#8b949e;cursor:pointer;font-size:1.4rem;line-height:1;padding:0 4px">&times;</button>
    </div>
    <div style="padding:12px 14px;flex-shrink:0">
      <input type="search" data-picker-buscar placeholder="Filtra por nombre" class="form-input form-input-sm" style="width:100%">
    </div>
    <div data-picker-lista style="flex:1;overflow-y:auto;padding:0 14px 14px"></div>`;
  document.body.appendChild($ov);
  document.body.appendChild($panel);

  const cerrar = () => { $ov.remove(); $panel.remove(); };
  $ov.addEventListener('click', cerrar);
  $panel.querySelector('[data-picker-cerrar]').addEventListener('click', cerrar);

  const $buscar = $panel.querySelector('[data-picker-buscar]');
  const $lista = $panel.querySelector('[data-picker-lista]');

  const agregar = (prod, cantidadRaw) => {
    const cantidad = num(cantidadRaw);
    if (!(cantidad > 0)) return false;
    seccion.items = seccion.items || [];
    // Iteracion 0: el precio sale del TRAMO del catalogo (precio por mayor desde N unidades); antes, siempre el de venta.
    seccion.items.push(itemDesdeProducto(prod, cantidad));
    if (typeof alAgregar === 'function') alAgregar();
    return true;
  };

  const pintar = (items) => {
    if (items.length === 0) {
      $lista.innerHTML = `<div style="padding:10px 0;color:#8b949e;font-size:0.875rem">Nada con ese nombre en tu cat&aacute;logo.</div>`;
      return;
    }
    $lista.innerHTML = items.map((it, k) => {
      const nombre = it.nombre ?? it.Nombre ?? '(sin nombre)';
      const unidad = it.unidad ?? it.Unidad ?? '';
      const venta = it.precioVenta ?? it.precio_venta ?? 0;
      return `<div style="border-bottom:1px solid #21262d;padding:8px 0">
        <div style="font-size:0.875rem;color:#c9d1d9;font-weight:600">${esc(nombre)}</div>
        <div style="font-size:0.875rem;color:#8b949e;margin-bottom:6px">${unidad ? esc(unidad) + ' &middot; ' : ''}${fmtCOP(venta)}</div>
        <div style="display:flex;gap:6px;align-items:center">
          <input data-picker-cant="${k}" class="form-input form-input-sm" inputmode="decimal" placeholder="Cantidad" style="width:110px;text-align:center">
          <button type="button" class="btn-primary btn-sm" data-picker-agregar="${k}" style="font-size:0.875rem">Agregar</button>
          <span data-picker-msg="${k}" style="font-size:0.875rem;color:#3fb950"></span>
        </div>
      </div>`;
    }).join('');

    $lista.querySelectorAll('[data-picker-agregar]').forEach(btn => btn.addEventListener('click', () => {
      const k = Number(btn.dataset.pickerAgregar);
      const $cant = $lista.querySelector(`[data-picker-cant="${k}"]`);
      const $msg = $lista.querySelector(`[data-picker-msg="${k}"]`);
      if (agregar(items[k], $cant.value)) {
        $msg.style.color = '#3fb950';
        $msg.textContent = 'agregado';
        $cant.value = '';
      } else {
        $msg.style.color = '#d29922';
        $msg.textContent = 'pon la cantidad';
        $cant.focus();
      }
    }));
  };

  let t = null, seq = 0;

  // Buscar es UNA sola funcion: la usan el teclado y el arranque. Antes la
  // logica vivia dentro del listener y por eso al abrir no habia nada que
  // llamar (ASSIGN #8561).
  const buscarYPintar = async (q) => {
    const mio = ++seq;
    $lista.innerHTML = `<div class="presu-skel"><div></div><div></div><div></div></div>`;
    const res = await PresupuestosApiClient.listarProductos(q ? { buscar: q } : {});
    if (mio !== seq) return; // llego tarde: ya hay una busqueda mas nueva
    if (!res.ok) {
      $lista.innerHTML = bloqueFalla(res, 'Tu catalogo todavia no esta conectado.');
      return;
    }
    pintar(res.data);
  };

  $buscar.addEventListener('input', () => {
    const q = $buscar.value.trim();
    clearTimeout(t);
    // SIN minimo de letras: al borrar lo escrito se vuelve a la lista completa,
    // no a una pantalla en blanco. El buscador REFINA una lista que ya existe;
    // no es la unica puerta al catalogo.
    t = setTimeout(() => buscarYPintar(q), 300);
  });

  // Al abrir se ve el catalogo. Un picker vacio que no dice nada se lee como
  // "no hay productos", y Josse reporto exactamente eso teniendo 49 cargados.
  buscarYPintar('');

  setTimeout(() => $buscar.focus(), 50);
}

function renderArmador($host, negocio, opts = {}) {
  // Los caminos que llegan aqui ("Crear un presupuesto nuevo" y "Crear el
  // primero" desde la lista vacia) significan lo mismo: uno nuevo. La excepcion
  // es editar un guardado, que llega con el estado ya cargado.
  if (!opts.yaCargado) limpiarArmador();
  $host.innerHTML = `
    <section class="artifact-view presu-scope presu-ancho">
      <div class="artifact-header"><div>
        ${regresoHtml({ volverA: '#/presupuestos', texto: opts.desde === 'detalle' ? 'Volver al presupuesto' : 'Volver al asistente', atributos: 'data-presu-volver' })}
        <h1>${armador.codigo ? 'Editar ' + esc(armador.codigo) + ' (versi&oacute;n ' + ((Number(armador.version) || 1) + 1) + ')' : 'Armar presupuesto'}</h1>
      </div>
      <!-- FRONT-OBSTRUCCIONES-T1: "Tu asistente" vive junto al titulo, no flotando sobre las partidas (medido: tapaba
           cantidades y Agregar a 375 y 768). Solo se ve bajo 1280; arriba el asistente esta en el carril. -->
      <button type="button" class="presu-fab" data-presu-copiloto-abrir>&#129333; Tu asistente</button></div>
      <div class="presu-grid">
        <div>
          <div data-presu-cab-resumen style="gap:8px;align-items:center;justify-content:space-between;flex-wrap:wrap;background:#161b22;border:1px solid #30363d;border-radius:10px;padding:10px 12px;margin-bottom:12px;font-size:14px">
            <span data-presu-cab-texto style="color:#c9d1d9;min-width:0"></span>
            <button type="button" class="btn-ghost btn-sm" data-presu-cab-editar aria-expanded="false" style="font-size:14px">Editar</button>
          </div>
          <div class="presu-form${esDemoMaderinco() ? ' presu-form-plegada' : ''}" style="background:#161b22;border:1px solid #30363d;border-radius:10px;padding:16px;margin-bottom:24px">
            <div class="presu-grupo presu-fila">
              <div class="presu-campo"><span>C&oacute;digo</span>
                <input data-presu-codigo class="form-input" style="width:160px" placeholder="ej. JJ 1347" value="${esc(armador.codigo || '')}"
                  ${armador.version ? 'readonly title="Ya guardado: se guarda como versi&oacute;n nueva del mismo c&oacute;digo"' : ''}>
                <small data-presu-err="codigo" style="display:block;color:#f85149;font-size:0.875rem;margin-top:2px"></small></div>
              <div class="presu-campo"><span>Proyecto</span>
                <input data-presu-proyecto class="form-input" style="width:260px" placeholder="ej. CHOIBA" value="${esc(armador.proyecto || '')}">
                <small data-presu-err="proyecto" style="display:block;color:#f85149;font-size:0.875rem;margin-top:2px"></small></div>
              <div class="presu-campo"><span>Cliente</span>
                <input data-presu-cliente class="form-input" style="width:260px" placeholder="ej. Maderinco S.A.S." value="${esc(armador.cliente || '')}">
                <small data-presu-err="cliente" style="display:block;color:#f85149;font-size:0.875rem;margin-top:2px"></small></div>
            </div>
            <div class="presu-grupo">
              <div class="presu-campo"><span>&Aacute;rea (m&sup2;)</span>
                <input data-presu-area class="form-input presu-num" style="width:8ch" inputmode="decimal" placeholder="64,70" value="${armador.areaM2 ? fmtNum(armador.areaM2) : ''}">
                <small data-presu-err="area" style="display:block;color:#f85149;font-size:0.875rem;margin-top:2px"></small></div>
            </div>
          </div>

          <div data-presu-arriba style="display:none;margin-bottom:12px">
            <p class="presu-ayuda" data-presu-ayuda>Lo que tu cliente no tiene en sus secciones se agrega desde el cat&aacute;logo o con una secci&oacute;n propia. Nada se agrega solo.</p>
            <!-- T151: el buscador solo se abre al pulsar Buscar (antes, campo y resultados siempre a la vista junto a la guia). -->
            <button type="button" class="btn-ghost btn-sm" data-presu-abrir-buscar aria-expanded="false" aria-controls="presu-buscador" style="font-size:14px">Buscar en todo el cat&aacute;logo</button>
            <div id="presu-buscador" data-presu-buscador hidden style="margin-top:8px">
            <div style="margin-bottom:8px"><label for="presu-buscar-plantilla" style="display:block;font-size:0.875rem;color:#8b949e;margin-bottom:6px">Buscar en todo el cat&aacute;logo <span style="color:#6e7681">(cada partida dice su grupo del cat&aacute;logo; entra a la secci&oacute;n de tu cliente que la usa, o a la que tienes abierta)</span></label>
              <input type="search" id="presu-buscar-plantilla" data-presu-buscar-plantilla class="form-input" style="width:100%;max-width:480px" placeholder="ej. teja, deck, griferia" autocomplete="off"></div>
            <div data-presu-busqueda-res role="status" aria-live="polite"></div>
            </div>
          </div>

          <div data-presu-secciones></div>

          <div class="presu-fila" data-presu-fila-seccion style="gap:8px;margin-top:16px">
            <input data-presu-nombre-seccion class="form-input" placeholder="${esDemoMaderinco() ? 'Nombre de tu secci&oacute;n propia (ej. Chimenea)' : 'Nombre de la secci&oacute;n (ej. Cocina)'}" style="width:260px">
            <button class="btn-ghost btn-sm" data-presu-nueva-seccion>${esDemoMaderinco() ? '+ Secci&oacute;n propia' : '+ Agregar secci&oacute;n'}</button>
          </div>
        </div>

        <div class="presu-aside">
          <div style="background:#161b22;border:1px solid #30363d;border-radius:8px;padding:12px">
            <div style="font-size:0.875rem;color:#8b949e;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:6px">Resumen</div>
            <div data-presu-resumen></div>
            <div class="presu-campo" style="margin-top:16px">
              <span>Utilidad %</span>
              <input data-presu-utilidad class="form-input presu-num" value="${fmtNum(armador.utilidadPct || 5)}" style="width:6ch" inputmode="decimal">
            </div>
            <button class="btn-primary btn-sm" data-presu-guardar style="width:100%;margin-top:10px">Guardar</button>
            <button class="btn-ghost btn-sm" data-presu-solicitar style="width:100%;margin-top:8px">Guardar y solicitar el PDF</button>
            <div data-presu-alcance-pdf style="display:none;font-size:0.875rem;color:#8b949e;margin-top:4px">Solicitar el PDF lo deja marcado como pedido; el PDF todav&iacute;a no se genera solo desde aqu&iacute;.</div>
            <div data-presu-msg style="font-size:0.875rem;margin-top:6px;min-height:1em"></div>
          </div>

          <div class="presu-copiloto" data-presu-copiloto-card style="background:#161b22;border:1px solid #30363d;border-radius:10px;padding:12px">
            <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:8px">
              <div style="font-size:14px;color:#8b949e">&#129333; Tu asistente</div>
              <button type="button" data-presu-copiloto-cerrar title="Ocultar" style="background:transparent;border:none;color:#8b949e;cursor:pointer;font-size:1.2rem;line-height:1;padding:0 4px">&times;</button>
            </div>
            ${esDemoMaderinco() ? '<button type="button" class="btn btn-primary" data-presu-guia style="width:100%;margin-bottom:8px;font-size:14px">Gu&iacute;ame secci&oacute;n por secci&oacute;n</button>' : ''}
            <div data-presu-copiloto-log style="max-height:320px;overflow-y:auto;font-size:0.875rem;color:#c9d1d9"></div>
            <input data-presu-copiloto class="form-input" placeholder="ej. sube la utilidad al 8" style="width:100%;margin-top:8px;font-size:0.875rem">
          </div>
        </div>
      </div>
      <div class="presu-barra-total" data-presu-barra role="status" aria-live="polite">
        <span>Total <b data-presu-barra-total>$0</b></span>
        <button type="button" class="btn-primary btn-sm" data-presu-barra-guardar>Guardar</button>
      </div>
    </section>`;

  // El regreso del armador vuelve A DONDE ESTABAS. Al editar una version nueva
  // se sale DEL presupuesto, y mandar al asistente obliga a buscarlo otra vez
  // para ver lo que acabas de dejar.
  $host.querySelector('[data-presu-volver]').addEventListener('click', (e) => {
    e.preventDefault();
    if (opts.desde === 'detalle' && opts.volverA) return renderDetalle($host, negocio, opts.volverA);
    renderRecepcion($host, negocio);
  });

  const $sec = $host.querySelector('[data-presu-secciones]');
  const $res = $host.querySelector('[data-presu-resumen]');
  const $msg = $host.querySelector('[data-presu-msg]');
  const $util = $host.querySelector('[data-presu-utilidad]');
  const $area = $host.querySelector('[data-presu-area]');

  // T1 (recorrido a 375, Ares #30856): la cabecera (codigo, proyecto, cliente, area) se pliega en una linea-resumen con
  // Editar; el espacio de accion queda arriba. Solo surte efecto a 600 px o menos (CSS). Un error de esos campos la abre.
  const $cabForm = $host.querySelector('.presu-form');
  const $cabTexto = $host.querySelector('[data-presu-cab-texto]');
  const $cabEditar = $host.querySelector('[data-presu-cab-editar]');
  const pintarCabResumen = () => {
    if (!$cabTexto) return;
    const v = (sel) => ($host.querySelector(sel)?.value || '').trim();
    const partes = [v('[data-presu-codigo]'), v('[data-presu-proyecto]'), v('[data-presu-cliente]'), v('[data-presu-area]') ? v('[data-presu-area]') + ' m²' : ''].filter(Boolean);
    $cabTexto.textContent = partes.length ? partes.join(' · ') : 'Datos del presupuesto: código, proyecto, cliente y área';
  };
  const desplegarCab = () => {
    if (!$cabForm?.classList.contains('presu-form-plegada')) return;
    $cabForm.classList.remove('presu-form-plegada');
    $cabEditar?.setAttribute('aria-expanded', 'true');
    if ($cabEditar) $cabEditar.textContent = 'Listo';
  };
  $cabEditar?.addEventListener('click', () => {
    if ($cabForm.classList.contains('presu-form-plegada')) { desplegarCab(); $host.querySelector('[data-presu-codigo]')?.focus(); return; }
    $cabForm.classList.add('presu-form-plegada');
    $cabEditar.setAttribute('aria-expanded', 'false'); $cabEditar.textContent = 'Editar';
    pintarCabResumen(); $cabEditar.focus();
  });
  ['[data-presu-codigo]', '[data-presu-proyecto]', '[data-presu-cliente]', '[data-presu-area]'].forEach((sel) => $host.querySelector(sel)?.addEventListener('input', pintarCabResumen));
  pintarCabResumen();

  // Josse 28-oct (#36553): el armador demo tiene UNA estructura, las secciones del cliente; pasoCapitulo es la que esta
  // en pantalla. Las 28 agrupaciones del catalogo ya no son secciones: son el filtro "grupo del catalogo" del buscador.
  let pasoCapitulo = 1;
  // Grupo 4: el tipo que el asesor eligio en cada linea (texto de la linea -> tipo; '' = sin tipo).
  const tipoPorLinea = new Map();
  // MAD-ASISTENTE-UN-PASO-T1 (maqueta aprobada #43601): con item estandar, el armador del demo va una pantalla a la vez.
  // idx = la pantalla; noAplica = secciones que no aplican en esta obra (Siguiente las salta, #43603); tipo = por seccion
  // el tipo elegido y la cantidad escrita (texto, como la escribio el asesor).
  // PR 3: filas = por "seccion|linea" lo que el asesor marco, eligio y escribio; chequeo = la respuesta de Revision.
  // editor = { clave, desde, borrador } de la fila con el lapiz abierto; guardado = la clave de la fila que acaba de guardar.
  const asi = { idx: 0, noAplica: new Set(), tipo: new Map(), filas: new Map(), chequeo: new Map(), editor: null, guardado: null };
  const asistenteActivo = () => esDemoMaderinco() && estandarDeLineas.size > 0 && !!plantillaDemo;
  // "Agregar otro item del catalogo" de una linea: cual esta abierto (numero|linea), lo escrito, el grupo y cuantos se ven.
  let otroAbierto = null;
  let otroQ = '';
  let otroGrupo = '';
  let otroMax = 12;
  const abrirBuscador = () => {
    const $c = $host.querySelector('[data-presu-buscador]');
    if ($c) { $c.hidden = false; $host.querySelector('[data-presu-abrir-buscar]')?.setAttribute('aria-expanded', 'true'); }
  };
  let editandoSeccion = null;
  // MADERINCO-SELECCION-POR-TIPO-T1: el recorrido de la Cubierta (Q1 tipo, Q2 estructura, Q3 complementos). Vive en la
  // sesion del armador: no se guarda en el servidor (eso seria contrato nuevo con A).
  let cubierta = { tipo: null, q2: null, q3: null, agregados: [], revisar: false, error: '', area: '' };
  // MADERINCO-ARBOLES-SECCIONES-T1: "No aplica" por esencial (obra parcial), con motivo obligatorio. En memoria del armador.
  const noAplica = new Map();
  let noAplicaEditando = null;
  // Parte 2: respuestas de los arboles de Paredes, Piso, Agua y gas y Electricidad (por id de esencial).
  const respArbol = new Map();
  const respDe = (id) => { if (!respArbol.has(id)) respArbol.set(id, { tipos: new Set(), checks: {}, complementos: null, error: '' }); return respArbol.get(id); };
  const deArboles = () => Object.fromEntries([...respArbol.entries()].map(([id, r]) => [id, estadoDelArbol(arbolDe(id), r)]));
  // #36553: las secciones del cliente mezclan items de varios grupos del catalogo; la esencial se reconoce por los ids de
  // catalogo de SUS partidas (no por el nombre de la seccion, que ahora es el del cliente).
  const comoGrupoDelCatalogo = (s) => {
    const porId = new Map((plantillaDemo?.secciones || []).flatMap((p) => (p.items || []).map((it) => [it.id, it])));
    return { items: (s.items || []).map((i) => porId.get(i.plantillaItemId)).filter(Boolean) };
  };
  const estadosActuales = () => estadosDeEsenciales({ armador, seccionDePlantilla: comoGrupoDelCatalogo, cubierta, noAplica, deArboles: deArboles() });
  /** La esencial (no Cubierta) a la que pertenece una seccion de la plantilla, o null. */
  const esencialDe = (p) => { const i = puestoEsencial(p); return Number.isFinite(i) && i > 0 ? ESENCIALES[i].id : null; };
  // conTitulo=false: dentro del espacio de arriba la seccion ya tiene su titulo (T3: un solo titulo, sin caja dentro de caja).
  const arbolHTML = (p, conTitulo = true) => {
    const id = esencialDe(p);
    const a = id && arbolDe(id);
    if (!a) return '';
    const r = respDe(id);
    const boton = (attr, texto, activo) => `<button type="button" ${attr} aria-pressed="${activo}" style="${chip};${activo ? 'border-color:#1f6feb;color:#79c0ff' : ''}">${texto}</button>`;
    const q1 = `<div class="presu-filtros"><span class="presu-rotulo">1. ${esc(a.tipo?.label || '')}</span>
        ${(a.tipo?.options || []).map((o) => boton(`data-arb-tipo="${esc(id)}|${esc(o.id)}"`, esc(o.label), r.tipos.has(o.id))).join('')}
        ${a.tipo?.unknown_allowed ? boton(`data-arb-tipo="${esc(id)}|no_definido"`, 'Todav&iacute;a no definido', r.tipos.has('no_definido')) : ''}</div>`;
    const cs = candidatosDeTipos(plantillaDemo, a, r.tipos);
    // T5: la partida que ya esta en el presupuesto lo dice en su fila (antes quedaba igual, con la cantidad vacia y Agregar).
    const puestas = new Map(armador.secciones.flatMap((sx) => (sx.items || []).filter((i) => i.plantillaItemId).map((i) => [i.plantillaItemId, i])));
    const lista = cs.length ? `<div class="presu-partidas" data-arb-candidatos="${esc(id)}">${cs.map(({ seccion, item }) => puestas.has(item.id)
      ? `<div class="presu-partida" data-presu-partida-puesta="${esc(item.id)}" style="color:#3fb950"><span class="presu-partida-nombre">&#10003; ${esc(item.descripcion)}</span><span style="grid-column:2 / -1;text-align:right">En tu presupuesto: ${fmtNum(puestas.get(item.id).cantidad)} ${esc(puestas.get(item.id).unidad || '')}</span></div>`
      : partidaHTML({ ...item, cantidadRef: null }, `data-arb-agregar="${esc(seccion.clave)}|${esc(item.id)}"`, seccion.clave !== p.clave ? ` <span class="presu-partida-sec">${esc(seccion.nombre)}</span>` : '')).join('')}</div>` : '';
    const q2 = `<div data-arb-obligada="${esc(id)}" style="margin-top:6px"><span class="presu-rotulo">2. ${esc(a.obligada?.label || '')} (obligatoria)</span>
        ${(a.obligada?.checks || []).map((c) => {
          const v = r.checks[c.id] || {};
          // T6: la etiqueta en su linea y las tres respuestas juntas debajo (antes Confirmado quedaba solo a la derecha).
          return `<div class="presu-filtros" data-arb-fila-check style="margin:2px 0"><span style="flex-basis:100%">${esc(c.label)}</span>
            ${['confirmado', 'pendiente', 'no_aplica'].map((st) => boton(`data-arb-check="${esc(id)}|${esc(c.id)}|${st}"`, { confirmado: 'Confirmado', pendiente: 'Pendiente', no_aplica: 'No aplica' }[st], v.estado === st)).join('')}
            ${v.estado === 'no_aplica' ? `<input class="form-input" data-arb-motivo="${esc(id)}|${esc(c.id)}" placeholder="&iquest;Por qu&eacute;?" maxlength="120" style="max-width:200px" value="${esc(v.motivo || '')}">` : ''}</div>`;
        }).join('')}</div>`;
    const q3 = `<div class="presu-filtros" style="margin-top:6px"><span class="presu-rotulo">3. ${esc(a.complementos?.label || '')} (opcional)</span>
        ${(a.complementos?.options || []).map((o) => boton(`data-arb-comp="${esc(id)}|${esc(o.id)}"`, esc(o.label), r.complementos === o.id)).join('')}</div>`;
    const est = estadoDelArbol(a, r);
    return `<div data-arbol="${esc(id)}" style="${conTitulo ? 'border:1px solid #1f6feb;border-radius:8px;padding:8px 10px;' : ''}margin-bottom:8px">
        <div style="display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap">${conTitulo ? `<strong style="color:#f0f6fc">${esc(a.nombre)}</strong>` : '<span class="presu-rotulo">Estado de la secci&oacute;n</span>'}
          <span data-arb-estado="${esc(est)}" role="status" style="color:${{ resuelta: '#3fb950', por_revisar: '#d29922' }[est] || '#8b949e'}">${esc(ETIQUETA_ESTADO[est])}</span></div>
        ${r.error ? `<div role="alert" style="color:#f85149">${esc(r.error)}</div>` : ''}
        ${q1}${lista}${q2}${q3}</div>`;
  };
  // T2 (recorrido a 375, Ares #30856): antes cada esencial ocupaba su linea con su boton No aplica (5 lineas arriba de
  // cada seccion). Ahora: una linea de chips con el estado (simbolo + texto para lectores) y No aplica en un plegable.
  const SIMBOLO_ESTADO = { resuelta: '&#10003;', no_aplica: '&ndash;', por_revisar: '!', en_curso: '&hellip;', pendiente: '&#9675;' };
  const esencialesHTML = (estados) => `<div data-presu-esenciales style="margin:2px 0 8px">
      <div style="display:flex;flex-wrap:wrap;gap:6px;align-items:center">
      <span class="presu-rotulo">Esenciales</span>
      ${estados.map((e) => {
        const color = { resuelta: '#3fb950', no_aplica: '#8b949e', por_revisar: '#d29922', en_curso: '#58a6ff', pendiente: '#c9d1d9' }[e.estado];
        return `<span data-presu-esencial="${esc(e.id)}" data-estado="${esc(e.estado)}" title="${esc(e.nombre)}: ${esc(ETIQUETA_ESTADO[e.estado])}${e.motivo ? ' (' + esc(e.motivo) + ')' : ''}" style="border:1px solid #30363d;border-radius:999px;padding:2px 10px;color:${color};white-space:nowrap"><span aria-hidden="true">${SIMBOLO_ESTADO[e.estado]}</span> ${esc(e.nombre)}<span class="presu-sr"> ${esc(ETIQUETA_ESTADO[e.estado])}</span></span>`;
      }).join('')}
      </div>
      <details data-presu-noaplica-plegado${noAplicaEditando ? ' open' : ''} style="margin-top:4px">
        <summary style="cursor:pointer;color:#8b949e">Alguna secci&oacute;n no aplica en esta obra</summary>
        <div style="display:flex;flex-wrap:wrap;gap:6px;align-items:center;margin-top:6px">
        ${estados.map((e) => {
          if (noAplicaEditando === e.id) {
            return `<span style="display:inline-flex;gap:4px;align-items:center;flex-wrap:wrap">${esc(e.nombre)}:
              <input class="form-input" data-presu-noaplica-motivo="${esc(e.id)}" placeholder="&iquest;Por qu&eacute; no aplica?" maxlength="120" style="max-width:220px" value="${esc(noAplica.get(e.id) || '')}">
              <button type="button" class="btn-ghost btn-sm" data-presu-noaplica-guardar="${esc(e.id)}">Guardar</button>
              <button type="button" class="btn-ghost btn-sm" data-presu-noaplica-cancelar>Cancelar</button></span>`;
          }
          if (e.estado === 'no_aplica') return `<span style="color:#8b949e">${esc(e.nombre)} no aplica${e.motivo ? ' (' + esc(e.motivo) + ')' : ''} <button type="button" class="presu-quitar" data-presu-noaplica-deshacer="${esc(e.id)}" title="Deshacer" aria-label="Deshacer no aplica de ${esc(e.nombre)}">&times;</button></span>`;
          return e.estado !== 'resuelta' ? `<button type="button" class="btn-ghost btn-sm" data-presu-noaplica="${esc(e.id)}">${esc(e.nombre)} no aplica</button>` : '';
        }).join('')}
        </div>
      </details></div>`;

  // El bloque Cubierta: una sola entrada que agrupa las secciones de origen de la cubierta (sin fusionar ids ni precios).
  const cubiertaHTML = () => {
    if (!esDemoMaderinco() || !seccionesDeCubierta(plantillaDemo).size) return '';
    const est = estadoDelConjunto(cubierta);
    const boton = (attr, texto, activo) => `<button type="button" ${attr} aria-pressed="${activo}" style="${chip};${activo ? 'border-color:#1f6feb;color:#79c0ff' : ''}">${texto}</button>`;
    // MAD-IMPLEMENTAR-DISENO-T1 (Josse #42796): el tipo en TARJETAS con su precio y una muestra de color; el area se
    // escribe una vez y cada tarjeta dice cuanto vale en esa area, asi se compara antes de elegir.
    const tarjetas = tarjetasDeTipos(plantillaDemo);
    const area = cantidadExplicita(cubierta.area);
    const tarjetaHTML = (t) => {
      const unidad = esc(unidadDeLista(t.unidad) || String(t.unidad || '').toLowerCase()).replace(/^m2$/, 'm&sup2;').replace(/^gnral$/, 'global');
      const material = MATERIAL[t.codigo] && MATERIAL[t.codigo].toLowerCase() !== t.nombre.toLowerCase() ? MATERIAL[t.codigo] : '';
      const precio = t.precio ? `${fmtCOP(t.precio)} por ${unidad}` : t.desde ? `desde ${fmtCOP(t.desde)} por ${unidad}` : 'sin precio en tu plantilla';
      const valor = t.precio && area !== null ? `<span class="presu-tipo-valor">${fmtCOP(valorDeLinea(area, t.precio))}</span>`
        : !t.unica && t.servidas ? `<span class="presu-tipo-valor presu-tipo-varias">${t.servidas} partidas para elegir</span>` : '';
      return `<button type="button" class="presu-tipo" data-presu-cub-tipo="${esc(t.codigo)}" role="radio" aria-checked="${cubierta.tipo === t.codigo}">
          <span class="presu-tipo-muestra" data-material="${esc(t.codigo)}" aria-hidden="true"></span>
          <span class="presu-tipo-nombre">${esc(t.nombre)}</span>
          ${material ? `<span class="presu-tipo-material">${esc(material)}</span>` : ''}
          <span class="presu-tipo-precio">${precio}</span>${valor}
        </button>`;
    };
    const q1 = `<div class="presu-filtros" data-presu-cub-q1><span class="presu-rotulo">1. &iquest;Qu&eacute; tipo de cubierta buscas?</span>
        <div class="presu-cub-area"><label for="presu-cub-area">&Aacute;rea de la cubierta (m&sup2;)</label>
          <input id="presu-cub-area" class="form-input presu-num" inputmode="decimal" data-presu-cub-area value="${esc(cubierta.area || '')}" placeholder="ej. 104,66"></div>
        <div class="presu-tipos" role="radiogroup" aria-label="Tipo de cubierta">${tarjetas.map(tarjetaHTML).join('')}</div>
        ${boton('data-presu-cub-otro', 'Otro tipo', false)}
        ${boton('data-presu-cub-tipo="no_definido"', 'Todav&iacute;a no definido', cubierta.tipo === 'no_definido')}</div>`;
    let candidatos = '';
    const elegida = tarjetas.find((t) => t.codigo === cubierta.tipo);
    if (elegida?.unica) {
      // Una sola partida servida para el tipo: no hay lista que elegir; se dice que quedo puesta, o que falta el area.
      const p = partidaDelTipo(armador.secciones.find((x) => numeroDeSeccionCliente(x) === pasoCapitulo));
      candidatos = p
        ? `<div class="presu-cub-puesta" data-presu-cub-puesta role="status">En tu presupuesto: <strong>${esc(p.titulo)}</strong> &middot; ${esc(fmtNum(p.cantidad))} m&sup2; &middot; ${fmtCOP(valorDeLinea(p.cantidad, p.precioUnit))}</div>`
        : '<div class="presu-cub-puesta" data-presu-cub-pide-area>Escribe el &aacute;rea de la cubierta para ver el valor y ponerlo en tu presupuesto.</div>';
    } else if (cubierta.tipo && cubierta.tipo !== 'no_definido') {
      const { candidatos: cs, faltantes } = candidatosDe(plantillaDemo, cubierta.tipo);
      const alcance = (v) => v.alcance === 'compuesto_con_estructura_explicita' ? ' <span class="presu-partida-sec">&middot; incluye estructura</span>' : ' <span class="presu-partida-sec">&middot; alcance por confirmar</span>';
      candidatos = `<div class="presu-partidas" data-presu-cub-candidatos>
          ${agruparVariantes(cs.map((c) => c.item), { medidas: true }).map(({ item, fuentes }) => {
            // Ares #30267: los candidatos del mismo grupo (iguales, o la misma partida con otra area de proyecto) van en
            // UNA fila con selector de fuente; agrega el fuenteId elegido.
            const variante = cs.find((c) => c.item === item).variante;
            return partidaHTML({ ...item, cantidadRef: null }, `data-presu-cub-agregar="${esc(variante.fuenteId)}"`, alcance(variante), fuentesHTML(fuentes));
          }).join('')}
        </div>
        ${faltantes.length ? `<div class="presu-rotulo" data-presu-cub-faltan>${faltantes.length} variante${faltantes.length === 1 ? '' : 's'} de este tipo no est&aacute;n en tu plantilla.</div>` : ''}`;
    }
    const q2 = cubierta.tipo && cubierta.tipo !== 'no_definido' ? `<div class="presu-filtros" data-presu-cub-q2><span class="presu-rotulo">2. &iquest;C&oacute;mo queda resuelta la estructura? (obligatoria)</span>
        ${Q2.map((o) => boton(`data-presu-cub-q2-op="${esc(o.codigo)}"`, esc(o.texto), cubierta.q2 === o.codigo)).join('')}</div>` : '';
    const comps = cubierta.q3 ? complementosDe(plantillaDemo, cubierta.q3) : [];
    const q3 = cubierta.tipo && cubierta.tipo !== 'no_definido' ? `<div class="presu-filtros" data-presu-cub-q3><span class="presu-rotulo">3. &iquest;Quieres revisar alg&uacute;n complemento? (opcional)</span>
        ${Q3.map((o) => boton(`data-presu-cub-q3-op="${esc(o.codigo)}"`, esc(o.texto), cubierta.q3 === o.codigo)).join('')}</div>
        ${comps.length ? `<div class="presu-partidas" data-presu-cub-complementos>${comps.map(({ item }) => partidaHTML({ ...item, cantidadRef: null }, `data-presu-cub-agregar="${esc(item.fuenteId)}"`)).join('')}</div>` : ''}` : '';
    const color = est.codigo === 'registrado' ? '#3fb950' : est.codigo === 'doble_conteo' ? '#f85149' : '#d29922';
    // #36553: Cubierta va DENTRO de la seccion del cliente que le toca (esencialesPorSeccion); seguir a la siguiente lo
    // hace la barra de secciones del cliente, asi que el bloque ya no trae su propio "Listo con Cubierta".
    const falta = cubiertaRespondida(cubierta) ? '' : '<div data-presu-cub-falta style="color:#8b949e;margin-top:8px">Responde las 3 preguntas de Cubierta antes de dar la secci&oacute;n por lista.</div>';
    return `<div data-presu-cubierta style="background:#161b22;border:1px solid #1f6feb;border-radius:8px;padding:10px 12px;margin-bottom:12px">
      <div style="display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap">
        <strong style="color:#f0f6fc">Cubierta</strong>
        <span data-presu-cub-estado="${esc(est.codigo)}" role="status" style="color:${color}">${esc(est.frase)}</span>
      </div>
      ${cubierta.revisar ? '<div data-presu-cub-revisar style="color:#d29922;margin-top:4px">Cambiaste el tipo: revisa las partidas de cubierta que ya agregaste.</div>' : ''}
      ${cubierta.error ? `<div data-presu-cub-error role="alert" style="color:#f85149;margin-top:4px">${esc(cubierta.error)}</div>` : ''}
      ${q1}${candidatos}${q2}${q3}${falta}
    </div>`;
  };
  const chip = 'border:1px solid #30363d;border-radius:999px;background:transparent;color:#c9d1d9;font-size:0.875rem;padding:3px 10px;max-width:100%;text-align:left;cursor:pointer';

  // MADERINCO-ARMADOR-DISENO-T1 (front-panel-claridad): una partida es una FILA, no un chip con el parrafo adentro:
  // nombre (2 lineas como maximo), unidad y precio alineados, cantidad y "Agregar". La descripcion completa y la fuente
  // quedan a demanda en un <details> (teclado y toque). attr arma el boton Agregar.
  const partidaHTML = (it, attr, extra = '', pie = '') => `<div class="presu-partida" data-presu-partida>
      <span class="presu-partida-nombre">${esc(it.descripcion)}${extra}</span>
      <span class="presu-partida-unidad">${esc(unidadDeLista(it.unidad) || String(it.unidad || '').toLowerCase())}</span>
      <span class="presu-partida-precio">${it.precioRef ? fmtCOP(it.precioRef) : '&mdash;'}</span>
      <input class="form-input presu-num presu-partida-cant" inputmode="decimal" aria-label="Cantidad de ${esc(it.descripcion)}" placeholder="${it.cantidadRef > 0 ? fmtNum(it.cantidadRef) : 'cant.'}">
      <button type="button" class="btn-ghost btn-sm presu-partida-agregar" ${attr}>Agregar</button>
      <details class="presu-partida-det"><summary>Detalle</summary><div>${esc(it.descripcion)}${it.fuente ? ' &middot; ' + esc(it.fuente) : ''}</div>${pie ? `<div class="presu-partida-fuentes" data-presu-fuentes>${pie}</div>` : ''}</details>
    </div>`;

  // MADERINCO-VARIANTES-PANTALLA-T1: registros iguales (Atenea #30203) y, en el arbol de Cubierta, la misma solucion con
  // otras medidas y el mismo precio, en UNA fila. Ares #30338 (Josse): SIN selector; las fuentes se leen en el Detalle.
  const fuentesHTML = (fuentes) => fuentes.length < 2 ? ''
    : `Fuentes (${fuentes.length}): ${fuentes.map((f) => esc(nombreDeFuente(f))).join(' &middot; ')}`;

  // La cantidad escrita en la fila de la partida (si hay una valida); si no, la de referencia de la plantilla.
  const conCantidad = (item, $btn) => {
    const q = parsearNumeroColombiano($btn?.closest('[data-presu-partida]')?.querySelector('.presu-partida-cant')?.value || '');
    return Number.isFinite(q) && q > 0 ? { ...item, cantidad: q } : item;
  };

  // #36553: arriba de una seccion del cliente, los arboles de Atenea que le tocan (Cubierta con su bloque; las demas con
  // su arbol). Si la plantilla no trae un grupo de esa esencial, no se pinta nada.
  const arbolDeSeccionHTML = (id) => {
    if (id === 'cubierta') return cubiertaHTML();
    const p = (plantillaDemo?.secciones || []).find((s) => esencialDe(s) === id);
    return p ? arbolHTML(p) : '';
  };

  // #36553 punto 2: "Agregar otro item del catalogo" en cada linea. Abre un buscador con filtro por grupo del catalogo
  // (las agrupaciones de la plantilla; en pantalla nunca "seccion"). Lo que se agrega entra a ESTA seccion del cliente.
  const otroDelCatalogoHTML = (l, i) => {
    const k = `${pasoCapitulo}|${i}`;
    if (otroAbierto !== k) {
      return `<button type="button" class="btn-ghost btn-sm" data-presu-otro-abrir="${k}" style="font-size:14px;width:auto;margin-top:6px">+ Agregar otro &iacute;tem del cat&aacute;logo</button>`;
    }
    const grupos = (plantillaDemo?.secciones || []).filter((s) => (s.items || []).length);
    return `<div data-presu-otro="${k}" style="margin-top:8px;padding-top:8px;border-top:1px dashed #30363d">
      <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end">
        <label style="display:flex;flex-direction:column;gap:4px;font-size:14px;color:#8b949e;flex:1 1 200px;min-width:0">Buscar en el cat&aacute;logo
          <input type="search" class="form-input" data-presu-otro-q autocomplete="off" placeholder="ej. teja, deck, grifer&iacute;a" value="${esc(otroQ)}"></label>
        <label style="display:flex;flex-direction:column;gap:4px;font-size:14px;color:#8b949e;flex:1 1 200px;min-width:0">Grupo del cat&aacute;logo
          <select class="form-input" data-presu-otro-grupo><option value="">Todos los grupos</option>${grupos.map((g) => `<option value="${esc(g.clave)}"${g.clave === otroGrupo ? ' selected' : ''}>${esc(g.nombre)} (${g.items.length})</option>`).join('')}</select></label>
        <button type="button" class="btn-ghost btn-sm" data-presu-otro-cerrar style="font-size:14px;width:auto">Cerrar</button>
      </div>
      <div data-presu-otro-res role="status" aria-live="polite" style="margin-top:6px"></div></div>`;
  };

  // #36553 (Josse 28-oct): UNA sola estructura en el armador demo, las secciones del cliente en su orden (10 y 68 lineas).
  // Arriba, el estado de las esenciales; en la seccion que les toca, sus arboles; en cada linea, el catalogo.
  const espacioHTML = () => {
    if (!esDemoMaderinco()) return '';
    if (asistenteActivo()) return asistenteEspacioHTML();
    const yaPuestos = new Set(armador.secciones.flatMap((s) => (s.items || []).map((i) => i.plantillaItemId)).filter(Boolean));
    const conPartidas = new Set(armador.secciones.filter((s) => (s.items || []).length).map(numeroDeSeccionCliente).filter((n) => n !== null));
    const arboles = esencialesPorSeccion(lineasCliente, ESENCIALES);
    return `<div data-presu-capitulos data-presu-capitulos-fuente="${fuenteLineasCliente}" style="background:#161b22;border:1px solid #1f6feb;border-radius:8px;padding:10px 12px;margin-bottom:12px">
      ${esencialesHTML(estadosActuales())}
      ${recorridoCapitulosHTML(recorridoPorCapitulos(plantillaDemo, lineasCliente), {
        paso: pasoCapitulo,
        yaPuestos,
        conPartidas,
        agregarAttr: (it) => `data-presu-sug-nueva="${esc(it.seccionClave)}|${esc(it.id)}|${pasoCapitulo}"`,
        // MAD-IMPLEMENTAR-DISENO-T1 grupo 2b: la casilla de una partida ya puesta la quita; la fila dice su cantidad.
        quitarAttr: (it) => `data-presu-sug-quitar="${esc(it.id)}"`,
        cantidadDe: (it) => armador.secciones.flatMap((s) => s.items || []).find((i) => i.plantillaItemId === it.id)?.cantidad ?? null,
        // Grupo 4: el item estandar de la linea y el tipo elegido en esta pantalla (undefined = no eligio).
        estandarDeLinea: (l) => estandarDeLineas.get(l.linea) || null,
        tipoDe: (l) => tipoPorLinea.get(l.linea),
        arribaHTML: (cap) => (arboles.get(cap.numero) || []).map(arbolDeSeccionHTML).join(''),
        otroHTML: (l, i) => (l.estado === 'dentro_de_otro' ? '' : otroDelCatalogoHTML(l, i))
      })}</div>`;
  };

  // MAD-ASISTENTE-UN-PASO-T1: el item de la plantilla por su libro:hoja:celda (el estandar de una linea apunta a items que
  // la lista del cliente no le pone) y el item estandar de cada linea.
  const asiItemDeRef = (ref) => {
    for (const s of plantillaDemo?.secciones || []) { const it = s.items.find((i) => i.fuenteId === ref); if (it) return { ...it, seccionClave: s.clave }; }
    return null;
  };
  const asiEst = (l) => (l ? estandarDeLineas.get(l.linea) || null : null);
  const asiRecorrido = () => recorridoPorCapitulos(plantillaDemo, lineasCliente);
  const asiPantallas = () => pantallasDelAsistente(asiRecorrido(), asiEst, asiItemDeRef);
  const pantallaActual = () => { const ps = asiPantallas(); asi.idx = Math.min(Math.max(0, asi.idx), ps.length - 1); return ps[asi.idx]; };

  // El tipo elegido en la pantalla Tipo entra al presupuesto con la cantidad escrita; cambiarlo saca el anterior de esa
  // linea y pone el nuevo con la MISMA cantidad (la pregunta de Josse en #42796). Sin cantidad, no se pone nada.
  const asiAplicarTipo = (n) => {
    const cap = asiRecorrido().capitulos.find((c) => c.numero === n);
    const lt = lineaDelTipo(cap, asiEst, asiItemDeRef);
    if (!lt) return;
    const tarjetas = tarjetasDeLinea(lt, asiEst(lt), asiItemDeRef);
    const ids = new Set(tarjetas.map((t) => t.item.id));
    armador.secciones.forEach((s) => { s.items = (s.items || []).filter((i) => !ids.has(i.plantillaItemId)); });
    const st = asi.tipo.get(n) || {};
    const sel = tarjetas.find((t) => t.tipo === st.tipo);
    const q = cantidadExplicita(st.cantidad || '');
    if (!sel || q === null) return;
    agregarDesdePlantilla(sel.item.seccionClave, sel.item.id, null, n);
    const it = armador.secciones.flatMap((s) => s.items || []).find((i) => i.plantillaItemId === sel.item.id);
    if (it) it.cantidad = q;
  };

  // PR 3: una fila de Cantidades al presupuesto. Saca lo que esa linea tuviera puesto y pone la opcion elegida con la
  // cantidad escrita; sin cantidad o sin marcar, no queda nada de esa linea.
  const asiAplicarFila = (n, linea) => {
    const l = asiRecorrido().capitulos.find((c) => c.numero === n)?.lineas.find((x) => x.linea === linea);
    if (!l) return;
    const ops = opcionesDeLinea(l, asiEst(l), asiItemDeRef);
    const ids = new Set(ops.map((o) => o.item.id));
    armador.secciones.forEach((s) => { s.items = (s.items || []).filter((i) => !ids.has(i.plantillaItemId)); });
    const ui = asi.filas.get(`${n}|${linea}`) || {};
    const o = ops[ui.op ?? 0];
    const q = cantidadExplicita(ui.cantidadTexto || '');
    if (!(l.obligatoria || ui.incluida) || q === null || !o) return;
    agregarDesdePlantilla(o.item.seccionClave, o.item.id, null, n);
    const it = armador.secciones.flatMap((s) => s.items || []).find((i) => i.plantillaItemId === o.item.id);
    if (it) it.cantidad = q;
  };
  // La fila como se pinta: lo puesto en el presupuesto manda; si no hay nada, lo que el asesor dejo marcado o escrito.
  const asiFila = (n, l, i, puestos) => {
    const ops = opcionesDeLinea(l, asiEst(l), asiItemDeRef);
    const ui = asi.filas.get(`${n}|${l.linea}`) || {};
    const e = filaEnPresupuesto(ops, puestos, ui.op);
    const op = e.puesta ? e.op : (ui.op ?? e.op);
    const { distingue, cortas } = etiquetasCortas(ops, asiEst(l)?.plantilla);
    const clave = `${n}|${l.linea}`;
    return {
      i, linea: l.linea, obligatoria: !!l.obligatoria, opciones: ops, op, distingue, cortas,
      // MAD-LAPIZ-ITEM-T1: el lapiz solo si la opcion elegida es un item estandar (lo que el editor sabe guardar).
      editable: !!(ops[op]?.estandar && lineaDeLectura(l.linea)), editando: asi.editor?.clave === clave, guardado: asi.guardado === clave,
      incluida: !!(l.obligatoria || e.puesta || ui.incluida),
      cantidadTexto: ui.cantidadTexto ?? (e.puesta && e.cantidad != null ? fmtNum(e.cantidad) : ''),
      // El valor sale de la partida puesta (su precio: el del catalogo o el de una replica), igual que el total.
      valor: e.puesta && ops[op] ? (() => { const it = armador.secciones.flatMap((s) => s.items || []).find((x) => x.plantillaItemId === ops[op].item.id); return it ? Math.round((Number(it.cantidad) || 0) * (Number(it.precioUnit) || 0)) : 0; })() : 0
    };
  };
  const valorDeSeccion = (n) => armador.secciones.filter((s) => numeroDeSeccionCliente(s) === n)
    .reduce((a, s) => a + (s.items || []).reduce((b, i) => b + Math.round((Number(i.cantidad) || 0) * (Number(i.precioUnit) || 0)), 0), 0);

  const asistenteEspacioHTML = () => {
    const rec = asiRecorrido();
    const ps = pantallasDelAsistente(rec, asiEst, asiItemDeRef);
    asi.idx = Math.min(Math.max(0, asi.idx), ps.length - 1);
    const p = ps[asi.idx];
    const cap = rec.capitulos.find((c) => c.numero === p.n);
    // Agregar desde el buscador entra a pasoCapitulo: se mantiene en la seccion de la pantalla.
    if (p.n) pasoCapitulo = p.n;
    const conPartidas = new Set(armador.secciones.filter((s) => (s.items || []).length).map(numeroDeSeccionCliente).filter((n) => n !== null));
    const lt = cap ? lineaDelTipo(cap, asiEst, asiItemDeRef) : null;
    let cuerpo = '';
    if (p.paso === 'tipo') {
      const st = asi.tipo.get(p.n) || {};
      const tarjetas = tarjetasDeLinea(lt, asiEst(lt), asiItemDeRef).map((t) => ({ ...t, precio: Number(t.item.precioRef) || t.precio }));
      const sel = tarjetas.find((t) => t.tipo === st.tipo);
      const q = cantidadExplicita(st.cantidad || '');
      cuerpo = pantallaTipoHTML({ linea: lt.linea, tarjetas, elegido: st.tipo, cantidad: st.cantidad, subtotal: sel && q !== null && sel.precio ? valorDeLinea(q, sel.precio) : null });
    } else if (p.paso === 'cantidades') {
      const puestos = new Map(armador.secciones.flatMap((s) => (s.items || []).filter((i) => i.plantillaItemId).map((i) => [i.plantillaItemId, i.cantidad])));
      cuerpo = pantallaCantidadesHTML(cap.lineas.map((l, i) => ({ l, i }))
        .filter(({ l }) => claseDeLinea(l, asiEst(l)) === 'cantidad' && l !== lt)
        .map(({ l, i }) => asiFila(p.n, l, i, puestos)));
    } else if (p.paso === 'revision') {
      const conIndice = cap.lineas.map((l, i) => ({ l, i }));
      const notaDe = (l) => l.notaPorConfirmar || (l.estado === 'sin_item_en_plantilla' ? 'Tu plantilla no trae el ítem de esta línea.' : '');
      cuerpo = pantallaRevisionHTML({
        chequeo: conIndice.filter(({ l }) => claseDeLinea(l, asiEst(l)) === 'chequeo').map(({ l, i }) => ({ i, linea: l.linea, nota: notaDe(l), valor: asi.chequeo.get(`${p.n}|${l.linea}`) })),
        dentro: conIndice.filter(({ l }) => claseDeLinea(l, asiEst(l)) === 'dentro').map(({ l }) => ({ linea: l.linea, nota: l.items[0] ? String(l.items[0].descripcion || '').slice(0, 90) : '' }))
      });
    } else {
      // PR 4: lo que no es de una seccion del cliente (por clasificar de una replica, adicionales, secciones propias)
      // tambien suma: va en su fila y se edita debajo, con la lista de siempre.
      const fuera = armador.secciones.filter((s) => numeroDeSeccionCliente(s) === null).map((s) => ({
        numero: null, nombre: s.nombre,
        valor: (s.items || []).reduce((b, i) => b + Math.round((Number(i.cantidad) || 0) * (Number(i.precioUnit) || 0)), 0),
        pendientes: (s.items || []).filter((i) => i.porConfirmar).length
      }));
      cuerpo = pantallaResumenHTML({
        secciones: [...resumenPorSeccion(rec, valorDeSeccion, (n, linea) => asi.chequeo.get(`${n}|${linea}`), asiEst).map((s) => ({ ...s, noAplica: asi.noAplica.has(s.numero) })), ...fuera],
        aiu: calcularAiu(armador), utilidadPct: armador.utilidadPct, ivaPct: armador.ivaUtilidadPct
      });
    }
    const corto = (n) => nombreCortoDeCapitulo(rec.capitulos.find((c) => c.numero === n));
    // data-presu-capitulos: las filas de siempre (casillas, tipo del grupo 4, buscador) se escuchan dentro de ese bloque.
    return `<div data-presu-capitulos data-presu-capitulos-fuente="${fuenteLineasCliente}">${asistenteHTML({
      pantallas: ps, idx: asi.idx, secciones: rec.capitulos.map((c) => ({ numero: c.numero, nombre: c.nombre })),
      noAplica: asi.noAplica, conPartidas, cuerpoHTML: cuerpo, total: calcularAiu(armador).total,
      siguienteTexto: textoSiguiente(ps, asi.idx, asi.noAplica, corto)
    })}</div>`;
  };

  // Un item de la plantilla al presupuesto. #36553: en el demo entra a una SECCION DEL CLIENTE: la pedida (la de la linea
  // o la de la pantalla), o la de la linea que usa ese item; si ninguna lo usa, la que esta en pantalla. Si esa seccion
  // todavia no esta, entra en su lugar (por numero; las propias quedan despues). Fuera del demo, a su seccion de siempre.
  const agregarDesdePlantilla = (clave, pid, $btn = null, numero = null) => {
    const p = plantillaDemo && plantillaDemo.secciones.find((x) => x.clave === clave);
    const it = p?.items.find((x) => x.id === pid);
    if (!p || !it) return null;
    if (esDemoMaderinco()) {
      const n = numero ?? seccionDeCadaItem(recorridoPorCapitulos(plantillaDemo, lineasCliente)).get(pid) ?? pasoCapitulo;
      let s = armador.secciones.find((x) => numeroDeSeccionCliente(x) === n);
      if (!s) {
        s = { nombre: NOMBRES_DE_CAPITULO[n], seccionCliente: n, items: [] };
        const pos = armador.secciones.findIndex((x) => (numeroDeSeccionCliente(x) ?? Number.MAX_SAFE_INTEGER) > n);
        if (pos === -1) armador.secciones.push(s); else armador.secciones.splice(pos, 0, s);
      }
      s.items.push(conCantidad(itemDesdePlantilla(it), $btn));
      return { nombre: s.nombre };
    }
    const ya = armador.secciones.find((s) => seccionDePlantilla(s)?.clave === clave);
    if (ya) { ya.items.push(conCantidad(itemDesdePlantilla(it), $btn)); return p; }
    const nueva = { nombre: p.nombre, plantillaClave: p.clave, items: [conCantidad(itemDesdePlantilla(it), $btn)] };
    const ordenDe = (s) => seccionDePlantilla(s)?.orden ?? Number.MAX_SAFE_INTEGER;
    const pos = armador.secciones.findIndex((s) => ordenDe(s) > p.orden);
    if (pos === -1) armador.secciones.push(nueva); else armador.secciones.splice(pos, 0, nueva);
    return p;
  };

  // Los resultados del buscador de una linea se pintan aparte (no se repinta el campo: no se pierde el foco).
  const pintarOtro = (aviso = '') => {
    const $c = $sec.querySelector('[data-presu-otro]');
    const $r = $c?.querySelector('[data-presu-otro-res]');
    if (!$r || !plantillaDemo) return;
    const { resultados, total } = buscarEnCatalogo(plantillaDemo, otroQ, { grupo: otroGrupo, max: otroMax });
    // Registros iguales en UNA fila, con sus fuentes en el Detalle (MADERINCO-VARIANTES-PANTALLA-T1, Atenea #30203).
    const grupoDe = new Map(resultados.map(({ seccion, item }) => [item, seccion]));
    const lista = resultados.length ? `<div class="presu-partidas">${agruparVariantes(resultados.map((r) => r.item)).map(({ item, fuentes }) =>
      partidaHTML(item, `data-presu-otro-agregar="${esc(grupoDe.get(item).clave)}|${esc(item.id)}"`, ` <span class="presu-partida-sec">${esc(grupoDe.get(item).nombre)}</span>`, fuentesHTML(fuentes))).join('')}</div>` : '';
    const pie = !otroQ.trim() && !otroGrupo ? '<div style="font-size:14px;color:#8b949e">Escribe una palabra o elige un grupo del cat&aacute;logo.</div>'
      : total === 0 ? '<div style="font-size:14px;color:#8b949e">Nada con esas palabras en el cat&aacute;logo. Prueba otra palabra u otro grupo.</div>'
      : total > resultados.length ? `<button type="button" class="btn-ghost btn-sm" data-presu-otro-mas style="font-size:14px;width:auto">Ver m&aacute;s (${total - resultados.length})</button>` : '';
    $r.innerHTML = (aviso ? `<div data-presu-otro-aviso style="font-size:14px;color:#3fb950;margin-bottom:4px">${esc(aviso)}</div>` : '') + lista + pie;
    const n = Number(String(otroAbierto).split('|')[0]);
    $r.querySelectorAll('[data-presu-otro-agregar]').forEach((b) => b.addEventListener('click', () => {
      const [clave, pid] = String(b.dataset.presuOtroAgregar).split('|');
      const s = agregarDesdePlantilla(clave, pid, b, n);
      if (!s) return;
      repintar();
      pintarOtro('Agregado a ' + s.nombre + '.');
      $sec.querySelector('[data-presu-otro-q]')?.focus();
    }));
    $r.querySelector('[data-presu-otro-mas]')?.addEventListener('click', () => { otroMax += 24; pintarOtro(); });
  };

  const repintar = () => {
    armador.utilidadPct = num($util.value);
    armador.areaM2 = num($area.value);
    const a = calcularAiu(armador);
    $res.innerHTML =
      filaResumen('Subtotal', fmtCOP(a.subtotal)) +
      filaResumen('Utilidad ' + fmtNum(armador.utilidadPct) + '%', fmtCOP(a.utilidad)) +
      filaResumen('IVA ' + fmtNum(armador.ivaUtilidadPct) + '% (sobre la utilidad)', fmtCOP(a.ivaUtilidad)) +
      (a.adicionales && a.adicionales.total > 0 ? filaResumen('Adicionales (con su AIU)', fmtCOP(a.adicionales.total), { color: '#8b949e' }) : '') +
      filaResumen('Total', fmtCOP(a.total), { grande: true, color: '#3fb950', borde: true }) +
      (a.valorM2 != null ? filaResumen('Por m&sup2;', fmtCOP(a.valorM2), { color: '#8b949e' }) : '');
    const $barraTotal = $host.querySelector('[data-presu-barra-total]');
    if ($barraTotal) $barraTotal.textContent = fmtCOP(a.total);
    // Skill v0.4 (nada tapado, tambien al cargar): con el total en $0 la barra fija no dice nada y tapaba lo que quedara
    // en el borde de abajo (medido a 375: los "No aplica" de la franja Esenciales). Aparece con la primera partida.
    $host.querySelector('[data-presu-barra]')?.classList.toggle('presu-barra-vacia', !(a.total > 0));

    // MAD-ASISTENTE-UN-PASO-T1: en el asistente la lista de siempre no se pinta; el Resumen dice lo que suma cada seccion.
    // REPLICAR-PRESUPUESTO-T1: arriba, de donde salio el presupuesto replicado y cuanto falta confirmar.
    const soloElPaso = asistenteActivo();
    // PR 4: en el Resumen del asistente se editan aqui las secciones que no son del cliente (por clasificar, adicionales).
    const enResumen = soloElPaso && pantallaActual().paso === 'resumen';
    const pendientes = armador._replica ? armador.secciones.reduce((n, s) => n + (s.items || []).filter((i) => i.porConfirmar).length, 0) : 0;
    $sec.innerHTML = (esDemoMaderinco() ? espacioHTML() : '') + (armador._replica
      ? `<div data-presu-replica role="status" class="presu-rep-aviso">${esc(fraseDeLaReplica(armador._replica))}
          <div data-presu-replica-pendientes class="${pendientes ? 'presu-rep-pendiente' : 'presu-rep-listo'}" style="margin-top:6px">${pendientes ? `Faltan ${pendientes} cantidades por confirmar con el cliente.` : 'Todas las cantidades est&aacute;n confirmadas.'}</div></div>`
      : '') + ((soloElPaso && !(enResumen && armador.secciones.some((s) => numeroDeSeccionCliente(s) === null))) ? '' : (armador.secciones.length === 0
      ? (esDemoMaderinco()
        ? `<div class="presu-vacio" data-presu-vacio-plantilla><p>Tu presupuesto empieza vac&iacute;o: agrega partidas desde las secciones de tu cliente, arriba, o crea una secci&oacute;n propia.</p></div>`
        : `<div class="presu-vacio"><div class="icono">&#128208;</div>
          <p>Un presupuesto se arma por secciones: cocina, ba&ntilde;o, closets.</p>
          <button type="button" class="btn-primary btn-sm" data-presu-primera-seccion>Crear la primera secci&oacute;n</button></div>`)
      : armador.secciones.map((s, si) => (soloElPaso && numeroDeSeccionCliente(s) !== null) ? '' : `
        <div style="background:#161b22;border:1px solid #30363d;border-radius:8px;padding:10px;margin-bottom:8px">
          <div class="presu-arm-sec-cab">
            <div style="font-size:0.875rem;color:#c9d1d9;font-weight:600">${esc(s.nombre)}${s.porClasificar ? ` <span data-presu-por-clasificar class="presu-rep-pendiente" style="font-weight:400">por clasificar: no est&aacute; en tu plantilla</span>` : ''}${esDemoMaderinco() ? ` <button type="button" class="presu-quitar" data-presu-ren-sec="${si}" title="Cambiar el nombre" aria-label="Cambiar el nombre de ${esc(s.nombre)}" style="font-size:0.875rem">&#9998;</button>` : ''}</div>
            ${(s.items || []).length === 0
              ? `<button type="button" class="presu-quitar" data-presu-del-sec="${si}" title="Quitar esta secci&oacute;n" aria-label="Quitar la secci&oacute;n ${esc(s.nombre)}">&times;</button>`
              : ''}
          </div>
          ${(s.items || []).length === 0
            ? `<div style="font-size:0.875rem;color:#6e7681">${esDemoMaderinco() ? 'Sin items todav&iacute;a.' : 'Sin productos todav&iacute;a.'}</div>`
            : s.items.map((i, ii) => `<div class="presu-arm-item" style="font-size:0.875rem;color:#c9d1d9">
                <span style="min-width:0;display:flex;flex-wrap:wrap;align-items:center;gap:4px 6px">${esc(i.titulo)}
                  <span style="display:inline-flex;align-items:center;gap:4px;color:#8b949e">
                    <input data-presu-item-cant="${si}:${ii}" class="form-input presu-num presu-item-edit" inputmode="decimal" aria-label="Cantidad de ${esc(i.titulo)}" value="${fmtNum(i.cantidad)}">${i.unidad ? ' ' + esc(i.unidad) : ''} &times;
                    <input data-presu-item-precio="${si}:${ii}" class="form-input presu-num presu-item-edit presu-item-precio" inputmode="decimal" aria-label="Precio de ${esc(i.titulo)}" value="${fmtNum(i.precioUnit)}">
                    ${i.porConfirmar ? `<button type="button" class="btn-ghost btn-sm" data-presu-confirmar="${si}:${ii}" aria-label="Confirmar la cantidad de ${esc(i.titulo)}" style="font-size:14px;min-height:44px"><span class="presu-rep-pendiente">Por confirmar</span></button>` : ''}
                    ${i.estandar ? `<span data-presu-item-estandar class="${i.estandar.sugerido ? 'presu-rep-pendiente' : 'presu-rep-antes'}">${i.estandar.sugerido ? 'tipo sugerido' : 'tipo'}: ${esc(i.estandar.nombre || i.estandar.tipo || '')}</span>` : ''}
                    ${i.precioAnterior !== undefined && i.diferencia ? `<span data-presu-item-antes class="presu-rep-antes">antes ${fmtCOP(i.precioAnterior)} (${i.diferencia > 0 ? '+' : '&minus;'}${fmtCOP(Math.abs(i.diferencia))})</span>` : ''}
                    ${origenEnPalabras(i) ? `<span data-presu-item-origen style="font-size:0.875rem;color:${i.precioOrigen === 'tecleado' ? '#d29922' : '#3fb950'}">${esc(origenEnPalabras(i))}</span>` : ''}
                  </span></span>
                <span style="display:flex;align-items:center;white-space:nowrap">
                  <span style="font-variant-numeric:tabular-nums">${fmtCOP(Math.round((Number(i.cantidad) || 0) * (Number(i.precioUnit) || 0)))}</span>
                  <button type="button" class="presu-quitar" data-presu-del-item="${si}:${ii}" title="Quitar" aria-label="Quitar ${esc(i.titulo)}">&times;</button>
                </span></div>`).join('')}
        ${esDemoMaderinco()
          ? (numeroDeSeccionCliente(s) !== null
            ? `<button type="button" class="btn-ghost btn-sm" data-presu-reabrir="${numeroDeSeccionCliente(s)}" style="margin-top:6px;font-size:14px">Reabrir ${esc(s.nombre)}</button>`
            : '')
          : `<button type="button" class="btn-ghost btn-sm" data-presu-add-item="${si}" style="margin-top:6px;font-size:0.875rem">+ Agregar producto</button>`}
        </div>`).join('')));

    // #36553: Agregar desde una linea del cliente: entra a esa seccion del cliente (clave|item|numero).
    $sec.querySelectorAll('[data-presu-sug-nueva]').forEach(b => b.addEventListener('click', () => {
      const [clave, pid, n] = String(b.dataset.presuSugNueva).split('|');
      // Grupo 2b: marcar la casilla exige la cantidad escrita en su fila (nunca 1 en silencio); si falta, se dice ahi.
      const $cant = b.closest('[data-presu-partida]')?.querySelector('.presu-partida-cant');
      if ($cant && cantidadExplicita($cant.value) === null) {
        $cant.setAttribute('aria-invalid', 'true');
        $cant.placeholder = 'cantidad';
        $cant.focus();
        return;
      }
      if (agregarDesdePlantilla(clave, pid, b, Number(n) || null)) repintar();
    }));
    // Desmarcar: la partida sale del presupuesto (la de esa linea de la plantilla, en la seccion que la tenga).
    $sec.querySelectorAll('[data-presu-sug-quitar]').forEach(b => b.addEventListener('click', () => {
      const pid = b.dataset.presuSugQuitar;
      const s = armador.secciones.find((x) => (x.items || []).some((i) => i.plantillaItemId === pid));
      if (!s) return;
      const eraElTipo = s.items.some((i) => i.plantillaItemId === pid && i.tipoCubierta);
      s.items = s.items.filter((i) => i.plantillaItemId !== pid);
      // Si era la partida del tipo de cubierta (la linea Teja lista los mismos items), la Cubierta la suelta tambien.
      if (eraElTipo) cubierta = { ...cubierta, agregados: cubierta.agregados.filter((a) => !a.auto) };
      repintar();
      $sec.querySelector(`[data-presu-sug-nueva*="|${CSS.escape(pid)}|"]`)?.focus();
    }));
    // Cambiar de seccion del cliente solo repinta; no agrega ni quita nada. El buscador de una linea se cierra.
    const irASeccion = (n) => {
      pasoCapitulo = Number(n) || 1;
      otroAbierto = null;
      repintar();
      $sec.querySelector('[data-presu-capitulos]')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };
    $sec.querySelectorAll('[data-presu-capitulos] [data-rc-paso], [data-presu-capitulos] [data-rc-ir]').forEach(b => b.addEventListener('click', () => irASeccion(b.dataset.rcPaso || b.dataset.rcIr)));
    // Grupo 4: elegir el tipo de una linea solo repinta (no agrega ni quita nada).
    $sec.querySelectorAll('[data-presu-capitulos] [data-rc-tipo]').forEach(b => b.addEventListener('click', () => {
      const [linea, tipo = ''] = String(b.dataset.rcTipo).split('|');
      tipoPorLinea.set(linea, tipo);
      repintar();
    }));
    // A 375 la barra de secciones se desliza de lado: el paso actual queda a la vista dentro de ella, sin mover la pagina.
    const $pasoActual = $sec.querySelector('[data-presu-capitulos] [data-rc-paso][aria-current="step"]');
    const $barraPasos = $pasoActual?.parentElement;
    if ($barraPasos && $barraPasos.scrollWidth > $barraPasos.clientWidth) $barraPasos.scrollLeft += $pasoActual.getBoundingClientRect().left - $barraPasos.getBoundingClientRect().left - 16;
    // "Agregar otro item del catalogo": abrir, escribir, elegir grupo, cerrar. Escribir no repinta la seccion.
    $sec.querySelectorAll('[data-presu-otro-abrir]').forEach(b => b.addEventListener('click', () => {
      otroAbierto = b.dataset.presuOtroAbrir; otroQ = ''; otroGrupo = ''; otroMax = 12;
      repintar();
      $sec.querySelector('[data-presu-otro-q]')?.focus();
    }));
    const $otro = $sec.querySelector('[data-presu-otro]');
    if ($otro) {
      $otro.querySelector('[data-presu-otro-q]')?.addEventListener('input', (e) => { otroQ = e.target.value; otroMax = 12; pintarOtro(); });
      $otro.querySelector('[data-presu-otro-grupo]')?.addEventListener('change', (e) => { otroGrupo = e.target.value; otroMax = 12; pintarOtro(); });
      $otro.querySelector('[data-presu-otro-cerrar]')?.addEventListener('click', () => { otroAbierto = null; repintar(); });
      pintarOtro();
    }

    // Cambiar el nombre de una seccion: arriba, en el mismo campo de crear, con el foco ahi.
    $sec.querySelectorAll('[data-presu-ren-sec]').forEach(b => b.addEventListener('click', () => {
      const si = Number(b.dataset.presuRenSec);
      const s = armador.secciones[si];
      if (!s) return;
      editandoSeccion = si;
      $nombreSec.value = s.nombre;
      $btnSeccion.textContent = 'Cambiar nombre';
      $nombreSec.focus();
      $nombreSec.select();
    }));
    // MADERINCO-FLUJO-SIGUIENTE-SECCION-T1: abrir la siguiente (o la elegida en "u otra") solo cuando se toca.
    // MADERINCO-ARBOLES-SECCIONES-T1 parte 2: los arboles de las demas esenciales. Ninguna pregunta agrega nada.
    const foco = (sel) => $sec.querySelector(sel)?.focus();
    $sec.querySelectorAll('[data-arb-tipo]').forEach((b) => b.addEventListener('click', () => {
      const [id, op] = b.dataset.arbTipo.split('|');
      const r = respDe(id);
      if (op === 'no_definido') r.tipos = r.tipos.has('no_definido') ? new Set() : new Set(['no_definido']);
      else { r.tipos.delete('no_definido'); if (r.tipos.has(op)) r.tipos.delete(op); else r.tipos.add(op); }
      r.error = ''; repintar(); foco(`[data-arb-tipo="${CSS.escape(b.dataset.arbTipo)}"]`);
    }));
    $sec.querySelectorAll('[data-arb-check]').forEach((b) => b.addEventListener('click', () => {
      const [id, cid, st] = b.dataset.arbCheck.split('|');
      const r = respDe(id);
      r.checks[cid] = { estado: st, motivo: st === 'no_aplica' ? (r.checks[cid]?.motivo || '') : '' };
      repintar(); foco(st === 'no_aplica' ? `[data-arb-motivo="${CSS.escape(id + '|' + cid)}"]` : `[data-arb-check="${CSS.escape(b.dataset.arbCheck)}"]`);
    }));
    // El motivo se guarda al escribir; el estado se repinta al salir del campo (no se pierde el foco al teclear).
    $sec.querySelectorAll('[data-arb-motivo]').forEach((i) => {
      i.addEventListener('input', () => { const [id, cid] = i.dataset.arbMotivo.split('|'); respDe(id).checks[cid].motivo = i.value; });
      i.addEventListener('change', () => repintar());
    });
    $sec.querySelectorAll('[data-arb-comp]').forEach((b) => b.addEventListener('click', () => {
      const [id, op] = b.dataset.arbComp.split('|');
      const r = respDe(id); r.complementos = r.complementos === op ? null : op; repintar();
    }));
    $sec.querySelectorAll('[data-arb-agregar]').forEach((b) => b.addEventListener('click', () => {
      const [clave, pid] = b.dataset.arbAgregar.split('|');
      const id = b.closest('[data-arbol]')?.dataset.arbol;
      const $cant = b.closest('[data-presu-partida]')?.querySelector('.presu-partida-cant');
      if (cantidadExplicita($cant?.value) === null) {
        if (id) respDe(id).error = 'Escribe la cantidad antes de agregar (la de referencia no se pone sola).';
        repintar(); return;
      }
      if (id) respDe(id).error = '';
      // #36553: el arbol vive dentro de la seccion del cliente en pantalla: lo que agrega entra a esa.
      if (agregarDesdePlantilla(clave, pid, b, pasoCapitulo)) repintar();
    }));

    // MADERINCO-ARBOLES-SECCIONES-T1: "No aplica" con motivo escrito (sin dialogo nativo); sin motivo no se guarda.
    $sec.querySelectorAll('[data-presu-noaplica]').forEach((b) => b.addEventListener('click', () => {
      noAplicaEditando = b.dataset.presuNoaplica; repintar();
      $sec.querySelector(`[data-presu-noaplica-motivo="${CSS.escape(noAplicaEditando)}"]`)?.focus();
    }));
    $sec.querySelector('[data-presu-noaplica-cancelar]')?.addEventListener('click', () => { noAplicaEditando = null; repintar(); });
    $sec.querySelector('[data-presu-noaplica-guardar]')?.addEventListener('click', (e) => {
      const id = e.currentTarget.dataset.presuNoaplicaGuardar;
      const $m = $sec.querySelector(`[data-presu-noaplica-motivo="${CSS.escape(id)}"]`);
      const motivo = String($m?.value || '').trim();
      if (!motivo) { $m?.setAttribute('aria-invalid', 'true'); $m?.focus(); return; }
      noAplica.set(id, motivo); noAplicaEditando = null; repintar();
    });
    $sec.querySelectorAll('[data-presu-noaplica-deshacer]').forEach((b) => b.addEventListener('click', () => { noAplica.delete(b.dataset.presuNoaplicaDeshacer); repintar(); }));

    // Reabrir desde Tu presupuesto: vuelve a esa seccion del cliente arriba, sin perder lo elegido.
    $sec.querySelectorAll('[data-presu-reabrir]').forEach((b) => b.addEventListener('click', () => irASeccion(b.dataset.presuReabrir)));

    // MADERINCO-SELECCION-POR-TIPO-T1: el recorrido de la Cubierta. Ninguna pregunta agrega: solo "Agregar" con cantidad.
    // MAD-IMPLEMENTAR-DISENO-T1 (Josse #42796): con el area escrita, la tarjeta de un tipo con UNA partida servida la pone
    // en la seccion del cliente en pantalla y cambiar de tarjeta la REEMPLAZA (el total cambia en el acto). Lo demas
    // agregado de cubierta se sigue marcando para revisar, como antes.
    const ponerTipoElegido = () => {
      const t = tarjetasDeTipos(plantillaDemo).find((x) => x.codigo === cubierta.tipo);
      if (!t?.unica || cantidadExplicita(cubierta.area) === null) return null;
      const n = pasoCapitulo;
      let s = armador.secciones.find((x) => numeroDeSeccionCliente(x) === n);
      if (!s) {
        s = { nombre: NOMBRES_DE_CAPITULO[n], seccionCliente: n, items: [] };
        const pos = armador.secciones.findIndex((x) => (numeroDeSeccionCliente(x) ?? Number.MAX_SAFE_INTEGER) > n);
        if (pos === -1) armador.secciones.push(s); else armador.secciones.splice(pos, 0, s);
      }
      const r = ponerPartidaDelTipo(s, itemDesdePlantilla(t.unica.item), t.codigo, cubierta.area);
      if (r.error) return r;
      const otros = cubierta.agregados.filter((a) => !a.auto);
      cubierta = { ...cubierta, agregados: [...otros, { fuenteId: t.unica.variante.fuenteId, alcance: t.unica.variante.alcance, auto: true }],
        revisar: otros.some((a) => IDS_DE_TIPO.includes(a.fuenteId)) || (cubierta.revisar && otros.length > 0) };
      return r;
    };
    // Q #42852: si el tipo nuevo no pone partida sola (varias variantes, "Todavia no definido" o sin area valida), la del
    // tipo anterior se quita: el total no puede quedar con dos cubiertas.
    const quitarTipoPuesto = () => {
      quitarPartidaDelTipo(armador.secciones.find((x) => numeroDeSeccionCliente(x) === pasoCapitulo));
      const otros = cubierta.agregados.filter((a) => !a.auto);
      cubierta = { ...cubierta, agregados: otros, revisar: otros.some((a) => IDS_DE_TIPO.includes(a.fuenteId)) || (cubierta.revisar && otros.length > 0) };
    };
    $sec.querySelectorAll('[data-presu-cub-tipo]').forEach(b => b.addEventListener('click', () => {
      const anterior = cubierta.tipo;
      cubierta = { ...cambiarTipo(cubierta, b.dataset.presuCubTipo), error: '' };
      const puesta = ponerTipoElegido();
      if (cubierta.tipo !== anterior && (!puesta || puesta.error)) quitarTipoPuesto();
      repintar();
      $sec.querySelector(`[data-presu-cub-tipo="${CSS.escape(b.dataset.presuCubTipo)}"]`)?.focus();
    }));
    $sec.querySelector('[data-presu-cub-area]')?.addEventListener('change', (e) => {
      cubierta = { ...cubierta, area: e.target.value, error: '' };
      const s = armador.secciones.find((x) => numeroDeSeccionCliente(x) === pasoCapitulo);
      if (!cambiarAreaDelTipo(s, cubierta.area)) ponerTipoElegido();
      repintar();
      $sec.querySelector('[data-presu-cub-area]')?.focus();
    });
    $sec.querySelector('[data-presu-cub-otro]')?.addEventListener('click', () => {
      // "Otro tipo": la busqueda libre de arriba, con la palabra cubierta para empezar.
      const $b = $host.querySelector('[data-presu-buscar-plantilla]');
      abrirBuscador();
      if ($b) { $b.value = 'cubierta'; $b.dispatchEvent(new Event('input')); $b.focus(); }
    });
    $sec.querySelectorAll('[data-presu-cub-q2-op]').forEach(b => b.addEventListener('click', () => {
      cubierta = { ...cubierta, q2: b.dataset.presuCubQ2Op, revisar: false, error: '' };
      repintar();
      $sec.querySelector(`[data-presu-cub-q2-op="${CSS.escape(b.dataset.presuCubQ2Op)}"]`)?.focus();
    }));
    $sec.querySelectorAll('[data-presu-cub-q3-op]').forEach(b => b.addEventListener('click', () => {
      cubierta = { ...cubierta, q3: b.dataset.presuCubQ3Op, error: '' };
      repintar();
      $sec.querySelector(`[data-presu-cub-q3-op="${CSS.escape(b.dataset.presuCubQ3Op)}"]`)?.focus();
    }));
    $sec.querySelectorAll('[data-presu-cub-agregar]').forEach(b => b.addEventListener('click', () => {
      const fid = b.dataset.presuCubAgregar;
      const $cant = b.closest('[data-presu-partida]')?.querySelector('.presu-partida-cant');
      // Cantidad POSITIVA ESCRITA: la de referencia no reemplaza en silencio un dato vacio o invalido.
      if (cantidadExplicita($cant?.value) === null) {
        cubierta = { ...cubierta, error: 'Escribe la cantidad (mayor que cero) antes de agregar.' };
        repintar();
        $sec.querySelector(`[data-presu-cub-agregar="${CSS.escape(fid)}"]`)?.closest('[data-presu-partida]')?.querySelector('.presu-partida-cant')?.focus();
        return;
      }
      const hallado = indicePorFuente(plantillaDemo).get(fid);
      if (!hallado || !agregarDesdePlantilla(hallado.seccion.clave, hallado.item.id, b, pasoCapitulo)) return;
      const variante = TIPOS.flatMap((t) => t.variantes).find((v) => v.fuenteId === fid);
      cubierta = { ...cubierta, agregados: [...cubierta.agregados, { fuenteId: fid, alcance: variante?.alcance || 'complemento' }], error: '' };
      repintar();
    }));

    // En el demo, crear o editar una seccion y buscar viven ARRIBA de las sugerencias, fuera de lo que se repinta.
    const $arriba = $host.querySelector('[data-presu-arriba]');
    const $filaSec = $host.querySelector('[data-presu-fila-seccion]');
    if (esDemoMaderinco() && $arriba && $filaSec && $filaSec.parentElement !== $arriba) {
      $arriba.style.display = '';
      $filaSec.style.marginTop = '0';
      $filaSec.style.marginBottom = '8px';
      $arriba.querySelector('[data-presu-ayuda]').after($filaSec);
      // T1 (recorrido a 375, Ares #30856): el espacio de accion queda ARRIBA; crear seccion, la ayuda y Buscar pasan debajo
      // de las secciones (siguen fuera de lo que se repinta).
      $sec.after($arriba);
      // MADERINCO-ARMADOR-DISENO-T1: el demo usa la presentacion de front-panel-claridad (estilos .presu-guiado).
      $host.querySelector('.presu-scope')?.classList.add('presu-guiado');
      // Una sola accion de cierre por viewport: la barra de abajo se esconde cuando el Guardar del resumen esta a la vista.
      const $guardar = $host.querySelector('[data-presu-guardar]');
      const $barra = $host.querySelector('[data-presu-barra]');
      if ($guardar && $barra && 'IntersectionObserver' in window) {
        new IntersectionObserver(([e]) => $barra.classList.toggle('presu-barra-oculta', e.isIntersecting)).observe($guardar);
      }
    }

    // MAD-ASISTENTE-UN-PASO-T1: una pantalla a la vez. Fuera del Resumen se esconde lo demas del armador (artifacts.css).
    const $scope = $host.querySelector('.presu-scope');
    $scope?.classList.toggle('presu-asistente', asistenteActivo());
    $scope?.classList.toggle('presu-en-resumen', asistenteActivo() && pantallaActual().paso === 'resumen');
    if (asistenteActivo()) {
      const irA = (dir) => {
        const ps = asiPantallas();
        const j = moverPantalla(ps, asi.idx, dir, asi.noAplica);
        // Al final no hay a donde ir: Siguiente es "Guardar presupuesto" y usa el Guardar de siempre.
        if (j === asi.idx) { if (dir > 0) $host.querySelector('[data-presu-guardar]')?.click(); return; }
        asi.idx = j; asi.editor = null; asi.guardado = null; otroAbierto = null; repintar(); window.scrollTo(0, 0);
      };
      $sec.querySelector('[data-asi-atras]')?.addEventListener('click', () => irA(-1));
      $sec.querySelector('[data-asi-siguiente]')?.addEventListener('click', () => irA(1));
      $sec.querySelector('[data-asi-noaplica]')?.addEventListener('click', () => {
        const n = pantallaActual().n;
        if (asi.noAplica.has(n)) { asi.noAplica.delete(n); repintar(); } else { asi.noAplica.add(n); irA(1); }
      });
      $sec.querySelectorAll('[data-asi-tipo]').forEach((b) => b.addEventListener('click', () => {
        const n = pantallaActual().n;
        asi.tipo.set(n, { ...(asi.tipo.get(n) || {}), tipo: b.dataset.asiTipo });
        asiAplicarTipo(n); repintar();
        $sec.querySelector(`[data-asi-tipo="${CSS.escape(b.dataset.asiTipo)}"]`)?.focus();
      }));
      // PR 3: las filas de Cantidades (marcar, cantidad, cual) y las respuestas de Revision. La linea sale de su indice.
      const lineaDe = (i) => asiRecorrido().capitulos.find((c) => c.numero === pantallaActual().n)?.lineas[Number(i)];
      const cambiarFila = (i, cambio, foco) => {
        const n = pantallaActual().n;
        const l = lineaDe(i);
        if (!l) return;
        const k = `${n}|${l.linea}`;
        const actual = asi.filas.get(k) || {};
        const op = Number($sec.querySelector(`[data-asi-op="${i}"][aria-checked="true"]`)?.dataset.k ?? actual.op ?? 0);
        const cant = $sec.querySelector(`[data-asi-cant="${i}"]`)?.value ?? actual.cantidadTexto ?? '';
        asi.filas.set(k, { incluida: !!$sec.querySelector(`[data-asi-marca="${i}"][aria-checked="true"]`) || l.obligatoria, op, cantidadTexto: cant, ...cambio });
        asiAplicarFila(n, l.linea); repintar();
        $sec.querySelector(foco)?.focus();
      };
      $sec.querySelectorAll('[data-asi-marca]').forEach((b) => b.addEventListener('click', () => {
        cambiarFila(b.dataset.asiMarca, { incluida: b.getAttribute('aria-checked') !== 'true' }, `[data-asi-marca="${b.dataset.asiMarca}"]`);
      }));
      $sec.querySelectorAll('[data-asi-cant]').forEach((inp) => inp.addEventListener('change', () => {
        cambiarFila(inp.dataset.asiCant, { cantidadTexto: inp.value, incluida: true }, `[data-asi-cant="${inp.dataset.asiCant}"]`);
      }));
      // MAD-LAPIZ-ITEM-T1: el estandar de la opcion k de la fila i (el que edita el lapiz).
      const estandarDeOpcion = (i, k) => { const l = lineaDe(i); return l ? opcionesDeLinea(l, asiEst(l), asiItemDeRef)[Number(k)]?.estandar || null : null; };
      const claveDe = (i) => `${pantallaActual().n}|${lineaDe(i)?.linea}`;
      $sec.querySelectorAll('[data-asi-op]').forEach((b) => b.addEventListener('click', () => {
        // Con el editor abierto, cambiar de tarjeta cambia lo que se edita (como la maqueta).
        if (asi.editor?.clave === claveDe(b.dataset.asiOp)) asi.editor = { clave: asi.editor.clave, desde: estandarDeOpcion(b.dataset.asiOp, b.dataset.k)?.id, borrador: null };
        cambiarFila(b.dataset.asiOp, { op: Number(b.dataset.k) }, `[data-asi-op="${b.dataset.asiOp}"][data-k="${b.dataset.k}"]`);
      }));
      $sec.querySelectorAll('[data-asi-lapiz]').forEach((b) => b.addEventListener('click', () => {
        const i = b.dataset.asiLapiz;
        const clave = claveDe(i);
        asi.guardado = null;
        if (asi.editor?.clave === clave) asi.editor = null;
        else {
          const k = $sec.querySelector(`[data-asi-op="${i}"][aria-checked="true"]`)?.dataset.k ?? 0;
          asi.editor = { clave, desde: estandarDeOpcion(i, k)?.id, borrador: null };
        }
        repintar();
        $sec.querySelector(asi.editor ? `[data-asi-editor="${i}"] [data-ce-editor] button, [data-asi-editor="${i}"] select, [data-asi-editor="${i}"] input` : `[data-asi-lapiz="${i}"]`)?.focus();
      }));
      // El editor abierto: el mismo de Tus items y precios. Al guardar se relee el item estandar y la fila dice Guardado.
      const $ed = $sec.querySelector('[data-asi-editor]');
      if ($ed && asi.editor) {
        const l = lineaDe($ed.dataset.asiEditor);
        const lineaL = l ? lineaDeLectura(l.linea) : null;
        // EDITOR-ITEM-DISENO-T1: arriba lo que distingue a las tarjetas de la fila; junto a la vigencia, el precio de la tarjeta.
        const ops = l ? opcionesDeLinea(l, asiEst(l), asiItemDeRef) : [];
        montarEditorEstandar($ed, {
          linea: lineaL, estandar: lineaL?.estandar.find((x) => x.id === asi.editor.desde), borrador: asi.editor.borrador,
          distingue: etiquetasCortas(ops, asiEst(l)?.plantilla).distingue, referencia: ops.find((o) => o.estandar?.id === asi.editor.desde)?.precio ?? null,
          alCambiar: (bo) => { if (asi.editor) asi.editor.borrador = bo; },
          alCerrar: () => { const i = $ed.dataset.asiEditor; asi.editor = null; repintar(); $sec.querySelector(`[data-asi-lapiz="${i}"]`)?.focus(); },
          alGuardar: (nueva) => {
            if (nueva) ponerLecturaEstandar(nueva);
            asi.guardado = asi.editor?.clave || null; asi.editor = null; repintar();
          }
        });
      }
      $sec.querySelectorAll('[data-asi-cheq]').forEach((b) => b.addEventListener('click', () => {
        const l = lineaDe(b.dataset.asiCheq);
        if (!l) return;
        asi.chequeo.set(`${pantallaActual().n}|${l.linea}`, b.dataset.v); repintar();
        $sec.querySelector(`[data-asi-cheq="${b.dataset.asiCheq}"][data-v="${b.dataset.v}"]`)?.focus();
      }));
      const $q = $sec.querySelector('[data-asi-cantidad]');
      $q?.addEventListener('change', () => {
        const n = pantallaActual().n;
        asi.tipo.set(n, { ...(asi.tipo.get(n) || {}), cantidad: $q.value });
        asiAplicarTipo(n); repintar();
        $sec.querySelector('[data-asi-cantidad]')?.focus();
      });
    }

    // El picker se abre por seccion. Se re-wirea en cada repintado porque el
    // listado se re-renderiza entero (los botones son nuevos nodos cada vez).
    $sec.querySelectorAll('[data-presu-add-item]').forEach(b => b.addEventListener('click', () => {
      abrirPicker(Number(b.dataset.presuAddItem), repintar);
    }));

    // Iteracion 0: cantidad y precio de un item se cambian ahi mismo. Se escucha 'change' (al salir del campo o con
    // Enter), no 'input': repintar reconstruye la lista y en cada tecla se perderia el foco.
    const editar = (sel, fn) => $sec.querySelectorAll(sel).forEach(inp => inp.addEventListener('change', () => {
      const [si, ii] = String(inp.dataset[sel === '[data-presu-item-cant]' ? 'presuItemCant' : 'presuItemPrecio']).split(':').map(Number);
      const seccion = armador.secciones[si];
      if (!seccion || !seccion.items[ii]) return;
      seccion.items[ii] = fn(seccion.items[ii], num(inp.value));
      repintar();
    }));
    editar('[data-presu-item-cant]', cambiarCantidad);
    // REPLICAR-PRESUPUESTO-T1: escribir la cantidad confirma la partida replicada (cambiarCantidad); el boton, tal cual.
    $sec.querySelectorAll('[data-presu-confirmar]').forEach(b => b.addEventListener('click', () => {
      const [si, ii] = String(b.dataset.presuConfirmar).split(':').map(Number);
      const it = armador.secciones[si]?.items?.[ii];
      if (!it) return;
      it.porConfirmar = false;
      repintar();
    }));
    editar('[data-presu-item-precio]', cambiarPrecio);
    // Quitar un item (punto 3 del feedback de Josse: antes solo se podia
    // AGREGAR, asi que un error obligaba a empezar de cero). repintar()
    // recalcula el resumen, de modo que el total se mueve en el acto.
    $sec.querySelectorAll('[data-presu-del-item]').forEach(b => b.addEventListener('click', () => {
      const [si, ii] = String(b.dataset.presuDelItem).split(':').map(Number);
      const seccion = armador.secciones[si];
      if (!seccion || !Array.isArray(seccion.items)) return;
      seccion.items.splice(ii, 1);
      repintar();
    }));

    // Quitar una seccion, solo si esta VACIA: borrar una con productos dentro
    // seria destruir trabajo con un clic y sin vuelta atras.
    $sec.querySelectorAll('[data-presu-del-sec]').forEach(b => b.addEventListener('click', () => {
      const si = Number(b.dataset.presuDelSec);
      const seccion = armador.secciones[si];
      if (!seccion || (seccion.items || []).length > 0) return;
      armador.secciones.splice(si, 1);
      repintar();
    }));
    // Punto 4 del feedback de Josse: habia DOS formas de crear seccion a la vez.
    // Ahora solo una esta a la vista: sin secciones manda el boton grande, y al
    // tocarlo aparece la fila y toma el foco.
    const $fila = $host.querySelector('[data-presu-fila-seccion]');
    const $primera = $sec.querySelector('[data-presu-primera-seccion]');
    // En el demo la fila vive arriba y siempre se ve (MADERINCO-PLANTILLAS-UX-T1).
    if ($fila) $fila.style.display = (!esDemoMaderinco() && $primera && armador.secciones.length === 0) ? 'none' : '';
    if ($primera) $primera.addEventListener('click', () => {
      if ($fila) $fila.style.display = '';
      const $n = $host.querySelector('[data-presu-nombre-seccion]');
      $n.scrollIntoView({ block: 'center', behavior: 'smooth' });
      $n.focus();
    });
  };

  const $copiCard = $host.querySelector('[data-presu-copiloto-card]');
  $host.querySelector('[data-presu-copiloto-abrir]').addEventListener('click', () => {
    $copiCard.classList.add('abierto');
    $host.querySelector('[data-presu-copiloto]').focus();
  });
  $host.querySelector('[data-presu-copiloto-cerrar]').addEventListener('click', () => {
    $copiCard.classList.remove('abierto');
  });

  // Nueva seccion por INPUT inline: el SKILL prohibe prompt()/alert()/confirm().
  const $nombreSec = $host.querySelector('[data-presu-nombre-seccion]');
  const $btnSeccion = $host.querySelector('[data-presu-nueva-seccion]');
  const agregarSeccion = () => {
    const nombre = ($nombreSec.value || '').trim();
    if (!nombre) { $nombreSec.style.borderColor = '#d29922'; $nombreSec.focus(); return; }
    $nombreSec.style.borderColor = '';
    // MADERINCO-PLANTILLAS-UX-T1: el mismo campo sirve para cambiar el nombre de una seccion (lapiz de su cabecera).
    if (editandoSeccion !== null && armador.secciones[editandoSeccion]) armador.secciones[editandoSeccion].nombre = nombre;
    else armador.secciones.push({ nombre, items: [] });
    editandoSeccion = null;
    $btnSeccion.innerHTML = esDemoMaderinco() ? '+ Secci&oacute;n propia' : '+ Agregar secci&oacute;n';
    $nombreSec.value = '';
    repintar();
    $nombreSec.focus();
  };
  $btnSeccion.addEventListener('click', agregarSeccion);

  // MADERINCO-PLANTILLAS-UX-T1: busqueda especifica en toda la plantilla. Los resultados se pintan aparte (no se repinta
  // el campo), asi no se pierde el foco ni el cursor. "techo" pide aclarar: cubierta (afuera) o cielo raso (adentro).
  const $buscar = $host.querySelector('[data-presu-buscar-plantilla]');
  // T151: el boton Buscar abre o cierra el buscador (con el foco en el campo al abrir).
  $host.querySelector('[data-presu-abrir-buscar]')?.addEventListener('click', (e) => {
    const $c = $host.querySelector('[data-presu-buscador]');
    if (!$c) return;
    $c.hidden = !$c.hidden;
    e.currentTarget.setAttribute('aria-expanded', String(!$c.hidden));
    if (!$c.hidden) $buscar?.focus();
  });
  const $busRes = $host.querySelector('[data-presu-busqueda-res]');
  const pintarBusqueda = (aviso = '') => {
    if (!$busRes || !plantillaDemo) return;
    const q = $buscar.value;
    const { resultados, total } = buscarEnPlantilla(plantillaDemo, q, 8);
    const techo = pideAclararTecho(q) ? `<div data-presu-techo style="font-size:0.875rem;color:#d29922;margin:4px 0">&iquest;Techo de afuera o de adentro?
        <button type="button" class="btn-ghost btn-sm" data-presu-techo-q="cubierta" style="font-size:0.875rem">Cubierta (afuera)</button>
        <button type="button" class="btn-ghost btn-sm" data-presu-techo-q="cielo" style="font-size:0.875rem">Cielo raso (adentro)</button></div>` : '';
    const lista = resultados.length ? `<div class="presu-partidas">${resultados.map(({ seccion, item }) =>
      partidaHTML(item, `data-presu-bus="${esc(seccion.clave)}|${esc(item.id)}"`, ` <span class="presu-partida-sec">${esc(seccion.nombre)}</span>`)).join('')}</div>` : '';
    const pie = !q.trim() ? '' : (total === 0 ? '<div style="font-size:0.875rem;color:#6e7681">Nada con esas palabras en el cat&aacute;logo. Prueba con otra o crea una secci&oacute;n propia.</div>'
      : (total > resultados.length ? `<div style="font-size:0.875rem;color:#6e7681">y ${total - resultados.length} m&aacute;s: escribe otra palabra para afinar</div>` : ''));
    $busRes.innerHTML = (aviso ? `<div data-presu-bus-aviso style="font-size:0.875rem;color:#3fb950;margin-bottom:4px">${esc(aviso)}</div>` : '') + techo + lista + pie;
    $busRes.querySelectorAll('[data-presu-bus]').forEach(b => b.addEventListener('click', () => {
      const [c, pid] = String(b.dataset.presuBus).split('|');
      const p = agregarDesdePlantilla(c, pid, b);
      if (!p) return;
      repintar();
      pintarBusqueda('Agregado a ' + p.nombre + '.');
      $buscar.focus();
    }));
    $busRes.querySelectorAll('[data-presu-techo-q]').forEach(b => b.addEventListener('click', () => {
      $buscar.value = b.dataset.presuTechoQ;
      pintarBusqueda();
      $buscar.focus();
    }));
  };
  $buscar?.addEventListener('input', () => pintarBusqueda());
  $nombreSec.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); agregarSeccion(); } });

  $util.addEventListener('input', repintar);
  $area.addEventListener('input', repintar);
  // #27982: el codigo lo pone el usuario en uno nuevo; en uno abierto queda fijo (version nueva del mismo codigo).
  $host.querySelector('[data-presu-codigo]').addEventListener('input', (e) => { if (!armador.version) armador.codigo = e.target.value.trim() || null; });
  $host.querySelector('[data-presu-proyecto]').addEventListener('input', (e) => { armador.proyecto = e.target.value; });
  $host.querySelector('[data-presu-cliente]').addEventListener('input', (e) => { armador.cliente = e.target.value; });

  // Copiloto fase A: responde con lo que YA puede calcular en pantalla y dice
  // honesto lo que todavia no sabe hacer (nada de prometer acciones que no hay).
  const $log = $host.querySelector('[data-presu-copiloto-log]');
  // #36553 punto 4: "Guiame seccion por seccion" recorre las secciones del cliente en su orden (pasoPorSecciones). Cada
  // opcion mueve la pantalla; nada se agrega sin que el usuario escriba la cantidad y toque Agregar.
  const listasGuia = new Set();
  let opcionesGuia = [];
  const guiar = () => {
    const yaPuestos = new Set(armador.secciones.flatMap((s) => (s.items || []).map((i) => i.plantillaItemId)).filter(Boolean));
    const paso = pasoPorSecciones({ recorrido: recorridoPorCapitulos(plantillaDemo, lineasCliente), yaPuestos, listas: listasGuia });
    opcionesGuia = paso.opciones;
    $log.querySelectorAll('[data-guia-ops]').forEach((d) => d.remove());
    if (paso.numero && paso.numero !== pasoCapitulo) { pasoCapitulo = paso.numero; otroAbierto = null; repintar(); }
    $log.innerHTML += `<div data-guia-paso="${esc(paso.paso)}" style="margin-bottom:8px">
        <div style="font-size:14px;color:#f0f6fc">${esc(paso.texto)}</div>
        ${paso.nota ? `<div style="color:#d29922">${esc(paso.nota)}</div>` : ''}
        <div style="color:#8b949e">Fuente: las secciones de tu cliente (${fuenteLineasCliente === 'api' ? 'cargadas' : 'lista de respaldo'}); son sugerencias, no aprobaci&oacute;n de ingenier&iacute;a.</div>
        ${paso.opciones.length ? `<div data-guia-ops style="display:flex;flex-wrap:wrap;gap:6px;margin-top:6px">${paso.opciones.map((o, i) =>
          `<button type="button" class="btn btn-secondary" data-guia-op="${i}" style="font-size:14px">${esc(o.label)}</button>`).join('')}</div>` : ''}</div>`;
    $log.scrollTop = $log.scrollHeight;
  };
  $log.addEventListener('click', (ev) => {
    const b = ev.target.closest('[data-guia-op]');
    if (!b) return;
    const o = opcionesGuia[Number(b.dataset.guiaOp)];
    if (!o) return;
    $log.querySelectorAll('[data-guia-ops]').forEach((d) => d.remove());
    $log.innerHTML += `<div style="margin-bottom:6px;color:#8b949e">&rarr; ${esc(o.label)}</div>`;
    pasoCapitulo = o.accion.numero; otroAbierto = null; repintar();
    $sec.querySelector('[data-presu-capitulos]')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    // Abrir deja trabajar en la seccion: el asistente espera a que vuelva a pedir "siguiente". Listo pasa a la que sigue.
    if (o.accion.tipo === 'listo-seccion') { listasGuia.add(o.accion.numero); guiar(); }
  });
  $host.querySelector('[data-presu-guia]')?.addEventListener('click', () => guiar());
  $host.querySelector('[data-presu-copiloto]').addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const txt = (e.target.value || '').trim();
    if (!txt) return;
    e.target.value = '';
    const m = txt.toLowerCase();
    let resp;
    if (esDemoMaderinco() && pideGuia(m)) {
      $log.innerHTML += '<div style="margin-bottom:6px;color:#8b949e">' + esc(txt) + '</div>';
      guiar();
      return;
    }
    if (/total|cuanto va|cu&aacute;nto|cuánto/.test(m)) {
      const a = calcularAiu(armador);
      resp = 'Vas en <b>' + fmtCOP(a.total) + '</b>' + (a.valorM2 != null ? ' (' + fmtCOP(a.valorM2) + ' por m&sup2;)' : '') + '.';
    } else if (/utilidad/.test(m)) {
      const capt = (m.match(/(\d+[.,]?\d*)/) || [])[1];
      const n = capt != null ? parsearNumeroColombiano(capt) : null;
      if (n !== null && !isNaN(n)) {
        $util.value = n;
        repintar();
        resp = 'Listo, utilidad al <b>' + fmtNum(n) + '%</b>. Total: <b>' + fmtCOP(calcularAiu(armador).total) + '</b>.';
      } else {
        resp = 'Dime a qu&eacute; porcentaje la subo (ej. "sube la utilidad al 8").';
      }
    } else {
      resp = 'Eso todav&iacute;a no lo s&eacute; hacer solo. Por ahora te ayudo con el total y la utilidad; agregarte productos hablando llega en la siguiente entrega.';
    }
    $log.innerHTML += '<div style="margin-bottom:6px"><div style="color:#8b949e">' + esc(txt) + '</div><div>' + resp + '</div></div>';
    $log.scrollTop = $log.scrollHeight;
  });

  // ITERACION 0 (Ares #27621): GUARDAR y PEDIR EL PDF son dos cosas. Antes el unico boton decia "Solicitar PDF", hacia
  // POST /presupuestos (la API lo deja en BORRADOR) y anunciaba "el equipo te envia el PDF" sin pedirlo nunca: el
  // pedido real es POST /{id}/solicitar-pdf, que existe. Y despues de guardar, el armador sigue sobre el MISMO codigo:
  // volver a guardar crea la version siguiente, no otro presupuesto suelto.
  const $guardar = $host.querySelector('[data-presu-guardar]');
  const $solicitar = $host.querySelector('[data-presu-solicitar]');
  const $barraGuardar = $host.querySelector('[data-presu-barra-guardar]');
  const botones = [$guardar, $solicitar, $barraGuardar].filter(Boolean);
  const textos = new Map(botones.map(b => [b, b.innerHTML]));
  const ocupar = (si) => botones.forEach(b => {
    b.disabled = si;
    b.innerHTML = si ? '<span class="presu-spinner" style="display:inline-block;width:14px;height:14px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;vertical-align:-2px"></span>' : textos.get(b);
  });
  const faltaAlgo = () => {
    // T129: en el demo, cada campo del formulario dice su error debajo, todos a la vez (no uno por intento).
    if (esDemoMaderinco()) {
      const val = (sel) => $host.querySelector(sel)?.value ?? '';
      const errores = erroresDelFormulario({ codigo: val('[data-presu-codigo]'), proyecto: val('[data-presu-proyecto]'),
        cliente: val('[data-presu-cliente]'), areaM2: val('[data-presu-area]') });
      ['codigo', 'proyecto', 'cliente', 'area'].forEach((k) => {
        const $e = $host.querySelector(`[data-presu-err="${k}"]`);
        const $in = $host.querySelector(`[data-presu-${k}]`);
        if ($e) $e.textContent = errores[k] || '';
        if ($in) $in.classList.toggle('input-error', !!errores[k]);
      });
      const primero = ['codigo', 'proyecto', 'cliente', 'area'].find((k) => errores[k]);
      if (primero) {
        $msg.style.color = '#d29922';
        $msg.textContent = 'Revisa los campos marcados arriba.';
        desplegarCab();
        $host.querySelector(`[data-presu-${primero}]`)?.focus();
        return true;
      }
    }
    if (armador.secciones.length === 0) {
      $msg.style.color = '#d29922';
      $msg.textContent = 'Agrega al menos una seccion.';
      return true;
    }
    // #27982: sin codigo la API no guarda (columna NOT NULL); se pide antes de gastar el viaje.
    if (!codigoYVersion(armador)) {
      $msg.style.color = '#d29922';
      $msg.textContent = 'Falta el codigo del presupuesto (ej. JJ 1347).';
      const $cod = $host.querySelector('[data-presu-codigo]');
      $cod.classList.add('input-error');
      desplegarCab();
      $cod.focus();
      setTimeout(() => $cod.classList.remove('input-error'), 1200);
      return true;
    }
    // La API exige a nombre de quien va (ClienteNombre, [Required] minimo 2).
    if ((armador.cliente || '').trim().length < 2) {
      $msg.style.color = '#d29922';
      $msg.textContent = 'Falta a nombre de quien va: escribe el cliente.';
      const $cli = $host.querySelector('[data-presu-cliente]');
      $cli.classList.add('input-error');
      desplegarCab();
      $cli.focus();
      setTimeout(() => $cli.classList.remove('input-error'), 1200);
      return true;
    }
    return false;
  };
  const guardarYa = async (pedirPdf) => {
    if (faltaAlgo()) return;
    ocupar(true);
    $msg.style.color = '#8b949e';
    $msg.textContent = '';
    // D-126 = a (Ares #28032): en el negocio demo, por pasos sobre el MISMO borrador; en otro negocio la API contesta
    // PUERTA_CERRADA y se guarda como siempre (guardarPresupuesto).
    const clasico = () => guardarPresupuesto(PresupuestosApiClient, {
      ...codigoYVersion(armador),
      proyecto: armador.proyecto,
      clienteNombre: armador.cliente,
      utilidadPct: armador.utilidadPct,
      ivaUtilidadPct: armador.ivaUtilidadPct,
      areaM2: armador.areaM2,
      descuento: armador.descuento,
      secciones: armador.secciones
    }, { pedirPdf });
    const r = await guardarArmador({ porPaso: PresupuestoPorPasoApiClient, clasico }, armador);
    // Por pasos, el PDF se pide despues con el id del borrador (el clasico ya lo pidio adentro).
    if (r.ok && r.modo === 'pasos' && pedirPdf && r.id) {
      const rp = await PresupuestosApiClient.solicitarPdf(r.id);
      r.pdfPedido = !!(rp && rp.ok);
    }
    ocupar(false);
    if (!r.ok) {
      $msg.style.color = '#d29922';
      if (r.modo === 'pasos') {
        // Corte a mitad: el borrador existe con lo que alcanzo a entrar; Guardar otra vez sigue sin duplicar.
        $msg.innerHTML = esc(fraseDelFallo(r)) + (r.parcial?.id
          ? `<br><span style="color:#8b949e">Alcanzaron a entrar ${esc(r.parcial.pasos)} paso(s). Toca <b>Guardar</b> otra vez para terminar: no se duplica nada.</span>` : '');
        // Otro cambio el borrador: se relee para comparar contra lo que hay AHORA (no se pierde el borrador ni se
        // crea otro). El siguiente Guardar manda lo que falte con la revision nueva.
        if (r.motivo === 'OTRO_CAMBIO' && armador._servidor) {
          const idB = armador._servidor.id ?? armador._servidor.Id;
          const fresco = idB ? await PresupuestosApiClient.obtenerPresupuesto(idB) : null;
          if (fresco && fresco.ok && fresco.data) armador._servidor = fresco.data;
        }
        return;
      }
      $msg.textContent = mensajeFalla(r.error?.code, 'Guardar todavia no esta conectado; el calculo de arriba ya es el bueno.');
      return;
    }
    // Lo siguiente se guarda como version nueva del MISMO codigo.
    if (r.codigo) {
      armador.codigo = r.codigo; armador.version = r.version;
      // Ya guardado: el campo muestra el codigo que quedo y no se edita (lo siguiente es version nueva del mismo).
      const $cod = $host.querySelector('[data-presu-codigo]');
      if ($cod) { $cod.value = r.codigo; $cod.readOnly = true; $cod.title = 'Ya guardado: se guarda como version nueva del mismo codigo'; }
    }
    // EL ECO (aviso de Argy-A #8450): lo que se VIO contra lo que QUEDO.
    const totalEnPantalla = calcularAiu(armador).total;
    const descuadra = Number.isFinite(r.totalServidor) && Math.round(r.totalServidor) !== totalEnPantalla;
    const cual = r.codigo ? ' como <b>' + esc(r.codigo) + '</b> (versi&oacute;n ' + esc(r.version) + ')' : '';
    if (descuadra) {
      $msg.style.color = '#d29922';
      $msg.innerHTML = `Qued&oacute; guardado${cual}, pero <b>no con el mismo total</b>.
        <br><span style="color:#8b949e">En pantalla ve&iacute;as <b>${fmtCOP(totalEnPantalla)}</b> y qued&oacute; <b>${fmtCOP(r.totalServidor)}</b>.
        No se lo mandes al cliente as&iacute;: av&iacute;sale al equipo.</span>`;
    } else if (pedirPdf && r.pdfPedido) {
      $msg.style.color = '#3fb950';
      $msg.innerHTML = `Guardado${cual} y <b>PDF pedido</b>: el equipo te lo env&iacute;a.`;
    } else if (pedirPdf) {
      $msg.style.color = '#d29922';
      $msg.innerHTML = `Guardado${cual}, pero <b>el PDF no qued&oacute; pedido</b>. Intenta de nuevo desde el presupuesto guardado.`;
    } else {
      $msg.style.color = '#3fb950';
      $msg.innerHTML = r.modo === 'pasos'
        ? `Guardado${cual} en borrador. Si cambias algo, <b>Guardar</b> actualiza este mismo borrador (solo lo que cambiaste).`
        : `Guardado${cual} en borrador. Si cambias algo, <b>Guardar</b> crea la versi&oacute;n siguiente.`;
    }
    // MADERINCO-ARBOLES-SECCIONES-T1 (contrato #30474, SI #30514): por pasos, lo respondido en las esenciales se
    // guarda con el presupuesto. Mientras A no lo publique, la linea dice que queda en esta pantalla.
    if (r.ok && r.modo === 'pasos' && r.id && esDemoMaderinco()) {
      const { cuerpo, sinMotivo } = cuerpoRecorrido({ cubierta, noAplica, respArbol, schemaVersion: ARBOLES.schema_version, arbolesSha: ARBOLES_SHA256 });
      const rr = await PresupuestoPorPasoApiClient.guardarRecorrido(r.id, revisionDe(armador._servidor), cuerpo);
      // A (api 573) devuelve {recorrido, arbolesSha, revision} y SUBE la revision del borrador: el siguiente paso la
      // necesita, o chocaria con 409 VERSION_CAMBIO.
      if (rr.ok && rr.revision !== null && armador._servidor) armador._servidor = { ...armador._servidor, revision: rr.revision };
      const $rec = document.createElement('div');
      $rec.dataset.presuRecorridoMsg = rr.ok ? 'guardado' : rr.motivo;
      $rec.style.cssText = 'margin-top:6px;font-size:14px;color:' + (rr.ok ? '#8b949e' : '#d29922');
      $rec.textContent = fraseDelRecorrido({ ...rr, sinMotivo });
      $msg.appendChild($rec);
    }
    if (r.id) {
      const $ver = document.createElement('button');
      $ver.type = 'button';
      $ver.className = 'btn-ghost btn-sm';
      $ver.style.cssText = 'width:100%;margin-top:8px';
      $ver.textContent = 'Ver el presupuesto guardado';
      $ver.addEventListener('click', () => renderDetalle($host, negocio, r.id));
      $msg.appendChild($ver);
    }
  };
  $guardar.addEventListener('click', () => guardarYa(false));
  $solicitar.addEventListener('click', () => guardarYa(true));
  if ($barraGuardar) $barraGuardar.addEventListener('click', () => guardarYa(false));

  repintar();
  // MADERINCO-ARBOLES-SECCIONES-T1: al editar una version, se trae lo respondido en ella (si A ya lo sirve).
  if (opts.recorridoDe && esDemoMaderinco()) {
    PresupuestoPorPasoApiClient.leerRecorrido(opts.recorridoDe).then((rl) => {
      const leido = rl.ok ? recorridoLeido(rl.data, { arbolesSha: ARBOLES_SHA256 }) : null;
      if (!leido || !$sec.isConnected) return;
      Object.assign(cubierta, leido.cubierta);
      for (const [k, v] of leido.noAplica) noAplica.set(k, v);
      for (const [k, v] of leido.respArbol) respArbol.set(k, v);
      repintar();
      if (leido.arbolCambio) { $msg.style.color = '#d29922'; $msg.textContent = 'Las preguntas de las secciones cambiaron desde que se guardo esta version: revisa las respuestas.'; }
    });
  }
  // T129: si se llega directo al armador, la plantilla puede no estar consultada; al llegar, se repinta con ella.
  if (plantillaDemo === null) cargarPlantilla().then(() => { if ($sec.isConnected) { repintar(); pintarAlcancePdf(); } });
  pintarAlcancePdf();

  function pintarAlcancePdf() {
    const $alc = $host.querySelector('[data-presu-alcance-pdf]');
    if ($alc) $alc.style.display = esDemoMaderinco() ? '' : 'none';
  }
}

export async function renderPresupuestos(container) {
  renderRecepcion(container, getSelectedBusinessName?.() || '');

  // El boton vive en la mini-cabecera (NavBar) y solo avisa; repintar es de aca.
  if (!window.__nxDemoResetWired) {
    window.__nxDemoResetWired = true;
    const alInicio = () => {
      const $host = document.getElementById('nx-main');
      if ($host && window.location.hash.startsWith('#/presupuestos')) {
        renderRecepcion($host, getSelectedBusinessName?.() || '');
      }
    };
    window.addEventListener('demo:reset', alInicio);
    // El breadcrumb de la mini-cabecera. Necesita su propio evento porque el
    // armador y la lista viven DENTRO de #/presupuestos: cambiar el hash a esa
    // misma ruta no dispara hashchange y no se repintaba nada. Lo cazo el smoke:
    // el hash quedaba bien y la pantalla seguia en "Armar presupuesto".
    window.addEventListener('demo:inicio', alInicio);
  }
}
